<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useBrandingStore } from '@/stores/branding'
import { useHeaderLayoutStore } from '@/stores/headerLayout'
import { useMenuLayoutStore } from '@/stores/menuLayout'
import { useCustomThemesStore } from '@/stores/customThemes'
import { useDisplay, useTheme } from 'vuetify'
import axios, { isOfflineError } from '@/api/client'
import { getTimezoneLabel } from '@/utils/timezones'
import { usePresence } from '@/composables/usePresence'
import { useCompanyContext } from '@/composables/useCompanyContext'
import { useFlow } from '@/composables/useFlow'
import { useWorkerTerm } from '@/composables/useWorkerTerm'
import { useNetworkHealth } from '@/composables/useNetworkHealth'
import { useCallOrchestratorWS } from '@/composables/useCallOrchestratorWS'
import { useOperatorActionLog } from '@/composables/useOperatorActionLog'
import { useInboundCommsPopups } from '@/composables/useInboundCommsPopups'
import { taskNotificationsApi, presenceApi } from '@/api/tasks'
import { setupGlobalSalesTracking } from '@/modules/sales/composables/useActivityTracking'
import {
  playAlertSound,
  showDesktopNotification,
  requestNotificationPermission,
  isSalesAlert,
  isNotificationsMuted,
  toggleNotificationsMuted,
} from '@/utils/alertSound'
import { shouldPoll, onBecomeActive } from '@/composables/usePollingGuard'
import QuickTaskDialog from '@/components/tasks/QuickTaskDialog.vue'

// MEMORY: Lightweight components loaded eagerly (small, always visible)
import GlobalNotifications from '@/components/GlobalNotifications.vue'
import ConnectionStatusBanner from '@/components/ConnectionStatusBanner.vue'
import VersionUpdateBanner from '@/components/VersionUpdateBanner.vue'
import CallCenterIcon from '@/components/icons/CallCenterIcon.vue'
import PermissionIndicator, { permissionDialogOpen } from '@/components/PermissionIndicator.vue'
import GlobalSearchDropdown from '@/components/base/GlobalSearchDropdown.vue'
import { getAllCatalogPermissions } from '@/components/base/catalogRegistry'
import CustomerCardDialog from '@/components/CustomerCardDialog.vue'
import CookieConsent from '@/components/CookieConsent.vue'
import CompanySwitcher from '@/components/CompanySwitcher.vue'
import HeaderEditOverlay from '@/modules/appearance/headerBuilder/HeaderEditOverlay.vue'
import PartDetailDialog from '@/modules/operations/components/PartDetailDialog.vue'
import { usePartCardDialog } from '@/composables/usePartCardDialog'
import { routeToFeature } from '@/utils/featureMap'
import { isNative } from '@/native/platform'
import { awaitBootGate } from '@/auth/tokenManager'
import {
  nativeSessionRightsGap,
  repairNativeSessionRights,
  reopenNativeSessionRightsBudget,
  resetNativeSessionRights,
} from '@/auth/nativeSessionRights'
import { TECH_SESSION_PUBLIC_PATHS, technicianLearningLocation } from '@/router/technicianPublicPaths'

// MEMORY: Heavy components loaded lazily (only when needed, reduces initial bundle)
const RegistrationRequestsView = defineAsyncComponent(() => import('@/views/RegistrationRequestsView.vue'))
const VoiceAssistantButton = defineAsyncComponent(() => import('@/components/VoiceAssistantButton.vue'))
const RobotMiniAvatar = defineAsyncComponent(() => import('@/components/RobotMiniAvatar.vue'))
// In-app visual annotation -> creates a support ticket (gated by support.annotate)
const AnnotationOverlay = defineAsyncComponent(() => import('@/modules/support/components/AnnotationOverlay.vue'))
// TICK-00022: floating Oliver chat widget (bottom-right corner, persists across pages)
const OliverChatWidget = defineAsyncComponent(() => import('@/components/OliverChatWidget.vue'))

// Ref to the (hidden) voice assistant instance so the robot avatar can
// trigger voice (replaces the old microphone button). Single instance —
// always mounted so compact/desktop header swaps do not kill the session.
const voiceAssistantRef = ref(null)

// Map the voice-assistant state to a robot status the avatar can visualize.
function robotStatusOf(v) {
  if (!v) return 'idle'
  if (v.isConnecting) return 'connecting'
  if (v.isSpeaking) return 'speaking'
  if (v.isConnected) return 'listening'
  return 'idle'
}
const voiceStatus = computed(() => robotStatusOf(voiceAssistantRef.value))
const OnboardingNudge = defineAsyncComponent(() => import('@/components/OnboardingNudge.vue'))
const ChatButton = defineAsyncComponent(() => import('@/components/ChatButton.vue'))
const SalesAlertToaster = defineAsyncComponent(() => import('@/modules/sales/components/SalesAlertToaster.vue'))
// MEMORY: Only V3 dialer is loaded (V1/V2 removed to save ~400MB RAM)
const DialerButtonV3 = defineAsyncComponent(() => import('@/components/DialerButtonV3.vue'))
const HeaderCommDropdown = defineAsyncComponent(() => import('@/components/communications/HeaderCommDropdown.vue'))
const TokenBalanceChip = defineAsyncComponent(() => import('@/modules/billing/components/TokenBalanceChip.vue'))
const InsufficientBalanceDialog = defineAsyncComponent(() => import('@/modules/billing/components/InsufficientBalanceDialog.vue'))
const TopUpDialog = defineAsyncComponent(() => import('@/modules/billing/components/TopUpDialog.vue'))
// Phase 4.5 — Sales Dialer Orchestration (per-domain feature flag, default OFF)
// 2026-05-06: GlobalCallDock removed in favor of two new components:
//   - CallAlertsBellButton — bell icon in the header (mute/unmute + deep-link)
//   - IncomingCallsBanner  — accordion stack rendered inside <v-main>, above
//                             the route content (push-down, no overlay)
const CallAlertsBellButton = defineAsyncComponent(() => import('@/components/global/CallAlertsBellButton.vue'))
const IncomingCallsBanner = defineAsyncComponent(() => import('@/components/global/IncomingCallsBanner.vue'))
// TICK-73: global inbound SMS/call popup layer (all dispatchers, per-user toggle)
const InboundCommsPopups = defineAsyncComponent(() => import('@/components/global/InboundCommsPopups.vue'))
const SwitchContextDialog = defineAsyncComponent(() => import('@/components/global/SwitchContextDialog.vue'))
// Profile photo editor (crop/zoom/reposition) — shared with UserProfileView
const AvatarEditorDialog = defineAsyncComponent(() => import('@/components/AvatarEditorDialog.vue'))
const showAvatarEditor = ref(false)
function openAvatarEditor() {
  showAvatarEditor.value = true
}

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

function trainingOnlyOffice() {
  return authStore.hasPermission('lms.training_only')
    && !authStore.hasPermission('dashboard.view')
    && !authStore.hasPermission('lms.view')
}
// MENU-PERSONALIZATION (2026-06-12): t() for strings built in script (headers etc.)
const { t } = useI18n()

// Platform God Mode session countdown (mm:ss), empty when not applicable
const godModeCountdown = ref('')
const brandingStore = useBrandingStore()
// Header Builder — data-driven top-header layout (only applied under the
// "New Design" preset; otherwise every helper reports the legacy behaviour).
const hb = useHeaderLayoutStore()
// Menu Builder — data-driven left sidebar (order / hide / rename / icon /
// sub-item order). Only applied under the "New Design" preset; otherwise every
// helper reports the legacy built-in menu. RBAC filtering always runs first.
const menu = useMenuLayoutStore()
// Desktop "⋯" overflow menu (New Design preset only) — holds items whose zone
// is 'more'. Separate from the compact (<1280px) more-menu above.
const showHeaderMoreMenu = ref(false)
// v3.5 - global Part Card dialog state (header search opens it)
const partCardDialog = usePartCardDialog()
const { mobile, mdAndDown } = useDisplay()
// COMPACT-HEADER (2026-06-03) — single source of truth for mobile + tablet header layout (<1280px)
const isCompactHeader = computed(() => mdAndDown.value)
const showCompactMoreMenu = ref(false)
// Aggregate unread counter — drives the red dot on the "⋯" button
const compactUnreadTotal = computed(() =>
  (taskNotifCount.value || 0) +
  (partsNotifCount.value || 0) +
  (pendingRegistrationsCount.value || 0)
)
// Auto-close compact "⋯" menu when viewport grows past breakpoint
watch(isCompactHeader, (v) => { if (!v) showCompactMoreMenu.value = false })

// Global network health — the single engine for connectivity
const { onNetworkEvent } = useNetworkHealth()

// Phase 4.5 — Sales Call Orchestrator WS (only connects when domain has feature flag ON)
useCallOrchestratorWS()
const networkActionLog = useOperatorActionLog()

// TICK-73 — global inbound SMS/call popups: prefs + toggles for the user menu.
// The actual WS wiring lives in the mounted <InboundCommsPopups> component.
const {
  enabled: inboundPopupsEnabled,
  soundEnabled: inboundPopupsSound,
  canSeeInboundPopups,
  toggleEnabled: toggleInboundPopups,
  toggleSound: toggleInboundPopupsSound,
} = useInboundCommsPopups()

let unsubNetwork = null
let lastNativeRightsResumeAt = 0
const NATIVE_RIGHTS_RESUME_COOLDOWN_MS = 10_000

// Registration requests notifications
const pendingRegistrations = ref([])
const pendingRegistrationsCount = computed(() => pendingRegistrations.value.length)
const showRegistrationRequests = ref(false)
const selectedRequestForDetails = ref(null)
const showRequestDetailsDialog = ref(false)

// User Presence (Workday status)
const {
  isOnline: presenceOnline, status: presenceStatus, statusColor: presenceStatusColor,
  statusLabel: presenceStatusLabel, workdayStarted: presenceWorkdayStarted,
  startWorkday: presenceStartWorkday, endWorkday: presenceEndWorkday,
  updateStatus: presenceUpdateStatus, initPresence, destroyPresence, statusLabels: presenceStatusLabels,
  currentStatusTimer, workdayTimer, todaySummary, formatDurationShort
} = usePresence()
const showPresenceMenu = ref(false)
const showPresenceHistory = ref(false)
const presenceStatusMsg = ref('')
const presenceHistoryData = ref([])
const taskNotifCount = ref(0)
const showTaskNotifMenu = ref(false)
const taskNotifList = ref([])
// Open tasks assigned to me. The bell badge answers "what is new", which people kept
// reading as "how much work do I have"; this is the workload figure, shown on the menu
// so it is visible from every page.
const openTaskCount = ref(0)
const showGlobalQuickTask = ref(false)
const userTeamName = ref('')
let taskNotifInterval = null

// Parts in-app notifications (for parts managers / technicians)
const partsNotifCount = ref(0)
const partsNotifList = ref([])
const showPartsNotifMenu = ref(false)
let partsNotifInterval = null
const canSeePartsNotifications = computed(() => {
  if (!flowField('menu.inventory_parts')) return false
  const perms = authStore.user?.permissions_list || authStore.permissions || []
  if (!Array.isArray(perms)) return false
  return ['parts.process_request', 'parts.manage_dashboard', 'parts.view_requests', 'technician.parts_request']
    .some(p => perms.includes(p))
})

function onGlobalQuickTaskCreated(task) {
  console.log('[App] Global quick task created:', task)
}

async function loadUserTeam() {
  try {
    const userId = authStore.user?.id
    if (!userId) return
    const { data } = await axios.get('/hr/employees/', { params: { page_size: 500 } })
    const employees = Array.isArray(data) ? data : (data.results || [])
    const myEmp = employees.find(e => (e.user_id || e.user) === userId)
    userTeamName.value = myEmp?.org_unit_name || ''
  } catch (e) {
    if (!isOfflineError(e)) console.error('[App] Failed to load user team:', e)
  }
}

async function toggleWorkday() {
  try {
    if (presenceWorkdayStarted.value) {
      await presenceEndWorkday()
    } else {
      await presenceStartWorkday()
    }
  } catch (e) {
    console.error('[Presence] Toggle failed:', e)
  }
}

async function changePresenceStatus(newStatus) {
  try {
    await presenceUpdateStatus(newStatus, presenceStatusMsg.value)
    showPresenceMenu.value = false
  } catch (e) {
    console.error('[Presence] Status change failed:', e)
  }
}

async function openPresenceHistory() {
  showPresenceHistory.value = true
  try {
    const { data } = await presenceApi.history()
    presenceHistoryData.value = data || []
  } catch (e) {
    console.error('[Presence] Failed to load history:', e)
  }
}

const presenceHistoryStatusColors = {
  available: 'success', on_hold: 'warning', busy: 'error',
  offline: 'grey', disconnected: 'grey-darken-1'
}

function formatPresenceTime(dt) {
  if (!dt) return '--'
  return new Date(dt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function formatPresenceDuration(seconds) {
  if (!seconds || seconds <= 0) return '--'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m ${s}s`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

async function fetchTaskNotifCount() {
  try {
    const { data } = await taskNotificationsApi.unreadCount()
    taskNotifCount.value = data.count || 0
    openTaskCount.value = data.open_tasks || 0
  } catch (e) { /* silent */ }
}

// The Tasks page lives inside the Home group, so the workload number is shown twice:
// on the group icon (visible while the sidebar is collapsed) and on the row itself.
const TASKS_ROUTE = '/tasks'

function menuTaskCount(item) {
  if (!item) return 0
  const owns = item.route === TASKS_ROUTE
    || (item.submenu || []).some(sub => sub && sub.route === TASKS_ROUTE)
  return owns ? openTaskCount.value : 0
}

async function fetchTaskNotifList() {
  try {
    const { data } = await taskNotificationsApi.list({ limit: 20 })
    taskNotifList.value = Array.isArray(data) ? data : (data?.results || [])
  } catch (e) {
    console.error('[App] Failed to load task notifications:', e)
  }
}

function notifDotColor(type) {
  const map = {
    task_assigned: 'dot-teal', task_claimed: 'dot-orange', task_completed: 'dot-green',
    task_released: 'dot-blue', running_late: 'dot-red', sla_warning: 'dot-red',
  }
  return map[type] || 'dot-teal'
}

function formatNotifTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now - d
  const diffMin = Math.floor(diffMs / 60000)
  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin} min ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hr ago`
  const diffDays = Math.floor(diffHr / 24)
  if (diffDays === 1) return 'Yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

async function markAllTaskNotifsRead() {
  try {
    await taskNotificationsApi.markAllRead()
    taskNotifCount.value = 0
    taskNotifList.value.forEach(n => { n.is_read = true })
  } catch (e) {
    console.error('[App] Failed to mark all read:', e)
  }
}

async function openTaskFromNotif(notif) {
  if (!notif.is_read) {
    try {
      await taskNotificationsApi.markRead(notif.id)
      notif.is_read = true
      taskNotifCount.value = Math.max(0, taskNotifCount.value - 1)
    } catch (e) { /* silent */ }
  }
  showTaskNotifMenu.value = false
  const taskId = notif.task_id
  if (taskId) {
    router.push({ path: '/tasks', query: { task: taskId } })
  } else if (notif.link_url) {
    router.push(notif.link_url)
  } else {
    router.push('/tasks')
  }
}

// ============== Parts notifications ==============
// Soften the unread-count poll: the backend now caches this per-(domain,user)
// count for a short TTL (DB_PERFORMANCE_GUIDE §3.2 — it was the #1 DB CPU hog),
// so the client hits the endpoint at most once per 60s and never stacks two
// in-flight requests. Visibility / idle / single-leader gating already happens
// via shouldPoll. `force` bypasses the 60s gate for the initial load.
const PARTS_NOTIF_MIN_INTERVAL_MS = 60_000
let lastPartsNotifFetch = 0
let partsNotifInFlight = false
async function fetchPartsNotifCount(force = false) {
  if (!canSeePartsNotifications.value) return
  if (partsNotifInFlight) return
  const now = Date.now()
  if (!force && now - lastPartsNotifFetch < PARTS_NOTIF_MIN_INTERVAL_MS) return
  partsNotifInFlight = true
  try {
    const { data } = await axios.get('/parts/notifications/unread_count/')
    partsNotifCount.value = data.count || 0
    lastPartsNotifFetch = Date.now()
  } catch (e) { /* silent */ } finally {
    partsNotifInFlight = false
  }
}

async function fetchPartsNotifList() {
  if (!canSeePartsNotifications.value) return
  try {
    const { data } = await axios.get('/parts/notifications/', { params: { limit: 20 } })
    partsNotifList.value = Array.isArray(data) ? data : (data?.results || [])
  } catch (e) {
    console.error('[App] Failed to load parts notifications:', e)
  }
}

async function markAllPartsNotifsRead() {
  try {
    await axios.post('/parts/notifications/mark_all_read/')
    partsNotifCount.value = 0
    partsNotifList.value.forEach(n => { n.is_read = true })
  } catch (e) {
    console.error('[App] Failed to mark parts notifs read:', e)
  }
}

async function openPartsNotif(notif) {
  if (!notif.is_read) {
    try {
      await axios.post(`/parts/notifications/${notif.id}/mark_read/`)
      notif.is_read = true
      partsNotifCount.value = Math.max(0, partsNotifCount.value - 1)
    } catch (e) { /* silent */ }
  }
  showPartsNotifMenu.value = false
  if (notif.link_url) {
    router.push(notif.link_url)
  } else if (notif.related_request_id) {
    router.push(`/operations/parts-requests/${notif.related_request_id}`)
  }
}

function partsNotifDotColor(type) {
  const map = {
    new_request: 'dot-orange',
    urgent_request: 'dot-red',
    status_change: 'dot-blue',
    sla_breach: 'dot-red',
    receipt_confirmed: 'dot-green',
  }
  return map[type] || 'dot-teal'
}

// Dialer version settings from API (per-user) — only V3 is supported now
const dialerSettings = ref({ allow_dialer_v1: false, allow_dialer_v2: false, allow_dialer_v3: true })
// RBAC-derived dialer capabilities (source of truth for the icon). The dialer
// must appear for ANYONE who holds the calling permission — no Call Center
// record or per-user flag required. This is additive: we still honour the
// legacy allow_dialer_v3 flag, we only ADD "show it when the role permits it".
const dialerCaps = ref({ can_make_calls: false, can_receive_calls: false })
const showDialerV3 = computed(() =>
  dialerCaps.value.can_make_calls
  || dialerCaps.value.can_receive_calls
  || dialerSettings.value.allow_dialer_v3
)
// The single persistent DialerButtonV3 (host) only hides its phone icon; the
// compact header always shows it, the desktop header follows Header Builder.
const dialerHostRef = ref(null)
const showDialerTrigger = computed(() => isCompactHeader.value || hb.inHeader('dialer'))

async function loadDialerSettings() {
  const prevV3 = dialerSettings.value.allow_dialer_v3
  console.warn('[App][DialerBlink] loadDialerSettings called, current v3:', prevV3, new Error().stack?.split('\n').slice(1,3).join(' <- '))
  try {
    const res = await axios.get('/dialer/cc-users/my_credentials/')
    // Capabilities are returned for BOTH Call Center users and permission-only
    // operators, so the icon follows the role permission even with no CC record.
    if (res.data?.capabilities) {
      dialerCaps.value = {
        can_make_calls: !!res.data.capabilities.can_make_calls,
        can_receive_calls: !!res.data.capabilities.can_receive_calls,
      }
    }
    if (res.data?.success && res.data?.dialer_settings) {
      const newV3 = res.data.dialer_settings.allow_dialer_v3
      if (prevV3 !== newV3) {
        console.warn('[App][DialerBlink] v3 CHANGED:', prevV3, '->', newV3)
      }
      dialerSettings.value = res.data.dialer_settings
      console.log('[App] Dialer settings loaded:', dialerSettings.value, 'caps:', dialerCaps.value)
    }
  } catch (err) {
    console.log('[App] Could not load dialer settings (user may not be in CC):', err.response?.status)
  }
}

let notificationInterval = null

// System notifications (cron failures, sync gaps, etc.)
const systemNotifications = ref([])
// Per-user bell preferences (which severities / types show, delivery options).
// Loaded from /system/notifications/settings/my/ and editable on
// Settings → Notifications ("My notifications" tab). Null until loaded → show
// everything (safe default).
const myNotifPrefs = ref(null)
const loadMyNotifPrefs = async () => {
  if (!authStore.isAuthenticated) return
  try {
    const { data } = await axios.get('/system/notifications/settings/my/')
    myNotifPrefs.value = data
  } catch (err) {
    if (!isOfflineError(err)) console.error('[Notifications] Error loading my prefs:', err)
  }
}
// Map a raw notification_type to its canonical settings code (mirror of the
// backend notification_settings_service.canonical_type).
const notifTypeCode = (rawType) => {
  const t = String(rawType || '')
  if (!t) return null
  if (t === 'sales_idle' || t === 'monitor_error_spike' || t === 'call_transcription_gap') return t
  if (t.startsWith('call_transcription_provider')) return 'call_transcription_provider'
  return null
}
// Does the current user want to see this notification in their bell?
// Unknown types and critical severity are always shown.
const userWantsNotif = (n) => {
  const prefs = myNotifPrefs.value
  if (!prefs) return true
  const sev = normalizeSeverity(n.severity)
  if (sev !== 'critical' && prefs.show && prefs.show[sev] === false) return false
  const code = notifTypeCode(n.notification_type)
  if (code && prefs.types && prefs.types[code] === false) return false
  return true
}
// The bell badge and the "System Alerts (N)" header count ROLLED-UP groups, not
// raw rows: repeated alerts of the same kind (one seller idle re-flagged many
// times) collapse into one row, so "10 raw" would read as "10" while the user
// only sees 2 rows. unreadSystemGroupCount is defined with groupedSystemNotifications below.
const totalNotificationsCount = computed(() => pendingRegistrationsCount.value + unreadSystemGroupCount.value)

// Global mute toggle for notification sounds (persisted in localStorage).
// Bound to the speaker icon in the header.
const notificationsMuted = ref(isNotificationsMuted())
const toggleMute = () => {
  const next = toggleNotificationsMuted()
  notificationsMuted.value = next
  console.log('[Notifications] Sound muted:', next)
}
// Keep state in sync if mute was changed in another tab.
const onMuteChanged = (e) => { notificationsMuted.value = !!e.detail }
if (typeof window !== 'undefined') {
  window.addEventListener('notifications-mute-changed', onMuteChanged)
}

// Load pending registration requests
const loadPendingRegistrations = async () => {
  if (!authStore.isAuthenticated) return
  try {
    const response = await axios.get('/auth/registration-requests/')
    pendingRegistrations.value = (response.data.results || []).slice(0, 5)
  } catch (err) {
    if (!isOfflineError(err)) console.error('Error loading registration requests:', err)
  }
}

// Track max-seen notification id to detect newly arrived alerts between polls
let lastSeenNotifId = 0
let notifBootstrapDone = false

// Load system notifications (cron failures, sync gaps, sales alerts).
// When new unread sales alerts arrive between polls, play a short sound
// and show a desktop notification (if the user allowed it).
const loadSystemNotifications = async () => {
  if (!authStore.isAuthenticated) return
  try {
    const response = await axios.get('/system/notifications/', {
      params: { is_read: false }
    })
    const notifications = response.data.results || response.data || []
    systemNotifications.value = notifications.slice(0, 10)

    const currentMax = notifications.reduce(
      (acc, n) => (n && n.id > acc ? n.id : acc),
      lastSeenNotifId,
    )

    if (!notifBootstrapDone) {
      lastSeenNotifId = currentMax
      notifBootstrapDone = true
      return
    }

    const newlyArrived = notifications.filter(
      n => n && n.id > lastSeenNotifId && !n.is_read,
    )
    if (newlyArrived.length) {
      // Only alert for notifications the user actually wants to see.
      const alerts = newlyArrived.filter(n => isSalesAlert(n) && userWantsNotif(n))
      if (alerts.length) {
        const worst = alerts.reduce((acc, n) => {
          const order = { critical: 3, warning: 2, info: 1 }
          return (order[n.severity] || 0) > (order[acc?.severity] || 0) ? n : acc
        }, alerts[0])
        // Delivery prefs: "Play a sound" and "Desktop notifications".
        const prefs = myNotifPrefs.value
        const soundPref = !(prefs && prefs.delivery && prefs.delivery.sound === false)
        const desktopPref = !!(prefs && prefs.delivery && prefs.delivery.desktop === true)
        // Respect per-rule notify_sound if backend mirrored it into details.
        // Global mute is enforced inside playAlertSound().
        const notifySound = soundPref && worst?.details?.notify_sound !== false
        if (notifySound) playAlertSound(worst.severity || 'warning')
        if (desktopPref) {
          showDesktopNotification(worst.title || 'Sales alert', worst.message || '', {
            tag: `sales-${worst.id}`,
            silent: !notifySound,
            onClick: () => {
              window.focus()
              if (worst.link_url) router.push(worst.link_url)
            },
          })
        }
      }
      lastSeenNotifId = currentMax
    } else {
      lastSeenNotifId = currentMax
    }
  } catch (err) {
    if (!isOfflineError(err)) console.error('[Notifications] Error loading system notifications:', err)
  }
}

// Mark system notification as read
const markNotificationRead = async (notificationId) => {
  try {
    await axios.post(`/system/notifications/${notificationId}/mark_read/`)
    // Refresh notifications
    loadSystemNotifications()
  } catch (err) {
    console.error('Error marking notification as read:', err)
  }
}

// TC-12-00031: clear ALL system alerts at once. Without this the badge kept its
// count because there was no bulk action and clicking each alert one by one is
// impractical when there are many. Backend scopes mark_all_read to the current
// user + company, so it only clears this user's own alerts.
const markingAllSystemRead = ref(false)
const markAllSystemRead = async () => {
  markingAllSystemRead.value = true
  try {
    await axios.post('/system/notifications/mark_all_read/')
    await loadSystemNotifications()
  } catch (err) {
    console.error('Error marking all system notifications as read:', err)
  } finally {
    markingAllSystemRead.value = false
  }
}

// Handle click on system notification - navigate to logs
const handleSystemNotificationClick = async (notification) => {
  console.log('[Notification] Clicked notification:', notification)
  
  // Mark as read first
  await markNotificationRead(notification.id)
  
  // Navigate to link_url if available
  if (notification.link_url) {
    console.log('[Notification] Navigating to:', notification.link_url)
    router.push(notification.link_url)
  } else if (notification.details?.link_url) {
    // Fallback to details.link_url
    console.log('[Notification] Navigating to (from details):', notification.details.link_url)
    router.push(notification.details.link_url)
  } else {
    // Default fallback to cron jobs
    console.log('[Notification] No link_url, fallback to cron jobs')
    router.push('/settings/cron-jobs')
  }
}

// Normalize any backend severity string into one of three buckets.
// Several backend inserts historically wrote severity='error' (not in the
// model's info/warning/critical set), which made the bell fall back to a
// neutral/grey colour. Map everything to a known bucket so the colour is
// always meaningful (critical=red, warning=amber, info=green).
const SEV_RANK = { critical: 3, warning: 2, info: 1 }
const normalizeSeverity = (severity) => {
  const s = String(severity || '').toLowerCase()
  if (s === 'critical' || s === 'error' || s === 'fatal' || s === 'danger' || s === 'severe') return 'critical'
  if (s === 'info' || s === 'information' || s === 'success' || s === 'notice') return 'info'
  return 'warning'
}

// Get severity icon
const getSeverityIcon = (severity) => {
  const icons = {
    critical: 'mdi-alert-circle',
    warning: 'mdi-alert',
    info: 'mdi-information'
  }
  return icons[normalizeSeverity(severity)] || 'mdi-bell'
}

// Get severity color (Vuetify theme token)
const getSeverityColor = (severity) => {
  const colors = {
    critical: 'error',
    warning: 'warning',
    info: 'success'
  }
  return colors[normalizeSeverity(severity)] || 'secondary'
}

// Compact relative time ("just now", "3m ago", "2h ago", "4d ago").
const timeAgo = (dateString) => {
  if (!dateString) return ''
  const then = new Date(dateString).getTime()
  if (Number.isNaN(then)) return ''
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000))
  if (sec < 45) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  return `${d}d ago`
}

// Severity filter for the system-alerts section ('all' | 'critical' | 'warning' | 'info').
const systemFilter = ref('all')

// Roll up repeated alerts of the same kind into a single row so the bell is not
// spammed (e.g. one "out of credits" row with a counter instead of many, one
// idle row per seller). Group key = notification_type + the subject it is about
// (seller / related entity). Representative = the most recent alert in the group.
const notifGroupKey = (n) => {
  const d = n.details || {}
  const subject = d.seller_user_id ?? d.related_id ?? n.related_id ?? ''
  return `${n.notification_type || 'x'}::${subject}`
}
const groupedSystemNotifications = computed(() => {
  const unread = systemNotifications.value.filter(n => !n.is_read && userWantsNotif(n))
  // Honour the per-user "Group repeated alerts" delivery toggle: when off, every
  // alert is its own row (unique key) instead of being rolled up.
  const grouping = !(myNotifPrefs.value && myNotifPrefs.value.delivery && myNotifPrefs.value.delivery.group === false)
  const map = new Map()
  for (const n of unread) {
    const key = grouping ? notifGroupKey(n) : `single-${n.id}`
    const sev = normalizeSeverity(n.severity)
    const g = map.get(key)
    if (!g) {
      map.set(key, { key, rep: n, count: 1, ids: [n.id], severity: sev })
    } else {
      g.count += 1
      g.ids.push(n.id)
      if ((n.id || 0) > (g.rep.id || 0)) g.rep = n // keep most recent as representative
      if (SEV_RANK[sev] > SEV_RANK[g.severity]) g.severity = sev // escalate to worst
    }
  }
  return Array.from(map.values()).sort(
    (a, b) => (SEV_RANK[b.severity] - SEV_RANK[a.severity]) || ((b.rep.id || 0) - (a.rep.id || 0)),
  )
})
// How many rolled-up alert rows the user actually sees (used by the bell badge
// and the header), so the count matches the list instead of the raw row total.
const unreadSystemGroupCount = computed(() => groupedSystemNotifications.value.length)
const systemSeverityCounts = computed(() => {
  const c = { all: 0, critical: 0, warning: 0, info: 0 }
  for (const g of groupedSystemNotifications.value) { c.all += 1; c[g.severity] += 1 }
  return c
})
const filteredSystemGroups = computed(() => {
  if (systemFilter.value === 'all') return groupedSystemNotifications.value
  return groupedSystemNotifications.value.filter(g => g.severity === systemFilter.value)
})
// Rows for one severity section (respects the active filter pill).
const systemGroupsForSeverity = (sev) => filteredSystemGroups.value.filter(g => g.severity === sev)

// Mark every alert in a rolled-up group as read in one click.
const markGroupRead = async (group) => {
  try {
    await Promise.all((group.ids || []).map(id => axios.post(`/system/notifications/${id}/mark_read/`)))
    await loadSystemNotifications()
  } catch (err) {
    console.error('[Notifications] Error marking group read:', err)
  }
}

// Make refreshNotifications available globally
window.refreshNotifications = loadPendingRegistrations

// Open request details dialog
const openRequestDetails = (request) => {
  selectedRequestForDetails.value = request
  showRequestDetailsDialog.value = true
}

// Format date helper - converts to user's timezone
const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  
  // Get user's timezone from authStore
  const userTimezone = authStore.user?.timezone || 'UTC'
  
  const date = new Date(dateString)
  
  // Convert to user's timezone
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: userTimezone
  })
}

// Helper function to get full name
const getUserFullName = () => {
  if (authStore.user?.full_name) {
    return authStore.user.full_name
  }
  if (authStore.user?.first_name || authStore.user?.last_name) {
    return `${authStore.user.first_name || ''} ${authStore.user.last_name || ''}`.trim() || 'User'
  }
  return authStore.user?.email || authStore.user?.phone_number || 'User'
}

// TICK-66: header badges refresh IMMEDIATELY after the user reads items, instead
// of waiting for the 30s poll. Views dispatch a window event after mark_read
// (chat / task notifications) and the header re-fetches the affected count only.
const onTaskNotifRefresh = () => { fetchTaskNotifCount() }
const onSystemNotifRefresh = () => { loadSystemNotifications() }
// The Settings → Notifications "My notifications" tab dispatches this after a
// save so the bell applies new preferences without a page reload.
const onNotifPrefsChanged = () => { loadMyNotifPrefs() }

// Load auth data from localStorage on app mount
// ---------------------------------------------------------------------------
// The in-app company switcher lives in <CompanySwitcher /> (header). It owns
// its own data loading (member companies + all-companies for global admins) and
// entry logic (in-place switch vs audited God Mode).
// ---------------------------------------------------------------------------
onMounted(async () => {
  window.addEventListener('app:task-notif-refresh', onTaskNotifRefresh)
  window.addEventListener('app:system-notif-refresh', onSystemNotifRefresh)
  window.addEventListener('notif-prefs-changed', onNotifPrefsChanged)
  // Customer Portal routes are standalone — skip ALL main app initialization
  // Use window.location.pathname because route.path is not ready yet at mount time
  if (window.location.pathname.startsWith('/portal')) {
    console.log('[App] Portal route detected, skipping main app init')
    return
  }

  // Dialer V2 popup window (/dialer) is standalone like Workiz's dialer:
  // it needs auth from storage but NONE of the CRM chrome/notification/presence
  // machinery — the window runs only the softphone.
  if (window.location.pathname.startsWith('/dialer')) {
    console.log('[App] Dialer window detected — auth only, skipping main app init')
    await awaitBootGate()
    authStore.loadFromStorage()
    return
  }

  // Wait for silent refresh so native/web cookie sessions rehydrate before we
  // decide isAuthenticated (avoids a false "logged out" flash on cold start).
  await awaitBootGate()
  authStore.loadFromStorage()
  // Header Builder: load the effective top-header layout (no-op visual change
  // unless the New Design preset is active). Non-blocking, never breaks boot.
  if (authStore.isAuthenticated) { hb.load() }
  // Menu Builder: publish the catalog + load the effective sidebar layout
  // (no-op visual change unless the New Design preset is active). Non-blocking.
  publishMenuCatalog()
  if (authStore.isAuthenticated) { menu.load() }
  console.log('App mounted - Auth data loaded:', {
    user: authStore.user?.email || authStore.user?.phone_number,
    domain: authStore.domain?.name,
    isAuthenticated: authStore.isAuthenticated,
    full_name: getUserFullName()
  })

  // Platform God Mode session countdown: the impersonation JWT lives 15 min
  // and has no refresh token. Show the remaining time and auto-close the tab
  // when the session expires (so a forgotten tab can never linger).
  if (authStore.isGodMode && authStore.godModeExpiresAt) {
    const tick = () => {
      const left = authStore.godModeExpiresAt - Date.now()
      if (left <= 0) {
        console.warn('[GodMode] Session expired — closing tab')
        godModeCountdown.value = '0:00'
        authStore.exitGodMode()
        return
      }
      const mins = Math.floor(left / 60000)
      const secs = Math.floor((left % 60000) / 1000)
      godModeCountdown.value = `${mins}:${String(secs).padStart(2, '0')}`
    }
    tick()
    setInterval(tick, 1000)
  }

  if (authStore.isAuthenticated && authStore.domain?.id && !isNative() && !window.location.pathname.startsWith('/login')) {
    authStore.refreshPermissions().catch(e => console.warn('[App] refreshPermissions failed:', e?.message))
  }

  // Native has no such boot refresh, so a session whose cached rights were lost
  // stayed rightless until the next manual sign-in: loadFromStorage() falls back
  // to an empty permission array, every hasPermission() on the technician screens
  // then answers false, and parts, invoices and the dialer silently disappear for
  // an account that works fine in a browser. Only acts in that broken state — a
  // healthy native boot makes no calls. See the watch below for a later revival.
  // Skipped on /login, where a background call can abort a password login; the
  // watch picks the session up as soon as the guard redirects off it.
  if (!window.location.pathname.startsWith('/login')) {
    runNativeSessionRepair()
  }

  // Throttle: prevent triple-firing of notification fetches from overlapping triggers
  // (setInterval + onBecomeActive + network recovery can all fire within 1-2 seconds)
  let lastNotifFetch = 0
  const NOTIF_THROTTLE_MS = 10_000
  function throttledNotifRefresh() {
    if (trainingOnlyOffice()) return
    const now = Date.now()
    if (now - lastNotifFetch < NOTIF_THROTTLE_MS) {
      console.log('[App] Notification refresh throttled (last:', Math.round((now - lastNotifFetch) / 1000), 's ago)')
      return
    }
    lastNotifFetch = now
    fetchTaskNotifCount()
    fetchPartsNotifCount()
    if (!isTechnicianRoute.value) {
      loadPendingRegistrations()
      loadSystemNotifications()
    }
  }

  // Subscribe to network recovery events — refresh all data when server comes back
  let recoveryDebounce = null
  unsubNetwork = onNetworkEvent((event, data) => {
    if (event === 'recovered' && authStore.isAuthenticated && !route.path.startsWith('/login')) {
      if (recoveryDebounce) {
        console.log('[App] Recovery already scheduled — skipping duplicate')
        return
      }
      recoveryDebounce = setTimeout(() => {
        recoveryDebounce = null
        if (!authStore.isAuthenticated || route.path.startsWith('/login')) return
        console.log('%c [App] Refreshing data after network recovery... ', 'background:#3b82f6;color:white;padding:2px 6px;border-radius:3px')
        // Offline failures may have spent the bounded repair budget. Reopen it
        // without invalidating an in-flight request; repeated recovery events
        // are coalesced by this same debounce.
        reopenNativeSessionRightsBudget()
        runNativeSessionRepair()
        throttledNotifRefresh()
        if (!isTechnicianRoute.value && !trainingOnlyOffice()) {
          loadDialerSettings()
        }
        networkActionLog.connectionLost(data)
        networkActionLog.connectionRestored(data)
      }, 3000)
    }
  })

  // Register regardless of the mount-time auth state: native users commonly
  // mount on /login and authenticate without reloading App.vue.
  onBecomeActive(() => {
    if (!authStore.isAuthenticated || route.path.startsWith('/login')) return
    // Visibility and idle detectors can report the same physical return. Limit
    // only the rights repair (notification refresh has its own throttle), while
    // always allowing the first real gap after a WebView reload.
    if (nativeSessionRightsGap(authStore, isNative())) {
      const now = Date.now()
      if (now - lastNativeRightsResumeAt >= NATIVE_RIGHTS_RESUME_COOLDOWN_MS) {
        lastNativeRightsResumeAt = now
        reopenNativeSessionRightsBudget()
        runNativeSessionRepair()
      }
    }
    throttledNotifRefresh()
  })
  
  // Load data if authenticated
  // Axios interceptor auto-blocks requests when server is offline — no manual checks needed
  if (authStore.isAuthenticated) {
    const isTechRoute = route.path.startsWith('/technician')
    setTimeout(() => {
      if (!isTechRoute && !trainingOnlyOffice()) {
        loadPendingRegistrations()
        loadSystemNotifications()
        loadMyNotifPrefs()
        loadDialerSettings()
        loadUserTeam()
        initPresence()
      }
      if (!trainingOnlyOffice()) {
        fetchTaskNotifCount()
        fetchPartsNotifCount(true) // initial load: bypass the 60s poll gate
        loadMenuUsage() // MENU-PERSONALIZATION (2026-06-12): frequent-items ranking
      }
      notificationInterval = setInterval(() => {
        if (!shouldPoll.value) return
        throttledNotifRefresh()
      }, 30000)
      // App-wide activity tracking for Sales users: captures typing in ALL
      // CRM/Sales forms (leads, deals, dialer popup, etc.) so Day Tape gets
      // green `writing` segments. Also emits idle_end on any activity so
      // idle-start events don't stay open for the 60-minute cap.
      try {
        if (authStore.hasPermission('sales.view')) {
          setupGlobalSalesTracking()
          console.log('[App] Global sales activity tracking enabled')
          // Non-blocking: ask once for desktop-notification permission so
          // sales alerts can surface as OS toasts with sound.
          requestNotificationPermission().then(p => {
            console.log('[App] Notification permission:', p)
          })
        }
      } catch (e) {
        console.warn('[App] setupGlobalSalesTracking failed:', e?.message)
      }
    }, 1000)
  }

  window.addEventListener('resize', checkSidebarOverflow)
  setTimeout(checkSidebarOverflow, 500)
})

onUnmounted(() => {
  if (notificationInterval) {
    clearInterval(notificationInterval)
  }
  if (taskNotifInterval) {
    clearInterval(taskNotifInterval)
  }
  if (unsubNetwork) {
    unsubNetwork()
  }
  destroyPresence()
  window.removeEventListener('resize', checkSidebarOverflow)
  window.removeEventListener('app:task-notif-refresh', onTaskNotifRefresh)
  window.removeEventListener('app:system-notif-refresh', onSystemNotifRefresh)
  window.removeEventListener('notif-prefs-changed', onNotifPrefsChanged)
})

// Show sidebar and app bar only when authenticated, not on login page, and not in technician mode
const isTechnicianRoute = computed(() => {
  // Check if this is a technician session
  const isTechSession = authStore.isTechnicianSession()
  
  // If technician session is active, the router guard will handle redirection
  // We just need to hide the main layout when in technician mode
  if (isTechSession) {
    console.log('🔧 [App] Technician session active, hiding main layout')
    return true
  }
  
  // Also check if we're on a technician path (fallback)
  const isOnTechnicianPath = route.matched.some(r => r.meta?.isTechnicianRoute)
  
  return isOnTechnicianPath
})

const isPortalRoute = computed(() => route.path.startsWith('/portal') || window.location.pathname.startsWith('/portal'))

// Sales Call Orchestrator incoming UI is SCOPED to the CRM Panel (SERM 2.0).
// The incoming call must surface ONLY inside that panel — as its own in-page
// card — never as an app-wide banner/bell that pops on unrelated pages where
// the operator cannot tell what is ringing or where the sound comes from. The
// panel (classic + v2) lives under /crm/panel*. The WS/store stay global so
// state is correct and the classic panel keeps working; only the GLOBAL UI
// surfaces (banner + header bell) are limited to the panel context.
const isCrmPanelRoute = computed(() => route.path.startsWith('/crm/panel'))

// Public e-sign page (/sign/<token>) — a document recipient opening the invite
// link must see a clean, standalone signing page with NO CRM chrome, even when
// they happen to be logged into the office app in the same browser. Treated
// like the customer estimate (/e/) and portal pages.
const isPublicSignRoute = computed(() => route.name === 'PublicSign' || route.path.startsWith('/sign/') || window.location.pathname.startsWith('/sign/'))

// Dialer V2 standalone popup window (Workiz-style) — no CRM chrome at all
const isDialerWindow = computed(() => route.path.startsWith('/dialer') || window.location.pathname.startsWith('/dialer'))

// TICK-00173 / TICK-00206: the bottom-right Oliver launcher overlaps the primary
// "Send" button on full-page messaging screens (office SMS inbox and internal
// chat), blocking users from sending. Those pages already have their own
// composer, so hide the floating Oliver there instead of covering the action.
const isMessagingRoute = computed(() => {
  const p = route.path
  return p.startsWith('/communications/messages') || p.startsWith('/communications/inbox')
    || p.startsWith('/communications/marketplace-inbox')
    || p === '/chat' || p.startsWith('/chat/')
})

const showLayout = computed(() => {
  // Don't show main layout on login page
  if (route.name === 'Login') return false
  if (route.name === 'AcceptCompanyInvite') return false
  // NOTE: the Quick-Launch Welcome page ('Welcome') is intentionally rendered
  // INSIDE the normal app shell (sidebar + top bar), so the new owner lands
  // straight in the live system on their first login — it is not a standalone
  // chrome-less page. Do NOT special-case it here.
  // Don't show main layout on the standalone 3D robot test page
  if (route.name === 'RobotTest') return false
  // Don't show main layout on the standalone menu-icons preview page
  if (route.name === 'MenuIconsPreview') return false
  // Public customer estimate page (/e/<token>) — standalone, no CRM chrome
  if (route.name === 'PublicEstimate' || route.path.startsWith('/e/')) return false
  // Public e-sign page (/sign/<token>) — standalone signing page, no CRM chrome
  // (even for a logged-in employee opening their own invite link)
  if (isPublicSignRoute.value) return false
  // Don't show main layout for technician routes - they have their own UI
  if (isTechnicianRoute.value) return false
  // Don't show main layout for customer portal - standalone UI
  if (isPortalRoute.value) return false
  // Don't show main layout in the Dialer V2 popup window - softphone only
  if (isDialerWindow.value) return false
  // Don't show main layout when the page is mounted as an iframe child
  // (Sales Panel embeds /jobs/new?embedded=1 — we want only the form,
  // not the global sidebar + top app-bar around it).
  if (route.query.embedded === '1') return false
  // Show layout only when authenticated
  return authStore.isAuthenticated
})

// Sidebar state
const sidebarDrawer = ref(true) // Open by default on desktop

// SIDEBAR-PUSH-LAYOUT BEGIN (2026-06-03) — unified submenu state with hover vs pin separation
// activeSubmenu: which module's submenu is currently shown (null = closed)
// submenuPinned: true when user clicked the icon (sticky), false when hover-only (auto-close)
const activeSubmenu = ref(null)
const submenuPinned = ref(false)
const hoverOpenTimer = ref(null)
const hoverCloseTimer = ref(null)
const HOVER_OPEN_DELAY = 120
const HOVER_CLOSE_DELAY = 200
// MENU-ICONS (2026-07-08): 280 -> 320 so long titles + permission/pin icons
// always fit (no truncation hiding the admin shield or hover actions).
const SUBMENU_WIDTH = 320

// Resolve module object for currently active submenu (for header icon/title/permission)
const activeSubmenuItem = computed(() => {
  if (!activeSubmenu.value) return null
  // MENU-PERSONALIZATION (2026-06-12): Quick Access is a synthesized pseudo-module
  if (activeSubmenu.value === QUICK_ACCESS_TITLE) return quickAccessModule.value
  // MENU-BUILDER: search the display list so the flyout reflects the saved
  // section order / hidden sections / reordered sub-items.
  return [...displayMenuItems.value, ...filteredBottomMenuItems.value]
    .find(m => m.title === activeSubmenu.value) || null
})

// Submenu items to render in the panel. Modules without real submenu get
// a single fallback entry duplicating their title, so hovering over them
// doesn't make the panel flicker open/close.
const activeSubmenuItems = computed(() => {
  if (!activeSubmenuItem.value) return []
  const item = activeSubmenuItem.value
  if (item.submenu && item.submenu.length > 0) return item.submenu
  if (item.route) {
    return [{ title: item.title, route: item.route, icon: item.icon, permission: item.permission }]
  }
  return []
})

// Parse a menu route that may include ?query= (e.g. /support?tab=voice-assistant).
function parseMenuRoute(routeStr) {
  if (!routeStr) return { path: '', query: {} }
  const qIdx = routeStr.indexOf('?')
  if (qIdx === -1) return { path: routeStr, query: {} }
  const path = routeStr.slice(0, qIdx)
  const query = {}
  for (const [k, v] of new URLSearchParams(routeStr.slice(qIdx + 1))) {
    query[k] = v
  }
  return { path, query }
}

// Match current location to a submenu row. Required query keys on the menu item
// must match; extra params on the current URL are ignored. Plain /support matches
// Tickets (no tab or tab=tickets), not other Support tabs.
function submenuRouteMatches(menuRoute, curPath, curQuery) {
  const { path, query: requiredQuery } = parseMenuRoute(menuRoute)
  const requiredKeys = Object.keys(requiredQuery)

  if (requiredKeys.length > 0) {
    if (curPath !== path && !curPath.startsWith(path + '/')) return false
    return requiredKeys.every((k) => String(curQuery[k] ?? '') === String(requiredQuery[k]))
  }

  if (curPath !== path && !curPath.startsWith(path + '/')) return false
  if (path === '/support') {
    const tab = curQuery.tab
    if (tab && tab !== 'tickets') return false
  }
  return true
}

// Only the SINGLE best-matching submenu entry is "active": the longest route that
// the current path equals or sits under. Prevents a parent route (e.g. /call-center)
// from also lighting up when we are on a child page (e.g. /call-center/monitor).
const activeSubmenuRoute = computed(() => {
  const items = activeSubmenuItems.value || []
  let best = null
  let bestLen = -1
  for (const it of items) {
    const r = it && it.route
    if (!r) continue
    if (!submenuRouteMatches(r, route.path, route.query)) continue
    if (r.length > bestLen) {
      bestLen = r.length
      best = r
    }
  }
  return best
})

// NEW-DESIGN FOUNDATION (2026-09-24): routes ported to the 1:1 Figma "v2" look
// declare `meta.hideModuleSubmenu: true`. Those screens are "immersive" and must
// not AUTO-pop a pinned/restored module flyout on entry — but the user must
// still be able to open any module's submenu from the left rail to navigate away
// (otherwise landing on a v2 screen traps them: the whole flyout dies and no
// submenu opens on hover/click). So we only auto-close a leftover flyout when
// ENTERING a v2 route; we do NOT gate the flyout open-state on the route, so a
// user-initiated hover/click still opens the flyout everywhere.
const hideSubmenuForRoute = computed(() => route?.meta?.hideModuleSubmenu === true)

// v-model bridge for the submenu flyout (auto-close when v-model becomes false).
// NOTE: the getter must NOT depend on hideSubmenuForRoute — that gate made the
// flyout permanently closed on every v2 screen and broke rail navigation.
const submenuDrawerOpen = computed({
  get: () => !!activeSubmenu.value,
  set: (v) => { if (!v) closeSubmenu() }
})

// When ARRIVING on a v2 route, close any open/pinned/restored flyout so the v2
// screen starts immersive. We watch the ACTUAL route (not just the boolean):
// navigating between two immersive routes (e.g. board-v2 → new-v2) keeps the
// boolean `true`, so a boolean watcher never re-fires and a flyout opened on the
// first v2 screen would stay open on the second. Watching route.fullPath closes
// it on every entry into a hideModuleSubmenu route. The user can still hover/
// click the rail afterwards to reopen a submenu and navigate away.
watch(() => route.fullPath, () => {
  if (hideSubmenuForRoute.value && activeSubmenu.value) closeSubmenu()
}, { immediate: true })

// SIDEBAR-FLYOUT BEGIN (2026-06-09) — HubSpot-style overlay submenu.
// The panel floats OVER the content (no v-main push) and is anchored next to the
// hovered/clicked sidebar item: top = item's top, left = item's right edge.
// Height fits its content (max 80vh, then scrolls); top is clamped so the panel
// never overflows the bottom of the viewport.
const submenuAnchorTop = ref(0)
const submenuAnchorLeft = ref(64)
const SUBMENU_MAX_VH = 0.8

// MENU-AUDIT FIX (2026-06-12): viewport height as a reactive ref (window.innerHeight
// inside a computed is NOT reactive — resizing with an open panel left it misplaced).
const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 800)
function onViewportResize() {
  viewportHeight.value = window.innerHeight
}

// MENU-AUDIT FIX (2026-06-12): real measured panel height (set after render below);
// 0 = not measured yet, fall back to the type-aware estimate.
const submenuFlyoutEl = ref(null)
const submenuMeasuredHeight = ref(0)

// Type-aware height estimate (old `count * 38 + 64` ignored headers/dividers and
// the real 42px item height, so the bottom-of-viewport clamp was off).
// Real heights: item 40px min + 2px margin, subheader 28px, divider ~1px + my-2 (16px),
// panel header 56px + 1px divider, v-list padding 16px.
const submenuEstimatedHeight = computed(() => {
  let h = 57 + 16
  for (const it of activeSubmenuItems.value) {
    if (it.divider) h += 17
    else if (it.header) h += 28
    else h += 42
  }
  return h
})

const submenuFlyoutStyle = computed(() => {
  const vh = viewportHeight.value
  const maxH = Math.round(vh * SUBMENU_MAX_VH)
  const panelH = Math.min(submenuMeasuredHeight.value || submenuEstimatedHeight.value, maxH)
  let top = submenuAnchorTop.value
  const maxTop = vh - panelH - 8
  if (top > maxTop) top = Math.max(8, maxTop)
  return {
    top: top + 'px',
    left: submenuAnchorLeft.value + 'px',
    width: SUBMENU_WIDTH + 'px',
    maxHeight: maxH + 'px',
  }
})

// Re-measure the real panel height whenever the panel opens or its content changes,
// then re-clamp via the computed above (measured height never changes `top` upward,
// so this cannot loop).
watch([activeSubmenu, activeSubmenuItems], () => {
  submenuMeasuredHeight.value = 0
  if (!activeSubmenu.value) return
  nextTick(() => {
    const el = submenuFlyoutEl.value
    if (el && activeSubmenu.value) {
      submenuMeasuredHeight.value = el.offsetHeight
    }
  })
})

// Capture the anchor position from the hovered/clicked sidebar item element.
function setSubmenuAnchorFromEvent(e) {
  const el = e && e.currentTarget
  if (!el || !el.getBoundingClientRect) return
  const rect = el.getBoundingClientRect()
  submenuAnchorTop.value = rect.top
  submenuAnchorLeft.value = rect.right
}
// SIDEBAR-FLYOUT END
// SIDEBAR-PUSH-LAYOUT END

// UI-PREFS MIRROR BEGIN (TICK-00188) — the whole per-account look of the rail
// (menu theme, icon scale, favorites, sidebar mode) lives in
// users.ui_preferences. On reload that object is read from the cached profile,
// but it is absent whenever the cache was written without it (fresh login
// payload, cleared storage, another device). The rail then painted its DEFAULT
// look for the entire boot and snapped to the real one once /auth/me answered —
// the reported "sidebar changes for a few seconds after a reload".
// Mirroring the prefs under their own key makes the first frame already correct;
// the profile stays the source of truth and wins key by key as soon as it lands.
// Same anti-FOUC pattern as ui_skin_v1 / icon_style_v1 in stores/branding.js.
const UI_PREFS_LS_KEY = 'ui_prefs_v1'
function readMirroredUiPrefs() {
  try {
    const raw = localStorage.getItem(UI_PREFS_LS_KEY)
    const cached = raw ? JSON.parse(raw) : null
    if (!cached || !cached.prefs || typeof cached.prefs !== 'object') return null
    return cached
  } catch (e) {
    return null
  }
}
const uiPrefsMirror = ref(readMirroredUiPrefs())
// The mirror is stamped with the account it belongs to, so the previous user's
// look can never be applied to the next one on a shared browser.
function mirrorBelongsToCurrentUser(mirror) {
  const uid = authStore.user?.id
  return !!(mirror && uid != null && Number(mirror.uid) === Number(uid))
}
const uiPrefs = computed(() => {
  const live = authStore.user?.ui_preferences || {}
  if (!mirrorBelongsToCurrentUser(uiPrefsMirror.value)) return live
  return { ...uiPrefsMirror.value.prefs, ...live }
})
watch(() => authStore.user?.ui_preferences, (prefs) => {
  const uid = authStore.user?.id
  if (uid == null || !prefs || typeof prefs !== 'object' || !Object.keys(prefs).length) return
  // Mirror exactly what the store holds right now — it is always the full,
  // authoritative object (never a delta). Merging in the previous mirror's
  // keys here would resurrect a key a failed updateUiPreference() revert
  // just removed.
  const next = { uid, prefs: { ...prefs } }
  uiPrefsMirror.value = next
  try {
    localStorage.setItem(UI_PREFS_LS_KEY, JSON.stringify(next))
  } catch (e) { /* quota or private mode — the profile still drives the look */ }
}, { immediate: true, deep: true })
// UI-PREFS MIRROR END

// SIDEBAR-MODE BEGIN (2026-05-22) — two-mode sidebar: compact (icons only) / expanded (icons + labels)
// Persisted per-user via users.ui_preferences JSON column (sidebar_mode: 'compact' | 'expanded').
const SIDEBAR_WIDTH_COMPACT = 64
const SIDEBAR_WIDTH_EXPANDED = 220
// TICK-00061: the chosen mode lives in users.ui_preferences (server source of
// truth), but that arrives only AFTER the profile request resolves on reload.
// Until then the computed fell back to 'compact', so an expanded rail visibly
// flashed to compact and back. Cache the last-known mode in localStorage and
// use it as the pre-profile default so the rail renders in the right mode
// immediately, with no flicker.
const SIDEBAR_MODE_LS_KEY = 'ui.sidebar_mode'
function readCachedSidebarMode() {
  try {
    return localStorage.getItem(SIDEBAR_MODE_LS_KEY) === 'expanded' ? 'expanded' : 'compact'
  } catch (e) {
    return 'compact'
  }
}
const sidebarMode = computed(() => {
  const fromUser = uiPrefs.value.sidebar_mode
  // Once the profile is loaded, it is the source of truth.
  if (fromUser === 'expanded' || fromUser === 'compact') return fromUser
  // Profile not loaded yet — avoid the flash by honoring the cached mode.
  return readCachedSidebarMode()
})
const isSidebarExpanded = computed(() => sidebarMode.value === 'expanded')
// MENU-PERSONALIZATION (2026-06-12): favorites-only flat panel needs labels — force expanded width
const sidebarWidth = computed(() => {
  const base = (favoritesOnly.value || isSidebarExpanded.value) ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COMPACT
  // MENU-ICON-SIZE (rev3): widen the rail as the user enlarges the menu icons so
  // they stay centered and never overflow to the right / overlap (30px = base).
  const extra = Math.max(0, (menuIconSizePx.value || 30) - 30)
  return base + extra
})
function toggleSidebarMode() {
  const next = isSidebarExpanded.value ? 'compact' : 'expanded'
  // Cache immediately (optimistic) so the very next reload is flicker-free even
  // before the server persist round-trips. TICK-00061.
  try { localStorage.setItem(SIDEBAR_MODE_LS_KEY, next) } catch (e) { /* ignore */ }
  authStore.updateUiPreference('sidebar_mode', next)
}
// Keep the localStorage cache in lockstep with the effective mode (covers the
// profile-load path too), so a subsequent reload starts in the right mode.
// TICK-00061.
watch(sidebarMode, (mode) => {
  try { localStorage.setItem(SIDEBAR_MODE_LS_KEY, mode) } catch (e) { /* ignore */ }
}, { immediate: true })
// SIDEBAR-MODE END

// MENU-THEME BEGIN (2026-06-09) — light/dark sidebar theme + 3D menu icons.
// Persisted per-user via users.ui_preferences JSON (menu_theme: 'light' | 'dark'); default 'light'.
const menuTheme = computed(() => (uiPrefs.value.menu_theme === 'dark') ? 'dark' : 'light')
const isMenuDark = computed(() => menuTheme.value === 'dark')
function setMenuTheme(t) {
  if (t !== 'light' && t !== 'dark') return
  authStore.updateUiPreference('menu_theme', t)
}

// APP-DARK-THEME BEGIN (2026-06-12) — app-wide dark mode (branding store).
// `appIsDark` = the EFFECTIVE app theme (user override / system / domain default).
// `sidebarOnDark` = sidebar must render light text/icons: either the per-user
// SIDEBAR pref is dark (navy sidebar) OR the whole app theme is dark.
const vuetifyTheme = useTheme()
const appIsDark = computed(() => brandingStore.isDark)
const sidebarOnDark = computed(() => isMenuDark.value || appIsDark.value)
const allowDarkToggle = computed(() => brandingStore.flags?.allow_user_dark_toggle !== false)

// BILLING VISIBILITY (2026-06-14): the token balance chip is shown ONLY to the
// business owner + system/platform admins. Regular users never see it.
const canSeeTokenBalance = computed(() => {
  const u = authStore.user
  if (!u) return false
  return !!(u.platform_access || u.is_superuser || u.role === 'owner' || u.role === 'administrator')
})

// PREMIUM-SKIN: keep the BUTTON skin in sync with the user's ACCOUNT preference
// (login, profile refresh, cross-device). Source of truth = ui_preferences.ui_skin.
//
// IMPORTANT: only the BUTTON skin follows ui_skin here. The menu ICON style is an
// INDEPENDENT layer (ui_preferences.icon_style, TICK-00144) resolved by
// _readIconStyle()/syncAccountPrefs() and set explicitly from AppearanceView.
// This watcher previously called setSkin(), which forcibly re-coupled the icon
// style to ui_skin (premium→color3d) on EVERY boot — clobbering a saved
// icon_style (e.g. 'paper') for ~1-2s until refreshUser()/syncAccountPrefs()
// restored it, i.e. the "icons flash color3d then the saved set" bug on reload.
// Syncing only the button skin removes that flash without touching the icon set.
watch(
  () => authStore.user?.ui_preferences?.ui_skin,
  (sk) => {
    const next = sk === 'premium' ? 'premium' : 'classic'
    if (brandingStore.buttonSkin !== next) brandingStore.setButtonSkin(next, vuetifyTheme)
  },
  { immediate: true }
)

// MENU-ICON-SIZE (2026-06-13, rev3): user-controlled menu icon scale (1x..3x),
// stored per-account in users.ui_preferences.menu_icon_scale. Resolved to a px
// size (base 30 / 38 premium × scale) and applied as --menu-icon-size. The
// sidebar width also grows with it (see sidebarWidth) so big icons stay centered
// in the rail and never overflow to the right or overlap vertically.
const menuIconScale = computed(() => {
  const s = parseFloat(uiPrefs.value.menu_icon_scale)
  if (!s || isNaN(s)) return 1
  return Math.min(3, Math.max(1, s))
})
const menuIconBasePx = computed(() => {
  // NEW DESIGN: small, clean Figma menu glyphs (~24px, like the design) — not the
  // oversized 38px premium size and not the 30px default. Rich sets stay 38.
  if (brandingStore.isNewDesignIcons) return 24
  return brandingStore.isRichIcons ? 38 : 30
})
const menuIconSizePx = computed(() => Math.round(menuIconBasePx.value * menuIconScale.value))
watch(menuIconSizePx, (px) => {
  document.documentElement.style.setProperty('--menu-icon-size', px + 'px')
}, { immediate: true })
const themeToggleBusy = ref(false)
async function setAppTheme(mode) {
  if (themeToggleBusy.value) return
  if (mode !== 'light' && mode !== 'dark') return
  if (mode === (appIsDark.value ? 'dark' : 'light')) return
  console.log('[App] setAppTheme →', mode)
  themeToggleBusy.value = true
  try {
    await brandingStore.setUserOverride({ theme_mode_override: mode }, vuetifyTheme)
  } catch (e) {
    console.error('[App] setAppTheme failed:', e?.message)
  } finally {
    themeToggleBusy.value = false
  }
}
// APP-DARK-THEME END

// CUSTOM DESIGN: when the active app mode flips (light/dark), branding.applyToDom
// rewrites the base CSS vars — so re-assert the user's active custom theme ON TOP
// for the new mode (otherwise a custom theme would partially revert on toggle).
watch(() => brandingStore.effectiveMode, (mode) => {
  try {
    const custom = useCustomThemesStore()
    if (custom.activeId) custom.applyActive(mode)
  } catch {/* store not ready — ignore */}
})

// COMPANY-BRAND BEGIN (2026-06-09) — current company logo + name shown in the
// header (top-left, next to the menu) and in the avatar dropdown.
// Source of truth = per-domain branding (app_name / logo_url). Falls back to the
// domain name, then to nothing. Header bar is always white, so we use the light logo.
const companyName = computed(() => brandingStore.current?.app_name || authStore.domain?.name || '')
// Dark mode prefers the dark logo variant (header follows the active theme now)
const companyLogo = computed(() => {
  const c = brandingStore.current
  if (!c) return null
  return appIsDark.value
    ? (c.logo_dark_url || c.logo_url || null)
    : (c.logo_url || c.logo_dark_url || null)
})
// COMPANY-BRAND END

// 3D icons for the 12 first-level menu items (rendered instead of the mdi glyph).
const menuIcons3d = import.meta.glob('@/assets/menu-icons-3d/*.png', { eager: true, import: 'default' })
// PREMIUM-SKIN: full-color light/dark icon set, used only when the premium skin is active.
const menuIcons3dLight = import.meta.glob('@/assets/menu-icons-3d/light/*.png', { eager: true, import: 'default' })
const menuIcons3dDark = import.meta.glob('@/assets/menu-icons-3d/dark/*.png', { eager: true, import: 'default' })
// PAPER ICON STYLE (2026-07-19): layered paper-cut set, light + dark variants.
const menuIconsPaperLight = import.meta.glob('@/assets/menu-icons-3d/paper/*.png', { eager: true, import: 'default' })
const menuIconsPaperDark = import.meta.glob('@/assets/menu-icons-3d/paper-dark/*.png', { eager: true, import: 'default' })
// NEW DESIGN ICON STYLE (2026-09-11): Iconly-Pro two-tone set exported from the
// new CRM Figma design, light + dark variants. See docs/FIGMA_DESIGN_MAP.md.
const menuIconsNewLight = import.meta.glob('@/assets/menu-icons-3d/newdesign/*.png', { eager: true, import: 'default' })
const menuIconsNewDark = import.meta.glob('@/assets/menu-icons-3d/newdesign-dark/*.png', { eager: true, import: 'default' })
// 3D "5 + star" brand mark — shown in BOTH default and premium skins.
// Dark (teal-glow) variant on a dark sidebar, light steel variant on a light one.
import logo5Light from '@/assets/brand/logo5-light.png'
import logo5Dark from '@/assets/brand/logo5-dark.png'
const brandLogo = computed(() => (sidebarOnDark.value ? logo5Dark : logo5Light))
const MENU_ICON_KEY = {
  'Home': 'home',
  'Sales & CRM': 'sales',
  'Marketing': 'marketing',
  'Contact Center': 'contact-center',
  'Field Service': 'field-service',
  'Inventory & Parts': 'inventory',
  'Finance': 'finance',
  // Module renamed People -> Team (2026-07-08); icon assets keep the 'people' key
  'Team': 'people',
  'Insights & Planning': 'insights',
  'Settings': 'settings',
  'Notifications': 'notifications',
  'Knowledge Base': 'knowledge',
  'Support': 'support',
  // Quick Access pseudo-module (QUICK_ACCESS_TITLE === '__quick_access__')
  '__quick_access__': 'quick-access',
}
function menuIcon3d(title) {
  const key = MENU_ICON_KEY[title]
  if (!key) return ''
  const style = brandingStore.iconStyle
  // FLAT: no PNG — the template falls back to the monochrome mdi glyph.
  if (style === 'flat') return ''
  // PAPER: layered paper-cut set. Dark variant used on a dark sidebar surface.
  if (style === 'paper') {
    const set = sidebarOnDark.value ? menuIconsPaperDark : menuIconsPaperLight
    const pp = Object.keys(set).find((p) => p.endsWith(`/${key}.png`))
    if (pp) return set[pp]
  }
  // NEW DESIGN: Iconly-Pro two-tone set. Dark (white) variant on a dark sidebar,
  // dark-ink variant on a light one. Keys not covered by the Figma sidebar fall
  // back to the monochrome mdi glyph (return '') so the set stays consistent.
  if (style === 'newdesign') {
    const set = sidebarOnDark.value ? menuIconsNewDark : menuIconsNewLight
    const pp = Object.keys(set).find((p) => p.endsWith(`/${key}.png`))
    return pp ? set[pp] : ''
  }
  // COLOR3D: full-color light/dark variant. The dark variant is used whenever
  // the sidebar renders on a dark surface (dark menu or dark app).
  if (style === 'color3d') {
    const set = sidebarOnDark.value ? menuIcons3dDark : menuIcons3dLight
    const pp = Object.keys(set).find((p) => p.endsWith(`/${key}.png`))
    if (pp) return set[pp]
  }
  // DUOTONE (and color3d/paper fallback when a variant is missing): base set.
  const path = Object.keys(menuIcons3d).find((p) => p.endsWith(`/${key}.png`))
  return path ? menuIcons3d[path] : ''
}
// COLOR3D HEADER ICONS (2026-06-14): full-color 3D glyphs for the top app-bar
// action buttons. Light variants only for now — on a dark app theme we fall back
// to the original mdi glyph until the dark header set is generated.
function headerIcon3d(key) {
  const style = brandingStore.iconStyle
  // PAPER: has both light AND dark header variants.
  if (style === 'paper') {
    const set = appIsDark.value ? menuIconsPaperDark : menuIconsPaperLight
    const pp = Object.keys(set).find((p) => p.endsWith(`/${key}.png`))
    return pp ? set[pp] : ''
  }
  // COLOR3D: light-only header set; on dark theme fall back to the mdi glyph.
  if (style !== 'color3d' || appIsDark.value) return ''
  const pp = Object.keys(menuIcons3dLight).find((p) => p.endsWith(`/${key}.png`))
  return pp ? menuIcons3dLight[pp] : ''
}
// Restart the spin+hop animation on hover/click of a menu row.
function spinMenuIcon(e) {
  // MENU-PERSONALIZATION (2026-06-12): user can disable icon animation in Menu Settings
  if (!menuAnimationEnabled.value) return
  const root = e && e.currentTarget
  if (!root || !root.querySelector) return
  const el = root.querySelector('.menu-icon-3d, .menu-icon-spin')
  if (!el) return
  el.classList.remove('spin')
  void el.offsetWidth // force reflow so the animation can restart
  el.classList.add('spin')
}
// MENU-THEME END

const sidebarContentRef = ref(null)
const sidebarCanScrollDown = ref(false)

function checkSidebarOverflow() {
  const el = sidebarContentRef.value
  if (!el) return
  const canScroll = el.scrollHeight > el.clientHeight + 2
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
  sidebarCanScrollDown.value = canScroll && !atBottom
}

function onSidebarScroll() {
  checkSidebarOverflow()
}

function scrollSidebarDown() {
  const el = sidebarContentRef.value
  if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
}

// Reload dialer settings when user authenticates
watch(() => authStore.isAuthenticated, (isAuth, wasAuth) => {
  if (window.location.pathname.startsWith('/portal')) return
  console.warn('[App][DialerBlink] isAuthenticated changed:', wasAuth, '->', isAuth, new Error().stack?.split('\n').slice(1,3).join(' <- '))
  if (isAuth && !isNative() && !route.path.startsWith('/technician') && !route.path.startsWith('/login')) {
    setTimeout(() => loadDialerSettings(), 500)
  } else if (!isAuth) {
    console.warn('[App][DialerBlink] RESETTING dialerSettings (isAuth=false)')
    dialerSettings.value = { allow_dialer_v1: false, allow_dialer_v2: false, allow_dialer_v3: false }
    dialerCaps.value = { can_make_calls: false, can_receive_calls: false }
  }
})

// TICK-00022: company-level toggle for the floating Oliver widget.
const companyCtxStore = useCompanyContext()
// Company Flow: optional-block visibility (e.g. hide Inventory & Parts menu for
// a cleaning company). field() reads the shared company context.
const { field: flowField, icon: flowIcon, isCleaning: flowIsCleaning } = useFlow()
// TC-12-00068: gate flow-dependent menu items until the company context that
// decides their visibility has resolved. Without this, a flow-gated item (e.g.
// "Inventory & Parts") renders with its system default = shown, then hides once
// the context/preset arrives — a visible flash on reload for a cleaning
// company. context.value becomes non-null on success AND on failure (the store
// falls back to a defaults object in .catch), so this flips true exactly once
// the async resolution settles and never stays pending forever.
const flowContextReady = computed(() => companyCtxStore.context.value != null)
watch(flowContextReady, (ready) => {
  if (ready) console.debug('[Menu][Flow] company context ready — flow-gated menu items now evaluated')
}, { immediate: true })
// Per-company worker terminology for the nav labels (e.g. "Cleaners" for a
// cleaning company). Read once when the menu is built; a domain switch reloads
// the app context.
const { term: workerTerm, termPlural: workerTermPlural } = useWorkerTerm()
const oliverWidgetEnabled = computed(
  () => companyCtxStore.context.value?.oliver_widget_enabled !== false,
)
// AP-7-00340: per-company custom Oliver badge icon. When a company uploads a
// custom icon in Company Settings, the office top-bar avatar (and the floating
// Oliver chat widget) show it; when empty (the default for every company) the
// header keeps the built-in 3D robot avatar — pixel-identical to today.
const oliverBadgeIconUrl = computed(
  () => companyCtxStore.context.value?.oliver_badge_icon_url || null,
)
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth && !isPortalRoute.value && !isDialerWindow.value) companyCtxStore.load(true)
}, { immediate: true })

watch(() => authStore.domain?.id, (domainId, prevId) => {
  if (!domainId || domainId === prevId) return
  if (!authStore.isAuthenticated || isPortalRoute.value || isDialerWindow.value) return
  companyCtxStore.reset()
  companyCtxStore.load(true)
})

// isTechnicianSession() reads localStorage, so the layout computed that depends
// on it does not react to the flag being restored — and the router has already
// sent this session to an office route (/dashboard) precisely because the flag
// was missing. So whoever restores it also has to move the session into the
// technician app, honouring the router's own allowlist of public legal pages a
// technician is allowed to stay on (Google Play requires them reachable).
function runNativeSessionRepair() {
  repairNativeSessionRights(authStore, isNative(), localStorage)
    .then(({ flagRestored }) => {
      if (!flagRestored || !authStore.isAuthenticated) return
      // Re-read the route here: the repair awaits the network, and the technician
      // may have navigated (or been signed out) while it ran.
      const path = route.path
      if (path.startsWith('/technician') || TECH_SESSION_PUBLIC_PATHS.has(path)) return
      const learning = technicianLearningLocation(route)
      if (learning) router.replace(learning).catch(() => { /* redundant navigation */ })
      else router.replace('/technician').catch(() => { /* redundant navigation */ })
    })
    .catch(e => console.warn('[App] Native session repair failed:', e?.message || e))
}

// A native session can come back to life AFTER mount: the biometric vault keeps
// its own refresh-token copy, purgeZombieSessionIfNeeded() restores it on the
// login screen and the next silent refresh authenticates from it. The mount-time
// repair cannot see that, and nothing rewrote the rights or the technician flag
// afterwards — so the office shell rendered to a technician with every menu group
// denied. The route is watched alongside the flag because such a revival happens
// ON /login: isAuthenticated stops changing before the guard redirects away.
// /login itself is excluded — an interactive biometric login flips isAuthenticated
// true there on its way to the company picker, and it restores the profile,
// company and rights itself, so repairing underneath it would race it.
watch([() => authStore.isAuthenticated, () => route.path], ([isAuth, path]) => {
  // The next sign-in must not inherit a spent repair budget.
  if (!isAuth) return resetNativeSessionRights()
  if (path.startsWith('/login')) return
  runNativeSessionRepair()
})

watch(showTaskNotifMenu, (open) => {
  if (open) fetchTaskNotifList()
})

watch(showPartsNotifMenu, (open) => {
  if (open) fetchPartsNotifList()
})

const currentPermissions = computed(() => authStore.permissions || [])


/**
 * Check if user has permission for menu item
 * Uses currentPermissions computed for proper reactivity
 */
const hasMenuPermission = (item) => {
  // Platform HQ gating takes precedence: items/headers flagged platformHQ are
  // visible ONLY while working inside the Platform HQ company (domain.is_platform),
  // and disappear from every client company — regardless of RBAC permission.
  // Mirrors the backend IsPlatformHQDomain boundary. We still honour any RBAC
  // permission on the item (e.g. so a Platform-HQ user without system.view_metrics
  // does not see that specific leaf), hence the fallthrough to the checks below.
  if (item.platformHQ && !authStore.isPlatformHQ) return false

  // Per-tenant opt-out for a single menu entry: the item disappears only for the
  // domain codes listed on it, every other company keeps it untouched. Used for
  // cross-linked shortcuts that one company considers a duplicate — the route
  // itself stays reachable from its canonical group, so nothing is lost.
  if (item.hiddenForDomains && item.hiddenForDomains.includes(authStore.domain?.code)) return false

  // Skip dividers and headers without routes
  if (item.divider || (item.header && !item.route)) return true
  
  // No permission specified on item - visible to all
  if (!item.permission) return true
  
  // Get permissions from computed (ensures reactivity)
  const perms = currentPermissions.value

  // If permissions not loaded - DENY (strict mode)
  if (!perms || !Array.isArray(perms) || perms.length === 0) {
    console.warn('🚫 [Menu] No permissions, denying:', item.title)
    return false
  }
  
  // Check single permission
  if (typeof item.permission === 'string') {
    return perms.includes(item.permission)
  }
  
  // Check array of permissions (user needs ANY of them)
  if (Array.isArray(item.permission)) {
    return item.permission.some(p => perms.includes(p))
  }
  
  return false
}

/**
 * Filter menu items based on user permissions
 * Also filters submenu items
 */
// White-label entitlement gate for a menu item: hidden when the tenant does not
// have the feature its route belongs to. No-op for normal domains (all features
// enabled) and for routeless items (dividers/headers) or unmapped routes.
const hasMenuFeature = (item) => {
  if (!item || item.divider || (item.header && !item.route)) return true
  return authStore.hasFeature(routeToFeature(item.route))
}

// AP-7-00368/00369: per-company hide switch for a single menu entry, driven by
// a company-context flag (system_settings). Hidden only when the named flag is
// true for the current company; no-op for items without `hideWhenFlag`. Until
// the context loads we keep the item visible (its default), so the majority of
// companies — for whom the flag is off — never see a flash, and only the one
// company that opted in loses the entry once the context arrives.
//
// `showWhenFlag` (FSJ-6-00071 Loyalty): inverse opt-in — hide until context
// confirms the named flag is true (fail-closed modules default OFF).
const hasMenuCompanyFlag = (item) => {
  if (!item) return true
  const ctx = companyCtxStore.context.value
  if (item.showWhenFlag) {
    if (!ctx) return false
    return ctx[item.showWhenFlag] === true
  }
  if (!item.hideWhenFlag) return true
  if (!ctx) return true
  return ctx[item.hideWhenFlag] !== true
}

// Company Flow gate for a menu item: hidden when the company turned this
// optional block off (e.g. a cleaning company hides Inventory & Parts). No-op
// for items without a flowField, so every other menu entry is unaffected.
const hasMenuFlowField = (item) => {
  if (!item || !item.flowField) return true
  // TC-12-00068: until the company context/flow data has loaded, hide every
  // flow-gated item instead of rendering its default-shown state and then
  // hiding it once the preset/context arrives (the reload flash). This is
  // targeted: only items carrying a `flowField` are held back — the rest of the
  // menu still paints immediately. Once ready, the item appears only if enabled.
  if (!flowContextReady.value) {
    console.debug('[Menu][Flow] holding flow-gated item until context ready:', item.title, item.flowField)
    return false
  }
  return flowField(item.flowField)
}

// MENU-AUDIT (2026-07-08): permission/feature filtering can leave visual junk in
// a submenu — several dividers in a row (items between them got filtered out),
// dividers glued to section headers, or a header whose whole group is hidden.
// Normalize the visible list: drop empty-group headers, collapse dividers,
// never render a divider at the start/end or next to a header.
const normalizeSubmenu = (items) => {
  const withGroups = []
  for (let i = 0; i < items.length; i++) {
    const it = items[i]
    if (it.header) {
      let hasLeaf = false
      for (let j = i + 1; j < items.length; j++) {
        if (items[j].header) break
        if (!items[j].divider) { hasLeaf = true; break }
      }
      if (!hasLeaf) continue // header with no visible items under it
    }
    withGroups.push(it)
  }
  const res = []
  for (const it of withGroups) {
    if (it.divider) {
      const prev = res[res.length - 1]
      if (!prev || prev.divider || prev.header) continue // no leading/double/after-header dividers
      res.push(it)
      continue
    }
    if (it.header && res.length && res[res.length - 1].divider) {
      res.pop() // header is a separator by itself — drop the divider before it
    }
    res.push(it)
  }
  while (res.length && res[res.length - 1].divider) res.pop() // no trailing divider
  return res
}

// Render-time Company-Flow display swap. The menu array keeps plain string
// literals for `icon`/`title` so the voice-map scanner (which reads App.vue with
// regexes that expect `icon: 'mdi-…'` / `title: '…'`) still learns every page —
// but a company on a vertical preset sees its own glyph and wording:
//   flowIcon: '<role>'       → swap the icon for that trade (broom for cleaning)
//   flowWorkerTitle: true    → swap "Technician" in the title for the worker term
// No-op for the default vertical, so every other company is byte-for-byte the same.
const applyFlowDisplay = (item) => {
  if (!item || (!item.flowIcon && !item.flowWorkerTitle && !item.flowWorkerRoute)) return item
  const out = { ...item }
  if (item.flowIcon) out.icon = flowIcon(item.flowIcon)
  if (item.flowWorkerTitle && workerTerm.value !== 'Technician') {
    out.title = String(item.title || '')
      .replace(/Technicians/g, workerTermPlural.value)
      .replace(/Technician/g, workerTerm.value)
  }
  // Cleaning companies address the field-worker page as /cleaners so the URL bar
  // reads the trade, not "technicians" (TC-67). Resolved at render time (reactive
  // to the loaded company context) so a stale localStorage domain — or the menu
  // array being built once before the context request resolves — can no longer
  // freeze the wrong route/icon/title into the nav.
  if (item.flowWorkerRoute && flowIsCleaning.value) {
    out.route = item.flowWorkerRoute
  }
  return out
}

const filterMenuByPermissions = (items) => {
  return items
    .filter(item => hasMenuPermission(item) && hasMenuFeature(item) && hasMenuFlowField(item) && hasMenuCompanyFlag(item))
    .map(item => {
      // Only filter submenu if it has items
      if (item.submenu && item.submenu.length > 0) {
        const filteredSubmenu = normalizeSubmenu(
          item.submenu
            .filter(sub => hasMenuPermission(sub) && hasMenuFeature(sub) && hasMenuFlowField(sub) && hasMenuCompanyFlag(sub))
            .map(applyFlowDisplay)
        )
        // Mark if submenu was emptied by filtering
        return { ...applyFlowDisplay(item), submenu: filteredSubmenu, _hadSubmenu: true }
      }
      return applyFlowDisplay(item)
    })
    // Only remove items that HAD submenu items but now have none after filtering.
    // Items with empty submenu from the start (like Dashboard, Ideas, Chat) should stay.
    .filter(item => !(item._hadSubmenu && item.submenu.length === 0))
}

// Menu structure - ALL modules protected by permissions
// permission: string or array - required permission(s) to see this menu item
// Every module MUST have a permission for proper RBAC
// ============================================================================
// MENU IA REDESIGN — Phase 1 (2026-06-09)
// 15 modules regrouped into 9 task-based groups. ROUTES & PERMISSIONS of every
// leaf are PRESERVED 1:1 — only the grouping + section headers changed.
// Parent `permission` is an array of all child permissions (ANY → group shows).
//
// Phase 2 (TICK-00226, 2026-07-31): trim daily-use flyouts — infrequent admin,
// legacy, sandbox and module-specific settings moved under Settings → section
// headers (Sales & CRM, Call Center, Field Service, Finance, Inventory, HR).
// No routes removed; every relocated leaf keeps the same path + permission.
// ============================================================================
const menuItems = [
  {
    icon: 'mdi-home-outline',
    title: 'Home',
    route: '/dashboard',
    permission: ['dashboard.view', 'tasks.view', 'tasks.my_tasks'],
    submenu: [
      { title: 'Workspace', header: true },
      { title: 'My Dashboard', route: '/dashboard', icon: 'mdi-view-dashboard-outline', permission: 'dashboard.view' },
      { title: 'Tasks', route: '/tasks', icon: 'mdi-format-list-checks', permission: ['tasks.my_tasks', 'tasks.created', 'tasks.view_team', 'tasks.view_all'] },
      { title: 'Tasks v2', route: '/tasks-v2', icon: 'mdi-format-list-checkbox', permission: ['tasks.my_tasks', 'tasks.created', 'tasks.view_team', 'tasks.view_all'] },
      // Tutorial v2 — new-design coaching-practice preview (visible to all).
      { title: 'Tutorial v2', route: '/tutorial-v2', icon: 'mdi-school-outline', permission: null },
      // Ideas moved to Settings → Administration (admin-only). See the Settings menu.
      // TICK-00226: Personal Settings + Appearance removed from the daily Home
      // flyout — both live under Settings (Appearance) or the dashboard gear
      // (Personal Settings at /dashboard/settings).
    ]
  },
  {
    icon: 'mdi-account-group',
    title: 'Sales & CRM',
    route: '/crm/panel',
    permission: ['crm.view', 'sales.view', 'crm.leads', 'crm.customers', 'sales.assessment.view', 'mail_monitor.view', 'sales.monitor', 'sales.training.view_team'],
    submenu: [
      { title: 'Pipeline', header: true },
      { title: 'CRM Panel', route: '/crm/panel', icon: 'mdi-view-dashboard-outline', permission: 'crm.view' },
      { title: 'CRM 2', route: '/crm/panel2', icon: 'mdi-view-dashboard-variant-outline', permission: 'crm.view' },
      { title: 'Inbound Journal', route: '/crm/inbound', icon: 'mdi-phone-incoming', permission: 'crm.view' },
      { title: 'Inbound Kanban', route: '/crm/inbound-kanban', icon: 'mdi-view-column-outline', permission: 'crm.view' },
      { title: 'Sales Report', route: '/crm/inbound-sales-report', icon: 'mdi-chart-box-outline', permission: 'crm.view' },
      { title: 'Lead Center', route: '/crm/lead-center', icon: 'mdi-headset', permission: 'crm.view' },
      { title: 'Mail Monitor', route: '/crm/mail-monitor', icon: 'mdi-email-search-outline', permission: 'mail_monitor.view' },
      // AP-7-00368/00369: this entry opens the CRM Lead Sources page, which is
      // empty for companies that do not track lead sources. hideWhenFlag hides
      // it only for a company that opted in (system_settings); every other
      // company keeps it exactly as before.
      { title: 'Leads', route: '/crm/leads', icon: 'mdi-account-plus-outline', permission: 'crm.leads', hideWhenFlag: 'crm_lead_sources_hidden' },
      { title: 'Deals', route: '/crm/deals', icon: 'mdi-handshake-outline', permission: 'crm.view' },
      { title: 'Deals Kanban v2', route: '/sales/deals-kanban-v2', icon: 'mdi-view-column-outline', permission: 'crm.view' },
      { title: 'Companies', route: '/crm/companies', icon: 'mdi-domain', permission: 'crm.view' },
      { title: 'Site Assessments', route: '/sales/assessments', icon: 'mdi-clipboard-search-outline', permission: 'sales.assessment.view' },
      { title: 'Customers', header: true },
      // TICK-00149: "Contacts" mounted the same CRMView as Customers (duplicate)
      // and "History" led to a dead /crm/history with no route. Collapsed to a
      // single Customers entry; the old URLs now redirect (see router/index.js).
      { title: 'Customers', route: '/crm/customers', icon: 'mdi-account-group-outline', permission: 'crm.customers' },
      // Customers v2 — isolated NEW-DESIGN screen (Artem Figma "1.0 Customers").
      // Additive; sits next to the classic Customers entry.
      { title: 'Customers v2', route: '/crm/customers-v2', icon: 'mdi-account-multiple-outline', permission: 'crm.view' },
      { title: 'Sales Activity', header: true },
      { title: 'Activity Board', route: '/sales/activity', icon: 'mdi-bulletin-board', permission: 'sales.view' },
      { title: 'Sales Team Dashboard', route: '/sales/team', icon: 'mdi-monitor-dashboard', permission: 'sales.monitor' },
      { title: 'My Homework', route: '/sales/my-homework', icon: 'mdi-notebook-outline', permission: 'sales.view' },
      // Enablement: AI Simulator stays in the daily Sales & CRM flyout as a day-to-day
      // seller tool. Live AI Coach / Coaching / Benchmark Library live under Settings.
      { title: 'Enablement', header: true },
      { title: 'AI Simulator', route: '/sales/simulator-v2', icon: 'mdi-robot-outline', permission: 'sales.view' },
      { title: 'Training Analytics', route: '/sales/training-analytics', icon: 'mdi-chart-box-outline', permission: 'sales.training.view_team' },
      { title: 'Training Analytics v2', route: '/sales/training-analytics-v2', icon: 'mdi-chart-timeline-variant', permission: 'sales.training.view_team' },
      { title: 'Settings', header: true, permission: 'sales.view' },
      { title: 'Sales Settings', route: '/sales/settings', icon: 'mdi-cog-outline', permission: 'sales.view' }
    ]
  },
  {
    icon: 'mdi-bullhorn-outline',
    title: 'Marketing',
    route: '/crm/analytics',
    permission: ['crm.view_analytics', 'crm.customer_export', 'crm.marketing_sheets.view', 'tracking.view', 'customer_portal.view', 'crm.view_email_marketing', 'crm.reputation.view', 'crm.reputation.manage', 'crm.automations.view', 'marketing.seo.drafts', 'marketing.loyalty.view', 'marketing.loyalty.manage'],
    submenu: [
      { title: 'Marketing & Attribution', header: true },
      { title: 'Analytics', route: '/crm/analytics', icon: 'mdi-chart-line', permission: 'crm.view_analytics' },
      { title: 'Customer Export', route: '/crm/customer-export', icon: 'mdi-table-arrow-down', permission: 'crm.customer_export' },
      { title: 'Customer Automations', route: '/crm/automations', icon: 'mdi-robot-happy-outline', permission: 'crm.automations.view' },
      { title: 'Cross-Channel Attribution', route: '/crm/channel-attribution', icon: 'mdi-chart-sankey', permission: 'crm.view_analytics' },
      { title: 'Marketing Sheets', route: '/crm/marketing-sheets', icon: 'mdi-table-large', permission: 'crm.marketing_sheets.view' },
      { title: 'Google Ads / LSA', route: '/crm/google-ads', icon: 'mdi-google-ads', permission: 'crm.view_analytics' },
      { title: 'Call Tracking (DNI)', route: '/crm/call-tracking', icon: 'mdi-phone-log', permission: 'tracking.view' },
      { title: 'Ad Costs (Spend)', route: '/crm/ad-costs', icon: 'mdi-currency-usd', permission: 'crm.view_analytics' },
      { title: 'SMS Open Rate Report', route: '/crm/portal-analytics', icon: 'mdi-message-text-outline', permission: 'customer_portal.view' },
      { title: 'Email Marketing', route: '/crm/email-marketing', icon: 'mdi-email-outline', permission: 'crm.view_email_marketing' },
      // FSJ-6-00071: Loyalty constructor — visible only when modules.loyalty_referrals.enabled
      { title: 'Loyalty & Referrals Setup', route: '/crm/loyalty-referrals', icon: 'mdi-gift-outline', permission: ['marketing.loyalty.view', 'marketing.loyalty.manage'], showWhenFlag: 'loyalty_referrals_enabled' },
      { title: 'SEO', header: true, permission: 'marketing.seo.drafts' },
      { title: 'Case Study SEO Drafts', route: '/crm/seo-drafts', icon: 'mdi-file-document-edit-outline', permission: 'marketing.seo.drafts' },
      { title: 'Reputation', header: true, permission: 'crm.reputation.view' },
      { title: 'Reviews Inbox', route: '/crm/reputation/reviews', icon: 'mdi-star-outline', permission: 'crm.reputation.view' },
      { title: 'Review Follow-up', route: '/crm/reputation/followup-monitor', icon: 'mdi-star-shooting-outline', permission: 'crm.reputation.view' },
      { title: 'Recovery', route: '/crm/reputation/recovery', icon: 'mdi-lifebuoy', permission: 'crm.reputation.view' },
      { title: 'Competitor Benchmark', route: '/crm/reputation/competitors', icon: 'mdi-podium', permission: 'crm.reputation.view' },
      { title: 'Review Widget & QR', route: '/crm/reputation/widget', icon: 'mdi-qrcode', permission: 'crm.reputation.view' },
      { title: 'Weekly Digest', route: '/crm/reputation/digest', icon: 'mdi-email-newsletter', permission: 'crm.reputation.view' },
      { title: 'Team Quality', route: '/crm/reputation/team-quality', icon: 'mdi-account-group-outline', permission: 'crm.reputation.view' },
      { title: 'NPS / CSAT', route: '/crm/reputation/surveys', icon: 'mdi-poll', permission: 'crm.reputation.view' },
      { title: 'Review Campaigns', route: '/crm/reputation/campaigns', icon: 'mdi-bullhorn-outline', permission: 'crm.reputation.manage' },
      { title: 'Reputation Analytics', route: '/crm/reputation/analytics', icon: 'mdi-chart-box-outline', permission: 'crm.reputation.view' },
      { title: 'Reputation Settings', route: '/crm/reputation/settings', icon: 'mdi-cog-outline', permission: 'crm.reputation.view' }
    ]
  },
  {
    icon: 'mdi-headset',
    title: 'Contact Center',
    route: '/call-center',
    permission: ['dialer.view', 'dialer.monitor', 'chat.view', 'communications.sms.view'],
    submenu: [
      { title: 'Telephony', header: true },
      { title: 'Monitor', route: '/call-center/monitor', icon: 'mdi-monitor-dashboard', permission: 'dialer.monitor' },
      // TICK-00010: "My Phone" page hidden — it duplicated Contact Center settings
      // and Inbound Calls views. The dialer itself opens from the header phone button.
      { title: 'Call Logs', route: '/call-center/logs', icon: 'mdi-phone-log', permission: 'dialer.view' },
      { title: 'Call Feed v2', route: '/call-center/feed-v2', icon: 'mdi-phone-in-talk-outline', permission: 'dialer.view' },
      { title: 'Voicemail', route: '/call-center/voicemails', icon: 'mdi-voicemail', permission: 'voicemail.view' },
      { title: 'Routing', header: true },
      { title: 'Call Groups', route: '/call-center/groups', icon: 'mdi-account-group', permission: 'call_groups.view' },
      { title: 'Routing Rules', route: '/call-center/routing', icon: 'mdi-routes', permission: 'routing_rules.view' },
      { title: 'Next Steps', route: '/call-center/next-steps', icon: 'mdi-skip-next', permission: 'next_steps.view' },
      // TICK-00226: IVR, legacy flow editor, diagnostics, billing and dialer
      // settings moved to Settings → Call Center (same routes + permissions).
      { title: 'Chat', header: true },
      { title: 'Unified Inbox', route: '/communications/inbox', icon: 'mdi-inbox-multiple', permission: ['dialer.monitor', 'communications.sms.view'] },
      { title: 'Marketplace Inbox', route: '/communications/marketplace-inbox', icon: 'mdi-robot-happy-outline', permission: 'integrations.marketing.view' },
      { title: 'Messages', route: '/communications/messages', icon: 'mdi-message-text', permission: ['dialer.monitor', 'communications.sms.view'] },
      { title: 'Team Chat', route: '/chat', icon: 'mdi-chat', permission: 'chat.view' },
      { title: 'Reports', header: true },
      { title: 'Reports', route: '/call-center/reports', icon: 'mdi-chart-bar', permission: 'dialer.view_reports' }
    ]
  },
  {
    // Literal icon + adjacent title keep the voice-map scanner able to read this
    // group (its regex needs `icon: '…'` immediately followed by `title: '…'`);
    // the trade glyph (broom for cleaning) is swapped in at render time via the
    // flowIcon role below.
    icon: 'mdi-account-hard-hat',
    title: 'Field Service',
    flowIcon: 'worker',
    route: '/jobs',
    permission: ['jobs.view', 'dispatch.view', 'territories.view'],
    submenu: [
      { title: 'Jobs', header: true },
      { title: 'All Jobs', route: '/jobs/board', icon: 'mdi-briefcase-outline', permission: 'jobs.view' },
      { title: 'All Jobs v2', route: '/jobs/board-v2', icon: 'mdi-briefcase-variant-outline', permission: 'jobs.view' },
      { title: 'New Job', route: '/jobs/new', icon: 'mdi-briefcase-plus-outline', permission: 'jobs.create' },
      { title: 'New Job v2', route: '/jobs/new-v2', icon: 'mdi-briefcase-plus', permission: 'jobs.create' },
      { title: 'Schedule', route: '/jobs/schedule', icon: 'mdi-calendar-outline', permission: 'jobs.view' },
      { title: 'Schedule (new)', route: '/jobs/schedule-newdesign', icon: 'mdi-calendar-star', permission: 'jobs.view' },
      { title: 'Schedule v2', route: '/jobs/schedule-v2', icon: 'mdi-calendar-week', permission: 'jobs.view' },
      // TICK-00226: Job Settings moved to Settings → Field Service.
      // TICK-00210: Pay Models shortcut — also under Finance → Technician Payroll.
      // TV-1-00293: Techvill reads that cross-link as a duplicate, so the shortcut
      // is hidden for them only; their canonical entry lives under Finance → Payroll.
      { title: 'Pay Models', route: '/field-service/payroll/models', icon: 'mdi-cash-sync', permission: 'payroll.manage', hiddenForDomains: ['techvill'], voiceHidden: true },
      { title: 'Dispatch', header: true },
      { title: 'Live Board', route: '/dispatch', icon: 'mdi-monitor-dashboard', permission: 'dispatch.live_board' },
      { title: 'Live Board v2', route: '/dispatch-v2', icon: 'mdi-monitor-cellphone', permission: 'dispatch.live_board' },
      { title: 'Coverage Needs', route: '/dispatch/coverage-needs', icon: 'mdi-account-clock', permission: 'dispatch.view' },
      { title: 'Crew Vehicles', route: '/fleet/vehicles', icon: 'mdi-van-utility', permission: 'vehicles.view', flowField: 'menu.fleet' },
      { title: 'Route Planner', route: '/fleet/trips', icon: 'mdi-map-marker-path', permission: 'trips.view', flowField: 'menu.fleet' },
      { title: 'Production', header: true },
      // Literal icon/title keep the voice-map scanner able to read these leaves;
      // the flow* roles swap them at render time (broom + "Cleaners" + /cleaners
      // URL for a cleaning company), reactively to the loaded context (TC-67/77).
      { title: 'Technicians', flowWorkerTitle: true, route: '/production/technicians', flowWorkerRoute: '/production/cleaners', icon: 'mdi-account-hard-hat', flowIcon: 'worker', permission: 'territories.technicians' },
      { title: 'Technician Zones', flowWorkerTitle: true, route: '/production/technician-zones', icon: 'mdi-map-outline', permission: 'territories.tz.view' },
      { title: 'Coverage self-test', route: '/production/coverage-self-test', icon: 'mdi-map-check-outline', permission: 'territories.tz.view' },
      { title: 'Projects & Zones', route: '/production/projects', icon: 'mdi-map-marker-radius-outline', permission: 'territories.projects' },
      { title: 'Regions', route: '/production/regions', icon: 'mdi-earth', permission: 'territories.view_regions' },
      { title: 'Skills', route: '/production/skills', icon: 'mdi-certificate', permission: 'territories.skills' },
      { title: 'Analytics', route: '/production/analytics', icon: 'mdi-chart-bar', permission: 'territories.analytics' },
      // TICK-00226: Tech Hours Report moved to Insights & Planning → Reports.
      // TICK-00209–211: Payroll Report / Models / Assignments moved to Finance.
    ]
  },
  {
    icon: 'mdi-package-variant-closed',
    title: 'Inventory & Parts',
    route: '/operations',
    permission: ['operations.view', 'parts.view_inventory', 'parts.manage_dashboard', 'parts.view_catalog'],
    flowField: 'menu.inventory_parts',
    submenu: [
      { title: 'Stock', header: true },
      { title: 'Parts Manager', route: '/operations/parts-manager', icon: 'mdi-view-dashboard-outline', permission: 'parts.manage_dashboard' },
      { title: 'Inventory Search', route: '/operations/inventory-search', icon: 'mdi-magnify', permission: 'parts.view_inventory' },
      { title: 'Inventory', route: '/operations/inventory', icon: 'mdi-package-variant', permission: 'parts.view_inventory' },
      { title: 'Supplies', route: '/operations/supplies', icon: 'mdi-spray-bottle', permission: 'supplies.view' },
      { title: 'Bulk Import Inventory', route: '/operations/inventory/bulk-import', permission: 'parts.bulk_import', icon: 'mdi-file-upload-outline' },
      // FSJ-6-00070 Phase 3 hub: Truck Stock + Van Audits folded into Vehicle Stock tabs
      // (Stock | Replenishment | Returns | Audits). Legacy routes redirect / remain for voice.
      { title: 'Vehicle Stock', route: '/operations/vehicle-stock', icon: 'mdi-car-estate', permission: ['parts.view_inventory', 'parts.truck_stock.manage', 'parts.van_audit'] },
      { title: 'Warehouses', route: '/operations/warehouses', icon: 'mdi-warehouse', permission: 'parts.view_inventory' },
      // TICK-00226: legacy/sandbox/admin parts tools moved to Settings → Inventory.
      { title: 'Purchasing', header: true },
      { title: 'Purchase Orders', route: '/operations/purchase-orders', icon: 'mdi-cart-outline', permission: 'parts.manage_po' },
      { title: 'Supplier Inbox', route: '/operations/supplier-inbox', icon: 'mdi-email-sync-outline', permission: 'parts.manage_po' },
      { title: 'Mobile Receive', route: '/operations/mobile-receive', icon: 'mdi-cellphone-arrow-down', permission: 'parts.process_request' },
      { title: 'Returns / RMA', route: '/operations/returns', icon: 'mdi-keyboard-return', permission: 'parts.manage_returns' },
      { title: 'Supplier Invoices', route: '/operations/supplier-invoices', icon: 'mdi-file-document-check-outline', permission: 'parts.view_supplier_invoices' },
      { title: 'Payment Proposals', route: '/operations/payment-proposals', icon: 'mdi-cash-check', permission: 'parts.view_supplier_invoices' },
      { title: 'Suppliers', route: '/operations/suppliers', icon: 'mdi-domain', permission: 'parts.view_suppliers' },
      { title: 'Audits', header: true },
      { title: 'Inventory Audits', route: '/operations/inventory/audits', permission: 'parts.stock_take.view', icon: 'mdi-clipboard-check-outline' },
      { title: 'Parts Audit Log', route: '/operations/audit-log', icon: 'mdi-file-document-outline', permission: 'parts.view_audit' },
      { title: 'Catalog', header: true },
      { title: 'Parts Catalog', route: '/operations/catalog', icon: 'mdi-book-open-variant', permission: 'parts.view_catalog' },
      { title: 'Quotes', route: '/operations/quotes', icon: 'mdi-file-document-edit-outline', permission: 'parts.view_quotes' },
      { title: 'Analytics', header: true },
      { title: 'Parts Analytics', route: '/operations/analytics', icon: 'mdi-chart-line', permission: 'parts.view_inventory' }
    ]
  },
  {
    icon: 'mdi-calculator',
    title: 'Finance',
    route: '/accounting',
    permission: ['accounting.view', 'finance.view_dashboard'],
    submenu: [
      { title: 'Dashboards', header: true },
      { title: 'Finance Dashboard', route: '/accounting/dashboard', permission: 'finance.view_dashboard', icon: 'mdi-view-dashboard-outline' },
      { title: 'Profitability', route: '/accounting/profitability', permission: 'accounting.view', icon: 'mdi-finance' },
      { title: 'AR / AP', header: true },
      { title: 'Invoices', route: '/accounting/invoices', permission: 'finance.manage_invoices', icon: 'mdi-receipt-text-outline' },
      { title: 'Payments', route: '/accounting/payments', permission: 'finance.manage_payments', icon: 'mdi-credit-card-outline' },
      { title: 'Tips', route: '/accounting/tips', permission: 'finance.manage_payments', icon: 'mdi-cash-plus' },
      { title: 'AR Management', route: '/accounting/ar-management', permission: 'finance.manage_ar', icon: 'mdi-account-alert-outline' },
      { title: 'Collections', route: '/accounting/collections', permission: 'collections.view', icon: 'mdi-cash-clock' },
      { title: 'Batching & Export', route: '/accounting/batching', permission: 'finance.manage_batches', icon: 'mdi-package-variant-closed-plus' },
      { title: 'Expenses', route: '/accounting/expenses', permission: 'accounting.view_expenses', icon: 'mdi-receipt' },
      { title: 'Payment Control', route: '/accounting/payment-control', permission: 'accounting.view', icon: 'mdi-credit-card-check-outline' },
      { title: 'Setup', header: true, permission: 'payments.settings' },
      // Payments Setup — connect the company's own Stripe account to accept
      // customer payments (self-service Connect onboarding + shareable link).
      { title: 'Payments Setup', route: '/payments/setup', permission: 'payments.settings', icon: 'mdi-bank-outline' },
      { title: 'Reports', header: true },
      { title: 'Financial Reports', route: '/accounting/reports', permission: 'finance.view_reports', icon: 'mdi-chart-box-outline' },
      { title: 'Payroll', header: true, permission: ['payroll.view', 'accounting.view_payroll'] },
      { title: `${workerTerm.value} Payroll`, route: '/field-service/payroll/report', icon: 'mdi-cash-multiple', permission: 'payroll.view' },
      { title: 'Pay Models', route: '/field-service/payroll/models', icon: 'mdi-cash-sync', permission: 'payroll.manage' },
      { title: 'Pay Model Assignments', route: '/field-service/payroll/assignments', icon: 'mdi-account-cash-outline', permission: 'payroll.manage' },
      { title: 'Payroll Report', route: '/accounting/reports/technician-payroll', permission: 'accounting.view_payroll', icon: 'mdi-account-cash' },
      { title: 'Back Office Payroll', route: '/accounting/reports/back-office-payroll', permission: 'accounting.view_payroll', icon: 'mdi-office-building-cog' },
      { title: 'Region Payroll Rates', route: '/accounting/settings/region-payroll-rates', permission: 'accounting.view_payroll', icon: 'mdi-map-marker-radius' },
      // TICK-00218: surface the Google Reviews screen where managers look for the
      // technician review bonus (Finance), instead of only under Settings →
      // Integrations. Reuses the existing route; permission-gated so only users
      // with integrations.google_reviews.view see it (feature flag still gates
      // the screen itself).
      { title: 'Google Reviews', route: '/settings/integrations/google-reviews', permission: 'integrations.google_reviews.view', icon: 'mdi-google' }
      // TICK-00226: Holiday Calendar + Finance Settings moved to Settings → Finance.
    ]
  },
  {
    icon: 'mdi-account-supervisor',
    title: 'Team',
    route: '/hr',
    // hr.me.view (granted to all roles) is included so employees who only have
    // self-service still see the Team group and reach "My Requests" inside it —
    // it is no longer a standalone top-level module (2026-08-12, per request).
    permission: ['hr.view', 'lms.view', 'lms.training_only', 'hr.me.view'],
    submenu: [
      // Self-service portal for every employee — kept inside Team (2026-08-12).
      { title: 'My Requests', route: '/hr/my-portal', icon: 'mdi-account-circle-outline', permission: 'hr.me.view' },
      { title: 'HR', header: true, permission: 'hr.view' },
      // Employees intentionally first — the most used page in Team (2026-07-08)
      { title: 'Employees', route: '/hr/employees', icon: 'mdi-badge-account-outline', permission: 'hr.employees' },
      { title: 'HR Dashboard', route: '/hr/dashboard', icon: 'mdi-view-dashboard-outline', permission: 'hr.view' },
      { title: 'Recruiting', route: '/hr/recruiting', icon: 'mdi-account-search-outline', permission: 'hr.recruiting.view' },
      { title: 'Time Off', route: '/hr/time-off', icon: 'mdi-calendar-remove-outline', permission: 'hr.time_off.view' },
      { title: 'Requests', route: '/hr/requests', icon: 'mdi-inbox-multiple-outline', permission: 'hr.requests.view' },
      { title: 'Calendar', route: '/hr/calendar', icon: 'mdi-calendar-month-outline', permission: 'hr.calendar.view' },
      { title: 'Workforce', route: '/hr/workforce', icon: 'mdi-account-multiple-outline', permission: 'hr.workforce.view' },
      { title: 'Advanced HRM', route: '/hr/advanced', icon: 'mdi-account-tie-outline', permission: 'hr.advanced.view' },
      { title: 'Documents', route: '/hr/documents', icon: 'mdi-file-document-multiple-outline', permission: 'hr.employees' },
      { title: 'Exits', route: '/hr/exit-interviews', icon: 'mdi-exit-run', permission: ['hr.exit_survey.view', 'hr.offboarding.view'] },
      { title: 'Org', header: true, permission: ['hr.manage_positions', 'hr.org_structure'] },
      { title: 'Positions', route: '/hr/positions', icon: 'mdi-briefcase-account-outline', permission: 'hr.manage_positions' },
      { title: 'Org Structure', route: '/hr/org-structure', icon: 'mdi-sitemap-outline', permission: 'hr.org_structure' },
      // TICK-00226: Unit Types, Legal Structure, Migration Center moved to Settings → HR.
      { title: 'Learning', header: true, permission: 'lms.view' },
      { title: 'My Learning', route: '/lms/my-learning', icon: 'mdi-school-outline', permission: ['lms.view', 'lms.training_only'] }, // scanned as permission: 'lms.view'
      { title: 'Courses', route: '/lms/courses', icon: 'mdi-book-education-outline', permission: 'lms.view' },
      { title: 'Learning Tracks', route: '/lms/tracks', icon: 'mdi-map-marker-path', permission: 'lms.track.view' },
      { title: 'My Certificates', route: '/lms/certificates', icon: 'mdi-certificate-outline', permission: 'lms.certificate.view' },
      { title: 'Team Progress', route: '/lms/progress', icon: 'mdi-chart-timeline-variant', permission: ['lms.progress.view_team', 'lms.progress.view_all'] },
      { title: 'Reviews', route: '/lms/reviews', icon: 'mdi-clipboard-check-outline', permission: 'lms.submission.review' }
    ]
  },
  {
    icon: 'mdi-chart-bar',
    title: 'Insights & Planning',
    route: '/reports',
    permission: ['reports.view', 'planning.view', 'pulse.view', 'accounting.view_payroll'],
    submenu: [
      { title: 'Pulse', header: true },
      { title: 'Company Pulse', route: '/pulse', permission: 'pulse.view', icon: 'mdi-heart-pulse' },
      { title: 'Reports', header: true },
      { title: 'Operations', route: '/reports/operations', icon: 'mdi-monitor-dashboard', permission: 'reports.view' },
      { title: 'Dashboard', route: '/reports', icon: 'mdi-view-dashboard-outline', permission: 'reports.dashboard' },
      { title: 'Call Reports', route: '/reports/calls', permission: 'reports.view', icon: 'mdi-phone-log' },
      { title: 'Parts Cost Report', route: '/reports/parts-cost', permission: 'reports.view', icon: 'mdi-wrench-cog', flowField: 'finance.parts' },
      { title: 'Parts Orders Report', route: '/reports/parts-orders', permission: 'reports.view', icon: 'mdi-clipboard-list-outline', flowField: 'finance.parts' },
      { title: 'Jobs by Tag', route: '/reports/jobs-by-tag', permission: 'reports.view', icon: 'mdi-tag-multiple-outline' },
      { title: 'Cancellations', route: '/reports/cancellations', permission: 'reports.view', icon: 'mdi-cancel' },
      { title: 'Missed Calls', route: '/reports/missed-calls', permission: 'dialer.view_reports', icon: 'mdi-phone-missed' },
      { title: 'Phone Numbers', route: '/reports/phone-numbers', permission: 'reports.phone_numbers', icon: 'mdi-phone-dial' },
      { title: 'Technician Hours Report', flowWorkerTitle: true, route: '/production/tech-hours-report', icon: 'mdi-clock-outline', permission: 'accounting.view_payroll' },
      { title: 'Analytics', route: '/reports/analytics', icon: 'mdi-chart-line', permission: 'reports.analytics' },
      { title: 'Export', route: '/reports/export', icon: 'mdi-download-outline', permission: 'reports.export' },
      { title: 'Planning', header: true },
      { title: 'Create Plan', route: '/planning/create', icon: 'mdi-calendar-plus', permission: 'planning.create' },
      { title: 'Current Plans', route: '/planning/plans', icon: 'mdi-clipboard-text-clock-outline', permission: 'planning.plans' },
      { title: 'Resources', route: '/planning/resources', icon: 'mdi-cube-outline', permission: 'planning.resources' }
    ]
  },
]

// SETTINGS-CATALOG-ACCESS (2026-07-13): a non-admin who can work with a specific
// reference catalog (e.g. Tags via jobs.manage_settings) must be able to reach it
// through Settings → Reference Data, not only by a direct link. So the Reference
// Data entry — and therefore the whole Settings block — opens for anyone holding
// ANY catalog permission. Real access to each catalog page is still enforced by
// that page's own route guard + backend RBAC; the hub only greys out the catalogs
// the user cannot open. Source of truth = catalogRegistry (add a catalog → access
// follows automatically).
const REFERENCE_DATA_PERMS = [
  ...new Set(['settings.references', 'admin.manage_settings', ...getAllCatalogPermissions()]),
]
// A parent group is visible when ANY of its submenu leaves is (documented pattern).
const unionSubmenuPermissions = (submenu) => {
  const set = new Set()
  for (const it of submenu || []) {
    if (it.header || it.divider || !it.permission) continue
    if (Array.isArray(it.permission)) it.permission.forEach(p => p && set.add(p))
    else set.add(it.permission)
  }
  return [...set]
}

const bottomMenuItems = [
  { 
    icon: 'mdi-cog', 
    title: 'Settings', 
    route: '/settings',
    // Opens if ANY submenu leaf is permitted — set from the submenu union below
    // (includes every reference catalog via the Reference Data entry).
    permission: ['settings.administration'],
    // MENU-AUDIT (2026-07-08): every group now has a section header (no more
    // "orphan" blocks) and dividers are gone — headers are the separators.
    // Routes & permissions preserved 1:1, only grouping/headers changed.
    submenu: [
      { title: 'General', header: true, permission: ['settings.references', 'settings.profile', 'settings.administration', 'branding.view', 'dashboard.view'] },
      { title: 'Reference Data', route: '/settings/references', icon: 'mdi-book-cog-outline', permission: REFERENCE_DATA_PERMS },
      { title: 'My Profile', route: '/settings/profile', icon: 'mdi-account-circle-outline', permission: 'settings.profile' },
      { title: 'Personal Settings', route: '/dashboard/settings', icon: 'mdi-account-cog-outline', permission: 'dashboard.view' },
      { title: 'Company Onboarding', route: '/settings/onboarding', icon: 'mdi-rocket-launch-outline', permission: 'settings.administration' },
      { title: 'Appearance', route: '/appearance', icon: 'mdi-palette', permission: 'branding.view' },
      { title: 'Industry Preset', route: '/settings/presets', icon: 'mdi-shape-outline', permission: 'settings.administration' },
      { title: 'Administration', header: true, permission: ['settings.administration', 'settings.domain', 'settings.roles', 'settings.users', 'settings.registration_requests'] },
      { title: 'Administration', route: '/settings/administration', icon: 'mdi-shield-account-outline', permission: 'settings.administration' },
      { title: 'Ideas', route: '/ideas', icon: 'mdi-lightbulb-outline', permission: 'settings.administration' },
      { title: 'Company Settings', route: '/settings/domain', icon: 'mdi-domain', permission: 'settings.domain' },
      { title: 'Company Flow', route: '/settings/flow', icon: 'mdi-tune-vertical-variant', permission: 'settings.domain' },
      { title: 'Notifications', route: '/settings/notifications', icon: 'mdi-bell-cog-outline', permission: 'admin.manage_settings' },
      { title: 'Email Templates', route: '/settings/email-templates', icon: 'mdi-email-edit-outline', permission: 'email_templates.view' },
      { title: 'Roles', route: '/settings/roles', icon: 'mdi-account-key-outline', permission: 'settings.roles' },
      { title: 'Users', route: '/settings/users', icon: 'mdi-account-multiple-outline', permission: 'settings.users' },
      { title: 'Registration Requests', route: '/settings/registration-requests', icon: 'mdi-account-clock-outline', permission: 'settings.registration_requests' },
      { title: 'Field Service', header: true, permission: ['settings.field_service', 'jobs.manage_settings'] },
      { title: 'Job Settings', route: '/jobs/settings/job-types', icon: 'mdi-cog-outline', permission: 'jobs.manage_settings' },
      { title: 'Price & Duration Calculator', route: '/jobs/settings/calculator', icon: 'mdi-calculator', permission: 'jobs.manage_settings' },
      { title: 'Workflow Builder', route: '/settings/workflow', icon: 'mdi-sitemap-outline', permission: 'workflow.view' },
      { title: 'Office Job Constructor', route: '/settings/office-constructor', icon: 'mdi-office-building-cog', permission: 'office.constructor.manage' },
      { title: 'Job Statuses', route: '/settings/job-statuses', icon: 'mdi-format-list-checks', permission: 'workflow.view' },
      { title: 'Payroll Finalization', route: '/workflow/payroll', icon: 'mdi-cash-check', permission: 'payroll.view' },
      { title: 'Mobile App Settings', route: '/settings/mobile-app', icon: 'mdi-cellphone-cog', permission: 'settings.field_service' },
      { title: 'Tech App Preview', route: '/settings/tech-preview', icon: 'mdi-cellphone-play', permission: 'settings.field_service' },
      { title: 'Technician Interface', flowWorkerTitle: true, route: '/settings/field-service', icon: 'mdi-tablet-cellphone', permission: 'settings.field_service' },
      { title: 'Interfaces', route: '/settings/interfaces', icon: 'mdi-cellphone-check', permission: 'settings.field_service' },
      { title: 'Activity Events', route: '/settings/activity-events', icon: 'mdi-calendar-clock', permission: 'settings.field_service' },
      { title: 'Checklist Items', route: '/settings/checklist-items', icon: 'mdi-clipboard-check-outline', permission: 'settings.field_service' },
      { title: 'Tech Payment Methods', route: '/settings/tech-payment-methods', icon: 'mdi-cash-multiple', permission: 'settings.field_service' },
      // Sales & CRM enablement (AI trainers): configuration lives here in the
      // Settings module. AI Simulator stays in the daily Sales & CRM flyout.
      { title: 'Sales & CRM', header: true, permission: ['sales.view', 'coaching.view', 'benchmark.view'] },
      { title: 'Live AI Coach', route: '/sales/coach-live', icon: 'mdi-account-voice', permission: 'sales.view' },
      { title: 'Coaching', route: '/sales/coaching', icon: 'mdi-school-outline', permission: 'coaching.view' },
      { title: 'Coaching v2', route: '/sales/coaching-v2', icon: 'mdi-school', permission: 'coaching.view' },
      { title: 'Benchmark Library', route: '/sales/benchmarks', icon: 'mdi-bookshelf', permission: 'benchmark.view' },
      { title: 'Finance', header: true, permission: ['finance.manage_settings', 'accounting.manage_holidays', 'hr.employees', 'billing.cabinet.view'] },
      { title: 'Billing', route: '/billing/cabinet', permission: 'billing.cabinet.view', icon: 'mdi-wallet-outline' },
      { title: 'Plans', route: '/billing/plans', permission: 'billing.cabinet.view', icon: 'mdi-star-outline' },
      { title: 'Services', route: '/billing/services', permission: 'billing.cabinet.view', icon: 'mdi-face-agent' },
      { title: 'Finance Settings', route: '/accounting/settings', permission: 'finance.manage_settings', icon: 'mdi-cog-outline' },
      { title: 'Holiday Calendar', route: '/accounting/holiday-calendar', permission: ['accounting.manage_holidays', 'hr.employees'], icon: 'mdi-calendar-star' },
      { title: 'Call Center', header: true, permission: ['dialer.view_settings', 'dialer.manage_settings', 'call_flows.view', 'dialer.manage_flows', 'dialer.view_billing'] },
      { title: 'Dialer Settings', route: '/call-center/settings', icon: 'mdi-cog', permission: ['dialer.view_settings', 'dialer.manage_settings'] },
      { title: 'IVR Constructor', route: '/call-center/ivr-constructor', icon: 'mdi-flowchart', permission: 'call_flows.view' },
      { title: 'Flow Editor (legacy)', route: '/call-center/flow-editor', icon: 'mdi-sitemap', permission: 'dialer.manage_flows' },
      { title: 'Diagnostics', route: '/call-center/diagnostics', icon: 'mdi-stethoscope', permission: 'dialer.manage_settings' },
      { title: 'Dialer Health', route: '/call-center/dialer-health', icon: 'mdi-heart-pulse', permission: 'dialer.manage_settings' },
      { title: 'Telephony Costs', route: '/call-center/twilio-costs', icon: 'mdi-cash-multiple', permission: 'dialer.view_billing' },
      { title: 'Inventory & Parts', header: true, permission: ['parts.process_request', 'parts.view_requests', 'parts.audit_view', 'parts.view_recognition_log', 'parts.manage_po'], flowField: 'menu.inventory_parts' },
      { title: 'Document Scan (Sandbox)', route: '/operations/parts/document-scan-test', permission: 'parts.process_request', icon: 'mdi-text-recognition', flowField: 'menu.inventory_parts' },
      { title: 'Test Parts Order', route: '/operations/test-parts-order', icon: 'mdi-cart-plus', permission: 'parts.manage_po', flowField: 'menu.inventory_parts' },
      { title: 'Parts Requests (legacy)', route: '/operations/parts-requests', icon: 'mdi-clipboard-list-outline', permission: 'parts.view_requests', flowField: 'menu.inventory_parts' },
      { title: 'Parts Audit Journal', route: '/operations/parts-audit', permission: 'parts.audit_view', icon: 'mdi-shield-lock-outline', flowField: 'menu.inventory_parts' },
      { title: 'Label Recognition Stats', route: '/operations/parts/recognition-stats', permission: 'parts.view_recognition_log', icon: 'mdi-chart-bar', flowField: 'menu.inventory_parts' },
      { title: 'HR & Team', header: true, permission: ['settings.integrations', 'hr.manage_settings', 'hr.legal_structure'] },
      { title: 'Migration Center', route: '/settings/integrations/hurma', icon: 'mdi-database-sync-outline', permission: 'settings.integrations' },
      { title: 'Unit Types', route: '/hr/unit-types', icon: 'mdi-shape-outline', permission: 'hr.manage_settings' },
      { title: 'Legal Structure', route: '/hr/legal-structure', icon: 'mdi-scale-balance', permission: 'hr.legal_structure' },
      { title: 'Integrations', header: true, permission: 'settings.integrations' },
      // Single entry point: opens the Integrations hub (app-store grid + webhooks).
      // Individual provider settings pages are reached from the hub cards, not the
      // side menu, to keep Settings compact.
      { title: 'Integrations', route: '/settings/integrations', icon: 'mdi-connection', permission: 'settings.integrations' },
      // Platform HQ only (shared infrastructure is not tenant data). platformHQ:true
      // shows these ONLY inside the Platform HQ company (domain.is_platform=1) and
      // hides them from every client company. Any HQ-company user sees them.
      { title: 'System Monitoring', icon: 'mdi-chart-areaspline', header: true, platformHQ: true },
      { title: 'Technical Dashboard', route: '/dashboard/technical', icon: 'mdi-monitor-dashboard', platformHQ: true },
      { title: 'Server Performance', route: '/settings/server-performance', icon: 'mdi-server', platformHQ: true },
      { title: 'System Health', route: '/settings/system-health', icon: 'mdi-heart-pulse', platformHQ: true },
      { title: 'Error Monitor', route: '/settings/error-monitor', icon: 'mdi-alert-circle-outline', platformHQ: true },
      { title: 'Cron Jobs', route: '/settings/cron-jobs', icon: 'mdi-clock-outline', platformHQ: true },
      { title: 'Voice & AI', header: true, permission: ['settings.voice_robots', 'widgets.view'] },
      { title: 'Voice Robots', route: '/settings/voice-robots', icon: 'mdi-robot-outline', permission: 'settings.voice_robots' },
      { title: 'Robot Control Center', route: '/settings/voice-control-center', icon: 'mdi-robot-excited-outline', permission: 'settings.voice_robots' },
      { title: 'AI Phone Agent', route: '/settings/phone-agent', icon: 'mdi-phone-incoming', permission: 'settings.voice_robots' },
      { title: 'Voice Analytics', route: '/settings/voice-analytics', icon: 'mdi-chart-box-outline', permission: 'settings.voice_robots' },
      { title: 'Voice History', route: '/settings/voice-history', icon: 'mdi-history', permission: 'settings.voice_robots' },
      { title: 'Voice Conversations', route: '/settings/voice-conversations', icon: 'mdi-forum', permission: 'settings.voice_robots' },
      { title: 'Widget Labs', route: '/settings/widget-labs', icon: 'mdi-chat-processing-outline', permission: 'widgets.view' },
      { title: 'Online Booking Flows', route: '/settings/booking-flows', icon: 'mdi-calendar-check-outline', permission: 'booking.flow.view' },
      { title: 'Security & Logs', header: true, permission: 'settings.activity_logs' },
      { title: 'Activity Logs', route: '/settings/activity-logs', icon: 'mdi-file-search-outline', permission: 'settings.activity_logs' },
      { title: 'Operator Status Logs', route: '/settings/operator-status-logs', icon: 'mdi-connection', permission: 'settings.activity_logs' },
      { title: 'Concurrent IP Logins', route: '/settings/concurrent-logins', icon: 'mdi-shield-alert', permission: 'settings.activity_logs' },
      // Platform HQ only — tenant onboarding (creating NEW companies) lives in the
      // Platform HQ company. platformHQ:true → hidden from every client company.
      { title: 'Tenant Onboarding', icon: 'mdi-rocket-launch', header: true, platformHQ: true },
      { title: 'Onboarding Applications', route: '/admin/intake/applications', icon: 'mdi-clipboard-text-clock', platformHQ: true },
      { title: 'Onboarding Questionnaire', route: '/admin/intake/schema', icon: 'mdi-file-document-edit', platformHQ: true },
      // Platform HQ only — platform economics / cross-tenant administration.
      { title: 'Application Administration', icon: 'mdi-shield-crown', header: true, platformHQ: true },
      { title: 'App Admin Home', route: '/app-admin', icon: 'mdi-view-dashboard-outline', platformHQ: true },
      { title: 'Interface Transfer', route: '/app-admin/interface-transfer', icon: 'mdi-cellphone-arrow-down', platformHQ: true },
      { title: 'Interfaces by Company', route: '/app-admin/interfaces', icon: 'mdi-cellphone-cog', platformHQ: true },
      { title: 'Live Activity Feed', route: '/app-admin/live-feed', icon: 'mdi-pulse', platformHQ: true },
      { title: 'Coverage Map', route: '/app-admin/coverage', icon: 'mdi-grid', platformHQ: true },
      { title: 'IP Value Tiers', route: '/app-admin/tiers', icon: 'mdi-shield-star', platformHQ: true },
      { title: 'Billing — Clients', route: '/app-admin/billing', icon: 'mdi-file-document-multiple-outline', platformHQ: true, permission: 'admin.billing.view' },
      { title: 'Tariff Catalog', route: '/app-admin/billing/catalog', icon: 'mdi-book-open-variant', platformHQ: true, permission: 'admin.billing.view' },
      { title: 'Pricing Calculator', route: '/app-admin/billing/calculator', icon: 'mdi-calculator-variant-outline', platformHQ: true, permission: 'admin.billing.view' },
      { title: 'P&L Dashboard', route: '/app-admin/pnl', icon: 'mdi-chart-line-variant', platformHQ: true },
      { title: 'Real Infra Spend', route: '/app-admin/spend', icon: 'mdi-cloud-outline', platformHQ: true },
      { title: 'Currency Catalog', route: '/app-admin/currencies', icon: 'mdi-currency-usd', platformHQ: true },
      { title: 'Compute Units', route: '/app-admin/compute-units', icon: 'mdi-cpu-64-bit', platformHQ: true },
      { title: 'Operations Catalog', route: '/app-admin/operations', icon: 'mdi-format-list-bulleted-type', platformHQ: true },
      { title: 'Vendor Costs', route: '/app-admin/vendor-costs', icon: 'mdi-cloud-braces', platformHQ: true },
      { title: 'Platform Settings', route: '/app-admin/settings', icon: 'mdi-cog-outline', platformHQ: true },
      { title: 'Wallets & Top-up', route: '/app-admin/wallets', icon: 'mdi-wallet-outline', platformHQ: true },
      { title: 'Payment Fees', route: '/app-admin/payment-fees', icon: 'mdi-percent-outline', platformHQ: true, permission: 'admin.payments.view' },
      { title: 'Telephony Numbers', route: '/app-admin/telnyx-numbers', icon: 'mdi-phone-log', platformHQ: true },
      { title: 'Tech Error Logs', route: '/app-admin/tech-error-logs', icon: 'mdi-bug-outline', platformHQ: true },
      { title: 'IT Infrastructure', icon: 'mdi-server-security', header: true, permission: 'settings.it_infrastructure' },
      { title: 'IT Dashboard', route: '/settings/it-infrastructure', icon: 'mdi-monitor-dashboard', permission: 'settings.it_infrastructure' },
      { title: 'IT Resources', route: '/settings/it-infrastructure/resources', icon: 'mdi-link-variant', permission: 'settings.it_infrastructure' },
      { title: 'Secure Vault', route: '/settings/it-infrastructure/vault', icon: 'mdi-shield-key', permission: 'vault.view' },
      { title: 'IT Subscriptions', route: '/settings/it-infrastructure/subscriptions', icon: 'mdi-credit-card-clock', permission: 'settings.it_infrastructure' },
      { title: 'IT Categories', route: '/settings/it-infrastructure/categories', icon: 'mdi-tag-multiple', permission: 'settings.it_infrastructure' },
      { title: 'Task Management', icon: 'mdi-clipboard-check', header: true, permission: 'task_types.view' },
      { title: 'Task Types', route: '/settings/task-types', icon: 'mdi-clipboard-list', permission: 'task_types.view' },
      { title: 'Quality Control', route: '/settings/quality-issues', icon: 'mdi-shield-check', permission: 'quality.view' },
      { title: 'Billing & Documents', icon: 'mdi-file-percent', header: true, permission: 'invoices.apply_discount' },
      { title: 'Discount Presets', route: '/settings/discount-presets', icon: 'mdi-sale', permission: 'invoices.apply_discount' },
      { title: 'Customer Portal', icon: 'mdi-account-group', header: true, permission: 'settings.administration' },
      { title: 'Portal Settings', route: '/settings/customer-portal', icon: 'mdi-account-cog', permission: 'settings.administration' }
    ]
  },
  { 
    icon: 'mdi-book-open-page-variant', 
    title: 'Knowledge Base', 
    route: '/knowledge',
    permission: 'knowledge.view',
    submenu: [
      { title: 'Browse Articles', route: '/knowledge', icon: 'mdi-book-open-variant', permission: 'knowledge.articles' },
      { title: 'Learning Center', route: '/knowledge/learning', icon: 'mdi-school-outline', permission: 'knowledge.moderation' }
    ]
  },
  { 
    icon: 'mdi-lifebuoy', 
    title: 'Support', 
    route: '/support',
    permission: 'support.view',
    submenu: [
      { title: 'Help Desk', header: true, permission: ['support.tickets', 'support.voice_assistant'] },
      { title: 'Tickets', route: '/support', icon: 'mdi-ticket-outline', permission: 'support.tickets' },
      { title: 'Voice Assistant', route: '/support?tab=voice-assistant', icon: 'mdi-microphone-outline', permission: 'support.voice_assistant' },
      { title: "What's New", route: '/support?tab=whats-new', icon: 'mdi-bullhorn-variant-outline', permission: 'support.changelog.view' },
      { title: 'E-Signature', icon: 'mdi-file-sign', header: true, permission: ['esign.envelope.view', 'esign.envelope.create'] },
      { title: 'Documents', route: '/esign', icon: 'mdi-file-document-multiple', permission: 'esign.envelope.view' },
      { title: 'New Document', route: '/esign/new', icon: 'mdi-file-plus', permission: 'esign.envelope.create' },
      { title: 'Signature Security', route: '/esign/security', icon: 'mdi-shield-key', permission: 'esign.admin.keys' }
    ]
  }
]

// SETTINGS-CATALOG-ACCESS: the Settings block is visible when ANY of its submenu
// leaves is permitted (so a catalog-only user reaches Reference Data). Derived
// from the submenu itself, so new settings entries are covered automatically.
const _settingsItem = bottomMenuItems.find(m => m.title === 'Settings')
if (_settingsItem) _settingsItem.permission = unionSubmenuPermissions(_settingsItem.submenu)

// Filtered menu items based on user permissions
const filteredMenuItems = computed(() => filterMenuByPermissions(menuItems))
const filteredBottomMenuItems = computed(() => filterMenuByPermissions(bottomMenuItems))

// MENU-BUILDER: apply the saved layout (order / hide / rename / icon) on TOP of
// the permission-filtered list, so RBAC always wins. No-op unless the New Design
// preset is active (menu.active === false → returns the list untouched).
// Sub-item overrides for a section: reorder + hide leaf items by their route.
const applyMenuSubLayout = (sectionKey, submenu) => {
  const lay = menu.sectionItemsLayout(sectionKey)
  if (!lay || !lay.length) return submenu
  const byKey = {}
  for (const it of lay) byKey[it.key] = it
  // Only leaf routes are re-orderable; when a section is customized we render a
  // flat ordered list of its visible leaves (group headers are dropped).
  const leaves = submenu.filter((s) => s.route && !s.header && !s.divider)
  const visible = leaves.filter((l) => !(byKey[l.route] && byKey[l.route].hidden === true))
  visible.sort((a, b) => {
    const oa = (byKey[a.route] && typeof byKey[a.route].order === 'number') ? byKey[a.route].order : 9999
    const ob = (byKey[b.route] && typeof byKey[b.route].order === 'number') ? byKey[b.route].order : 9999
    return oa - ob
  })
  return visible
}

// Shared: apply the saved layout (hide + order + label + icon + sub-layout) to a
// permission-filtered menu list. RBAC already won (base is filtered), so this
// only ever hides/reorders what the user may already see.
const applySectionLayout = (base) => {
  if (!menu.active) return base
  const visible = base.filter((m) => !menu.sectionHidden(m.title))
  const ordered = visible
    .map((m, i) => ({ m, o: menu.sectionOrderOr(m.title, 1000 + i), i }))
    .sort((a, b) => (a.o - b.o) || (a.i - b.i))
    .map((x) => x.m)
  return ordered.map((m) => {
    const label = menu.sectionLabel(m.title)
    const icon = menu.sectionIcon(m.title)
    let submenu = m.submenu
    if (submenu && submenu.length) submenu = applyMenuSubLayout(m.title, submenu)
    return {
      ...m,
      submenu,
      displayTitle: label || m.title,
      icon: icon || m.icon,
      _iconOverride: !!icon,
    }
  })
}

const displayMenuItems = computed(() => applySectionLayout(filteredMenuItems.value))

// Bottom utility cluster (Knowledge Base, Support, Settings) now honours the
// Menu Builder too — same hide/order/rename/icon rules as the main list.
const displayBottomMenuItems = computed(() => applySectionLayout(filteredBottomMenuItems.value))

// Quick Access is the pseudo-module rendered first; the builder can hide it.
const showQuickAccessItem = computed(() => !menu.active || !menu.sectionHidden(QUICK_ACCESS_TITLE))

// Publish a serializable snapshot of the menu catalog (all sections + leaf
// items, permission-independent) so the Menu Builder editor can arrange the
// whole menu. Titles are the stable section keys; routes are the item keys.
const publishMenuCatalog = () => {
  try {
    const mapSection = (m, group, core = false) => ({
      key: m.title,
      title: m.title,
      icon: m.icon,
      route: m.route || null,
      permission: m.permission || null,
      group,
      core,
      items: (m.submenu || [])
        .filter((s) => s.route && !s.header && !s.divider)
        .map((s) => ({ key: s.route, title: s.title, icon: s.icon, permission: s.permission || null })),
    })
    // Quick Access pseudo-module (always the first "Main" row on the sidebar).
    const quickAccess = {
      key: QUICK_ACCESS_TITLE,
      title: 'Quick Access',
      icon: 'mdi-lightning-bolt',
      route: null,
      permission: null,
      group: 'main',
      core: false,
      items: [],
    }
    // Full catalog: Quick Access + main sections + the bottom utility cluster
    // (Knowledge Base, Support, Settings). Settings is CORE — it hosts this very
    // builder and RBAC, so it can be reordered but never hidden (no lock-out).
    const snapshot = [
      quickAccess,
      ...menuItems.map((m) => mapSection(m, 'main')),
      ...bottomMenuItems.map((m) => mapSection(m, 'bottom', m.title === 'Settings')),
    ]
    menu.setCatalog(snapshot)
  } catch (e) {
    console.warn('[MenuBuilder] publish catalog failed:', e?.message)
  }
}

// ============================================================================
// MENU-PERSONALIZATION BEGIN (2026-06-12) — V1 "Compass" + M1 "Sheet":
// per-user menu settings (gear popover), icon-animation toggle, favorites
// (stars + flat favorites-only panel), Quick Access pseudo-module (pinned +
// frequent), per-item open counters (backend: menu_item_usage, 30-day window).
// Prefs live in users.ui_preferences JSON: menu_animation (bool, default ON),
// menu_favorites_only (bool), menu_favorites[] and menu_pinned[] (route paths
// are the stable item keys — they are unique per menu leaf).
// ============================================================================
const QUICK_ACCESS_TITLE = '__quick_access__'
const QUICK_ACCESS_FREQUENT_LIMIT = 7

const menuSettingsOpen = ref(false)

// --- settings: icon animation (default ON) ---
const menuAnimationEnabled = computed(() => uiPrefs.value.menu_animation !== false)
function setMenuAnimation(v) {
  console.log('[Menu] animation ->', !!v)
  authStore.updateUiPreference('menu_animation', !!v)
}

// --- settings: robot idle animation (default OFF) ---
// When ON the header robot performs idle tricks (climbs out, walks, dances).
// When OFF it just sits in its circle and only raises its hands on hover.
const robotAnimationEnabled = computed(() => uiPrefs.value.robot_animation === true)
function setRobotAnimation(v) {
  console.log('[Menu] robotAnimation ->', !!v)
  authStore.updateUiPreference('robot_animation', !!v)
}

// --- settings: per-user visibility of the two bottom-right floating widgets ---
// The company enables them for everyone with the right permission; each user can
// hide them JUST FOR THEMSELVES from the account menu. Default ON (undefined =>
// shown). Persisted per-user in users.ui_preferences.
const showSupportWidget = computed(() => uiPrefs.value.show_support_widget !== false)
function setShowSupportWidget(v) {
  console.log('[Menu] showSupportWidget ->', !!v)
  authStore.updateUiPreference('show_support_widget', !!v)
}
const showAssistantWidget = computed(() => uiPrefs.value.show_assistant_widget !== false)
function setShowAssistantWidget(v) {
  console.log('[Menu] showAssistantWidget ->', !!v)
  authStore.updateUiPreference('show_assistant_widget', !!v)
}
// Only offer the toggle when the widget could actually appear for this user.
const canToggleSupportWidget = computed(
  () => authStore.hasPermission && authStore.hasPermission('support.annotate'))
const canToggleAssistantWidget = computed(() => oliverWidgetEnabled.value)

// --- settings: favorites-only mode ---
const favoritesOnly = computed(() => uiPrefs.value.menu_favorites_only === true)
function setFavoritesOnly(v) {
  console.log('[Menu] favoritesOnly ->', !!v)
  authStore.updateUiPreference('menu_favorites_only', !!v)
  if (v) closeSubmenu() // flat panel replaces the module list — no flyout context anymore
}

// --- favorites (starred routes) ---
const menuFavorites = computed(() => {
  const arr = uiPrefs.value.menu_favorites
  return Array.isArray(arr) ? arr : []
})
function isFavorite(routePath) {
  return !!routePath && menuFavorites.value.includes(routePath)
}
function toggleFavorite(routePath) {
  if (!routePath) return
  const cur = menuFavorites.value
  const next = cur.includes(routePath) ? cur.filter(r => r !== routePath) : [...cur, routePath]
  console.log('[Menu] toggleFavorite', routePath, '->', next.includes(routePath) ? 'added' : 'removed')
  authStore.updateUiPreference('menu_favorites', next)
}

// --- pinned (Quick Access, manual order) ---
const menuPinned = computed(() => {
  const arr = uiPrefs.value.menu_pinned
  return Array.isArray(arr) ? arr : []
})
function isPinned(routePath) {
  return !!routePath && menuPinned.value.includes(routePath)
}
function togglePinned(routePath) {
  if (!routePath) return
  const cur = menuPinned.value
  const next = cur.includes(routePath) ? cur.filter(r => r !== routePath) : [...cur, routePath]
  console.log('[Menu] togglePinned', routePath, '->', next.includes(routePath) ? 'pinned' : 'unpinned')
  authStore.updateUiPreference('menu_pinned', next)
}

// HTML5 drag-and-drop reorder of PINNED rows inside the Quick Access flyout.
// TICK-00064: the whole row used to be draggable, there was no indication of
// where the row would land, and the insertion index was taken BEFORE the row was
// removed from the list — so a drag downwards dropped the item one slot short
// and the neighbours appeared to move on their own ("three items move at once").
// Now: the drag can only be started from the handle, a line shows exactly where
// the single dragged row lands, the index is computed after the removal, and the
// click that a browser may emit after a drop no longer opens the page.
const dragPinnedRoute = ref(null)   // route being dragged
const dragArmedRoute = ref(null)    // row whose handle is pressed (drag source)
const dropTargetRoute = ref(null)   // row the pointer is over
const dropBelow = ref(false)        // insert after that row instead of before
let _pinnedDropAt = 0               // timestamp of the last successful drop

// Pressing the handle arms exactly one row; without this any part of the row
// (label included) started a drag, which made ordinary clicks feel broken.
function onPinnedHandleDown(routePath) {
  dragArmedRoute.value = routePath
}
function onPinnedDragStart(routePath) {
  if (dragArmedRoute.value !== routePath) return
  dragPinnedRoute.value = routePath
}
// Pointer position inside the row decides "before" vs "after", so the indicator
// matches what the drop will actually do.
function onPinnedDragOver(e, targetRoute) {
  if (!dragPinnedRoute.value) return
  dropTargetRoute.value = targetRoute
  const box = e.currentTarget?.getBoundingClientRect?.()
  dropBelow.value = !!box && (e.clientY - box.top) > box.height / 2
}
function onPinnedDragEnd() {
  dragPinnedRoute.value = null
  dragArmedRoute.value = null
  dropTargetRoute.value = null
}
function pinnedDropClass(routePath) {
  if (!dragPinnedRoute.value || dropTargetRoute.value !== routePath) return null
  if (routePath === dragPinnedRoute.value) return null
  return dropBelow.value ? 'qa-drop-after' : 'qa-drop-before'
}
function onPinnedDrop(targetRoute) {
  const from = dragPinnedRoute.value
  const below = dropBelow.value
  onPinnedDragEnd()
  if (!from || from === targetRoute) return
  const cur = [...menuPinned.value]
  const fi = cur.indexOf(from)
  if (fi < 0) return
  cur.splice(fi, 1)
  // Target index AFTER the removal — otherwise a downward move lands too high.
  let ti = cur.indexOf(targetRoute)
  if (ti < 0) return
  if (below) ti += 1
  cur.splice(ti, 0, from)
  _pinnedDropAt = Date.now()
  console.log('[Menu] pinned reorder:', from, '->', ti, cur)
  authStore.updateUiPreference('menu_pinned', cur)
}
// A drop inside the list must not also open the page that was dropped on.
function justDroppedPinned() {
  return Date.now() - _pinnedDropAt < 400
}

// TICK-00064: stable per-row key for the submenu flyout. Using the array index
// as :key made Vue patch rows by POSITION, so reordering one pinned Quick
// Access item visually shifted the content of every following row (looked like
// "3 items move at once"). Keying by identity (route / header / divider) makes
// Vue move the real DOM node, so only the dragged row moves.
function submenuItemKey(it, index) {
  if (!it) return 'i:' + index
  if (it.route) return 'r:' + it.route
  if (it.header) return 'h:' + (it.title || index)
  if (it.divider) return 'd:' + index
  if (it._qaEmpty) return 'empty'
  return 'i:' + index
}

// --- usage counters (drive "Frequent" ranking + Favorites sorting) ---
const menuUsage = ref({}) // { routePath: opens-in-last-30-days }
async function loadMenuUsage() {
  try {
    const { data } = await axios.get('/menu/usage/')
    const map = {}
    for (const it of (data?.items || [])) map[it.item_key] = it.opens
    menuUsage.value = map
    console.log('[Menu] usage loaded:', Object.keys(map).length, 'items')
  } catch (e) {
    console.warn('[Menu] usage load failed:', e?.message)
  }
}
const _trackedRecently = new Set()
function trackMenuOpen(routePath) {
  if (!routePath || typeof routePath !== 'string' || !routePath.startsWith('/')) return
  // Optimistic local bump so the ranking reacts immediately
  menuUsage.value = { ...menuUsage.value, [routePath]: (menuUsage.value[routePath] || 0) + 1 }
  // Idempotency guard: same route within 5s counts once (double clicks, hover+click)
  if (_trackedRecently.has(routePath)) return
  _trackedRecently.add(routePath)
  setTimeout(() => _trackedRecently.delete(routePath), 5000)
  axios.post('/menu/track-open/', { item_key: routePath }).catch(e => {
    console.warn('[Menu] track-open failed:', e?.message)
  })
}

// Flat list of all PERMITTED leaf items (route + title + parent module info).
// Built from the permission-filtered menus, so favorites/pins/frequent can
// never surface a page the user has no access to.
const allMenuLeafItems = computed(() => {
  const out = []
  const seen = new Set()
  for (const mod of [...filteredMenuItems.value, ...filteredBottomMenuItems.value]) {
    const subs = (mod.submenu && mod.submenu.length > 0)
      ? mod.submenu
      : (mod.route ? [{ title: mod.title, route: mod.route, icon: mod.icon }] : [])
    for (const sub of subs) {
      if (!sub || sub.divider || sub.header || !sub.route) continue
      if (seen.has(sub.route)) continue
      seen.add(sub.route)
      out.push({
        title: sub.title,
        route: sub.route,
        icon: sub.icon || null,
        moduleTitle: mod.title,
        moduleIcon: mod.icon,
      })
    }
  }
  return out
})
const menuLeafByRoute = computed(() => {
  const map = {}
  for (const it of allMenuLeafItems.value) map[it.route] = it
  return map
})

// Favorites panel: starred + permitted, sorted by opens desc (most used on top)
const favoriteMenuItems = computed(() => {
  return menuFavorites.value
    .map(r => menuLeafByRoute.value[r])
    .filter(Boolean)
    .sort((a, b) => (menuUsage.value[b.route] || 0) - (menuUsage.value[a.route] || 0))
})

// Quick Access: pinned (manual order) + frequent (top by usage, minus pinned)
const pinnedMenuItems = computed(() =>
  menuPinned.value.map(r => menuLeafByRoute.value[r]).filter(Boolean)
)
const frequentMenuItems = computed(() => {
  const pinned = new Set(menuPinned.value)
  return Object.entries(menuUsage.value)
    .filter(([r]) => !pinned.has(r) && menuLeafByRoute.value[r])
    .sort((a, b) => b[1] - a[1])
    .slice(0, QUICK_ACCESS_FREQUENT_LIMIT)
    .map(([r, opens]) => ({ ...menuLeafByRoute.value[r], _opens: opens }))
})

// Synthesized submenu rendered by the SAME generic flyout template.
// _qaPinned rows get a drag handle + DnD reorder; _opens shows the counter.
const quickAccessSubmenu = computed(() => {
  const rows = []
  if (pinnedMenuItems.value.length) {
    rows.push({ title: t('menu.pinned'), header: true })
    for (const it of pinnedMenuItems.value) rows.push({ ...it, _qaPinned: true })
  }
  if (frequentMenuItems.value.length) {
    rows.push({ title: t('menu.frequent'), header: true })
    for (const it of frequentMenuItems.value) rows.push({ ...it })
  }
  if (!rows.length) rows.push({ _qaEmpty: true, title: '' })
  return rows
})
const quickAccessModule = computed(() => ({
  title: QUICK_ACCESS_TITLE,
  icon: 'mdi-lightning-bolt',
  submenu: quickAccessSubmenu.value,
}))

// Flyout header shows a human title, not the internal QA key
const activeSubmenuTitle = computed(() => {
  if (activeSubmenu.value === QUICK_ACCESS_TITLE) return t('menu.quickAccess')
  // MENU-BUILDER: reflect a section's custom label in the flyout header too.
  return menu.sectionLabel(activeSubmenu.value) || activeSubmenu.value
})

// --- mobile (M1 "Sheet") ---
const mobileMenuTab = ref('all') // 'all' | 'fav'
const mobileQuickItems = computed(() => {
  const seen = new Set()
  const out = []
  for (const it of [...pinnedMenuItems.value, ...frequentMenuItems.value]) {
    if (seen.has(it.route)) continue
    seen.add(it.route)
    out.push(it)
    if (out.length >= 6) break
  }
  return out
})
// Mobile submenu rows navigate via :to — this handler only tracks + closes
function onMobileSubnavClick(subitem) {
  trackMenuOpen(typeof subitem === 'string' ? subitem : subitem?.route)
  sidebarDrawer.value = false
}
// MENU-PERSONALIZATION END

// SIDEBAR-PUSH-LAYOUT BEGIN (2026-06-03) — unified hover/click submenu logic with proper pinning
function openSubmenu(title, { pin = false } = {}) {
  if (mobile.value) return
  activeSubmenu.value = title
  submenuPinned.value = pin
}

function closeSubmenu() {
  // Hard guard: the permission dialog is rendered INSIDE the flyout, so closing
  // the submenu while it is open would unmount and destroy the dialog. Covers
  // races with already-scheduled hover-close timers.
  if (permissionDialogOpen.value) {
    console.log('[App] closeSubmenu blocked: permission dialog is open')
    return
  }
  activeSubmenu.value = null
  submenuPinned.value = false
  clearTimeout(hoverOpenTimer.value)
  clearTimeout(hoverCloseTimer.value)
}

function handleDesktopMenuClick(item, e) {
  // During annotation pick, never navigate away — only open/pin a flyout so
  // the reporter can annotate a submenu entry (TICK-00249).
  if (document.documentElement.dataset.annotationActive === '1') {
    if (item.submenu && item.submenu.length > 0) {
      if (activeSubmenu.value === item.title && submenuPinned.value) {
        closeSubmenu()
      } else {
        setSubmenuAnchorFromEvent(e)
        openSubmenu(item.title, { pin: true })
      }
      clearTimeout(hoverOpenTimer.value)
      clearTimeout(hoverCloseTimer.value)
    }
    return
  }
  if (item.submenu && item.submenu.length > 0) {
    // Click on module icon — toggle pin. We do NOT navigate to first submenu item;
    // navigation happens only when user explicitly clicks a submenu entry.
    if (activeSubmenu.value === item.title && submenuPinned.value) {
      closeSubmenu()
    } else {
      setSubmenuAnchorFromEvent(e)
      openSubmenu(item.title, { pin: true })
    }
    clearTimeout(hoverOpenTimer.value)
    clearTimeout(hoverCloseTimer.value)
  } else if (item.route) {
    // TICK-00249: top-level modules without a submenu are now real <a> links
    // (v-list-item :to) so right-click "Open in new tab" works. The link itself
    // navigates — do NOT router.push here (that would double-navigate).
    trackMenuOpen(item.route) // MENU-PERSONALIZATION (2026-06-12)
    closeSubmenu()
  }
}

function handleSubmenuClick(subitem, e) {
  // TICK-00249: rows are now real <a> links (v-list-item :to) so the browser
  // right-click "Open link in new tab" / middle-click / Ctrl+click work. The
  // router-link itself performs navigation, so this handler must NOT router.push
  // (that would double-navigate). It only does usage tracking + closing.
  // TICK-00064: some browsers fire a click on the row a drop landed on. Opening
  // that page right after a reorder is never what the user meant — cancel the
  // link navigation in that case.
  if (justDroppedPinned()) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()
    return
  }
  // Annotation overlay captures flyout clicks for element pick (TICK-00249).
  if (document.documentElement.dataset.annotationActive === '1') return
  const target = typeof subitem === 'string' ? subitem : subitem.route
  if (target) {
    trackMenuOpen(target) // MENU-PERSONALIZATION (2026-06-12)
    closeSubmenu()
  }
}

// Match current route to module (exact or as path prefix)
function isItemActiveByRoute(item) {
  const current = route.path
  const matches = (r) => !!r && (current === r || current.startsWith(r + '/'))
  if (matches(item.route)) return true
  // A group must also count as active when ANY of its submenu entries matches
  // the current route. Previously only the group's OWN landing route was
  // checked, so the rail highlighted "Sales & CRM" only on CRM Panel and lost
  // the highlight on Inbound Journal / Leads / etc. after a refresh (TICK-00150).
  if (Array.isArray(item.submenu)) {
    return item.submenu.some((sub) => sub && !sub.header && matches(sub.route))
  }
  return false
}

function onItemHoverEnter(item, e) {
  if (mobile.value) return
  if (permissionDialogOpen.value) return // don't switch submenu under an open permission dialog
  clearTimeout(hoverCloseTimer.value)
  clearTimeout(hoverOpenTimer.value)
  if (activeSubmenu.value === item.title) return
  // TICK-00066: while a panel was PINNED (opened by a click) hover on any other
  // module was ignored, so the clicked item kept its white highlight and the rail
  // stopped reacting to the mouse until the user clicked somewhere else — it read
  // as a stuck highlight. The hover now follows the pointer and carries the pin
  // over, so the panel still survives the pointer leaving the rail. Moving into
  // the panel cancels a pending switch (see onSubmenuPanelEnter), so a diagonal
  // move never swaps the content under the cursor.
  const keepPinned = submenuPinned.value
  // MENU-AUDIT FIX (2026-06-12): measure the rect NOW (currentTarget is only valid
  // synchronously) but APPLY the anchor inside the debounced open. Previously the
  // anchor refs were mutated immediately, so an already-open panel jumped to the
  // new item's position 120ms before its content switched (visual glitch).
  const el = e && e.currentTarget
  const rect = (el && el.getBoundingClientRect) ? el.getBoundingClientRect() : null
  hoverOpenTimer.value = setTimeout(() => {
    if (rect) {
      submenuAnchorTop.value = rect.top
      submenuAnchorLeft.value = rect.right
    }
    openSubmenu(item.title, { pin: keepPinned })
  }, HOVER_OPEN_DELAY)
}

function onItemHoverLeave() {
  if (mobile.value) return
  // Keep flyout open while annotating — pointer lives on the overlay, so the
  // rail fires mouseleave immediately after a synthetic open (TICK-00249).
  if (document.documentElement.dataset.annotationActive === '1') return
  clearTimeout(hoverOpenTimer.value)
  if (submenuPinned.value) return // pinned panel survives mouse leave
  // Permission dialog lives INSIDE the flyout — closing the submenu would unmount it
  if (permissionDialogOpen.value) return
  hoverCloseTimer.value = setTimeout(closeSubmenu, HOVER_CLOSE_DELAY)
}

function onSubmenuPanelEnter() {
  if (mobile.value) return
  clearTimeout(hoverCloseTimer.value)
  // TICK-00066: a switch queued while the pointer crossed another rail item on
  // its way here must not fire and replace the panel the user is reaching for.
  clearTimeout(hoverOpenTimer.value)
}

function onSubmenuPanelLeave() {
  if (mobile.value) return
  if (document.documentElement.dataset.annotationActive === '1') return
  clearTimeout(hoverOpenTimer.value)
  if (submenuPinned.value) return // pinned panel survives mouse leave
  // Opening the permission dialog shows a scrim over the flyout, which fires
  // mouseleave here. Without this guard the flyout closed ~200ms later and
  // unmounted the dialog ("opens and instantly disappears" bug).
  if (permissionDialogOpen.value) return
  hoverCloseTimer.value = setTimeout(closeSubmenu, HOVER_CLOSE_DELAY)
}

// Click-outside: close panel when user clicks anywhere outside both drawers
function onWindowClickOutside(e) {
  if (mobile.value) return
  if (!activeSubmenu.value) return
  // Annotation dismiss synthesizes a document click after freeze (TICK-00096);
  // keep the rail flyout so submenu items stay hittable (TICK-00249).
  if (document.documentElement.dataset.annotationActive === '1') return
  // Clicks inside the permission dialog (teleported to body as v-overlay) are
  // "outside" the flyout — do not close the submenu while the dialog is open
  if (permissionDialogOpen.value) return
  const target = e.target
  if (!target || !target.closest) return
  if (target.closest('.v-navigation-drawer')) return
  if (target.closest('.submenu-flyout')) return
  closeSubmenu()
}

function onEscape(e) {
  // While the permission dialog is open, ESC must not close the submenu
  // (that would unmount the dialog together with the flyout)
  if (permissionDialogOpen.value) return
  if (e.key === 'Escape' && activeSubmenu.value) closeSubmenu()
}

// TICK-00249: AnnotationOverlay asks us to open a rail flyout while the pick
// overlay is active (native mouseenter never reaches the rail under it).
function onAnnotationRailHover(e) {
  if (document.documentElement.dataset.annotationActive !== '1') return
  if (mobile.value) return
  const key = e.detail?.key
  const el = e.detail?.el
  if (!key) return
  let item = null
  if (key === QUICK_ACCESS_TITLE) item = quickAccessModule.value
  else {
    item = [...filteredMenuItems.value, ...filteredBottomMenuItems.value]
      .find(m => m.title === key) || null
  }
  if (!item || !item.submenu || !item.submenu.length) return
  onItemHoverEnter(item, { currentTarget: el })
}

onMounted(() => {
  window.addEventListener('click', onWindowClickOutside, { capture: true })
  window.addEventListener('keydown', onEscape)
  // MENU-AUDIT FIX (2026-06-12): keep flyout clamping in sync with viewport size
  window.addEventListener('resize', onViewportResize)
  window.addEventListener('tv-annotation-rail-hover', onAnnotationRailHover)
})
onUnmounted(() => {
  window.removeEventListener('click', onWindowClickOutside, { capture: true })
  window.removeEventListener('keydown', onEscape)
  window.removeEventListener('resize', onViewportResize)
  window.removeEventListener('tv-annotation-rail-hover', onAnnotationRailHover)
})
// SIDEBAR-PUSH-LAYOUT END

function handleMobileMenuClick(item) {
  if (!item.submenu || item.submenu.length === 0) {
    if (item.route) {
      trackMenuOpen(item.route) // MENU-PERSONALIZATION (2026-06-12)
      router.push(item.route)
      sidebarDrawer.value = false
    }
  }
}

function handleMobileSubmenuClick(subitem) {
  const route = typeof subitem === 'string' ? subitem : subitem.route
  if (route) {
    trackMenuOpen(route) // MENU-PERSONALIZATION (2026-06-12)
    router.push(route)
    sidebarDrawer.value = false
  }
}

async function logout() {
  await authStore.logout()
  // Hard redirect (not SPA navigation) so the entire app re-bootstraps cleanly
  // on /login: no in-memory store data from the previous company survives. This
  // is what makes switching companies (logout -> login into another tenant)
  // show ONLY the new company's branding/data — never the old one's. Mirrors
  // the technician app + God Mode, which already hard-reload for tenant isolation.
  window.location.href = '/login'
}

// Click the sidebar brand logo -> back to the main Dashboard (home).
function goHome() {
  if (router.currentRoute.value.path !== '/dashboard') router.push('/dashboard')
}

// Native (Capacitor): keep a stable shell key so login does not remount the
// entire v-app tree (that flash re-opens an empty LoginView mid-flow). Web
// keeps the authed/guest key so tenant branding still hard-resets on login.
const appShellKey = computed(() => {
  if (isNative()) return 'native'
  return authStore.isAuthenticated ? 'authed' : 'guest'
})
</script>

<template>
  <v-app :key="appShellKey">
    <!-- God Mode Banner -->
    <v-system-bar
      v-if="authStore.isGodMode && !isPublicSignRoute"
      color="god-mode"
      height="36"
      class="god-mode-bar"
      window
    >
      <v-icon size="18" class="mr-2">mdi-incognito</v-icon>
      <span class="text-body-2 font-weight-bold">
        GOD MODE:<template v-if="authStore.godModeCompany"> {{ authStore.godModeCompany }} —</template>
        viewing as {{ authStore.user?.first_name }} {{ authStore.user?.last_name }} ({{ authStore.user?.email }})
      </span>
      <v-chip v-if="authStore.domain?.id" size="x-small" variant="outlined" color="white" class="ml-3" title="Active session domain — all data you enter goes to this company">
        <v-icon start size="12">mdi-domain</v-icon>
        Domain ID: {{ authStore.domain.id }}
      </v-chip>
      <v-chip v-if="authStore.godModeReadonly" size="x-small" variant="outlined" color="white" class="ml-3">
        <v-icon start size="12">mdi-eye-lock-outline</v-icon>
        READ-ONLY
      </v-chip>
      <v-spacer />
      <span v-if="godModeCountdown" class="text-body-2 mr-3 font-weight-bold">
        <v-icon size="14" class="mr-1">mdi-timer-outline</v-icon>{{ godModeCountdown }}
      </span>
      <span class="text-body-2 mr-3">Admin: {{ authStore.godModeAdminName }}</span>
      <v-btn size="small" variant="outlined" color="white" @click="authStore.exitGodMode()">
        <v-icon start size="16">mdi-close</v-icon>
        Exit
      </v-btn>
    </v-system-bar>

    <!-- Global connection status banner (visible when server is down) -->
    <ConnectionStatusBanner v-if="showLayout" />

    <template v-if="showLayout">
      <!-- DESKTOP Sidebar Navigation (narrow, icons only) -->
      <!-- SIDEBAR-MODE MODIFIED: width is dynamic (compact 64 / expanded 220) based on user preference -->
      <v-navigation-drawer
        v-if="!mobile"
        v-model="sidebarDrawer"
        permanent
        app
        :width="sidebarWidth"
        :color="appIsDark ? 'surface' : (isMenuDark ? 'primary' : 'surface')"
        :class="{ 'sidebar-drawer--expanded': isSidebarExpanded, 'sidebar--light': !sidebarOnDark }"
      >
        <div ref="sidebarContentRef" class="sidebar-content" @scroll="onSidebarScroll">
          <!-- Logo/Brand — click returns to the main Dashboard -->
          <div
            class="sidebar-logo"
            role="button"
            tabindex="0"
            @click="goHome"
            @keydown.enter="goHome"
          >
            <img :src="brandLogo" alt="FiveStarJob" class="sidebar-logo-img" />
          </div>

          <!-- MENU-PERSONALIZATION D3 (2026-06-12): the control block moved to the
               footer bar at the bottom of the rail — the top stays clean
               (logo, then menu items right away). See .sidebar-footer below. -->
          
          <!-- MENU-PERSONALIZATION (2026-06-12): Favorites-only flat panel —
               replaces the module list with starred items sorted by usage. -->
          <template v-if="favoritesOnly">
            <div class="favorites-panel-header">
              <v-icon size="16" color="warning">mdi-star</v-icon>
              <span class="favorites-panel-title">{{ $t('menu.favorites') }}</span>
              <v-spacer />
              <v-tooltip location="right" :text="$t('menu.exitFavorites')">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon
                    size="x-small"
                    variant="text"
                    :color="sidebarOnDark ? 'white' : 'primary'"
                    @click="setFavoritesOnly(false)"
                  >
                    <v-icon size="16">mdi-format-list-bulleted</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>
            <v-list class="sidebar-menu" density="compact" nav color="transparent">
              <v-list-item
                v-for="fav in favoriteMenuItems"
                :key="fav.route"
                class="sidebar-item favorites-item"
                :class="{ 'sidebar-item-route-active': route.path === fav.route || route.path.startsWith(fav.route + '/') }"
                :to="fav.route || undefined"
                @click="handleSubmenuClick(fav, $event)"
              >
                <template v-slot:prepend>
                  <v-icon size="18" :color="sidebarOnDark ? 'white' : 'primary'">{{ fav.icon || fav.moduleIcon }}</v-icon>
                </template>
                <v-list-item-title class="sidebar-item-label favorites-item-label">
                  {{ fav.title }}
                </v-list-item-title>
                <template v-slot:append>
                  <span v-if="menuUsage[fav.route]" class="favorites-usage">{{ menuUsage[fav.route] }}</span>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    :title="$t('menu.removeFavorite')"
                    @click.stop="toggleFavorite(fav.route)"
                  >
                    <v-icon size="14" color="warning">mdi-star</v-icon>
                  </v-btn>
                </template>
              </v-list-item>
              <div v-if="!favoriteMenuItems.length" class="favorites-empty">
                {{ $t('menu.favoritesEmpty') }}
              </div>
            </v-list>
            <div v-if="favoriteMenuItems.length" class="favorites-hint">{{ $t('menu.favoritesHint') }}</div>
          </template>

          <template v-else>
          <!-- Navigation Items - key forces re-render when permissions change -->
          <v-list class="sidebar-menu" density="compact" nav color="transparent" :key="'menu-' + (authStore.permissions?.length || 0)">
            <!-- MENU-PERSONALIZATION (2026-06-12): Quick Access pseudo-module (always first).
                 Flyout shows pinned items (drag to reorder) + most-used pages. -->
            <v-tooltip v-if="showQuickAccessItem" location="right" :text="$t('menu.quickAccess')" :disabled="isSidebarExpanded || !!activeSubmenu">
              <template v-slot:activator="{ props }">
                <v-list-item
                  v-bind="props"
                  class="sidebar-item sidebar-item--qa"
                  :data-submenu-key="QUICK_ACCESS_TITLE"
                  :class="{ 'sidebar-item-active': activeSubmenu === QUICK_ACCESS_TITLE }"
                  @click="handleDesktopMenuClick(quickAccessModule, $event)"
                  @mouseenter="onItemHoverEnter(quickAccessModule, $event)"
                  @mouseleave="onItemHoverLeave()"
                >
                  <template v-slot:prepend>
                    <!-- Premium: 3D lightning icon (light/dark), same footprint as module icons.
                         Non-premium: fall back to the mdi lightning glyph. -->
                    <img
                      v-if="menuIcon3d(QUICK_ACCESS_TITLE)"
                      :src="menuIcon3d(QUICK_ACCESS_TITLE)"
                      alt="Quick Access"
                      class="menu-icon-3d"
                      :class="{ 'menu-icon-3d--glow': sidebarOnDark && activeSubmenu !== QUICK_ACCESS_TITLE && !brandingStore.isNewDesignIcons }"
                    />
                    <v-icon v-else class="menu-icon-spin" color="accent" :size="menuIconSizePx">mdi-lightning-bolt</v-icon>
                  </template>
                  <v-list-item-title v-if="isSidebarExpanded" class="sidebar-item-label">
                    {{ $t('menu.quickAccess') }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-tooltip>
            <v-divider v-if="showQuickAccessItem" class="sidebar-qa-divider" :class="sidebarOnDark ? 'sidebar-qa-divider--dark' : ''" />
            <!-- SIDEBAR-MODE MODIFIED: tooltip disabled in expanded mode (label is already visible) -->
            <!-- MENU-AUDIT FIX (2026-06-12): tooltip also disabled while the submenu flyout
                 is open — tooltips are globally forced to max z-index (style.css) and were
                 rendering ON TOP of the open flyout panel at the same anchor position. -->
            <v-tooltip
              v-for="item in displayMenuItems"
              :key="item.title"
              location="right"
              :text="item.displayTitle || item.title"
              :disabled="isSidebarExpanded || !!activeSubmenu"
            >
              <template v-slot:activator="{ props }">
                <!-- SIDEBAR-PUSH-LAYOUT MODIFIED: submenu-active (primary highlight) and route-active (thin bar) can coexist -->
                <v-list-item
                  v-bind="props"
                  class="sidebar-item"
                  :data-submenu-key="item.title"
                  :class="{
                    'sidebar-item-active': activeSubmenu === item.title,
                    'sidebar-item-route-active': isItemActiveByRoute(item) && activeSubmenu !== item.title
                  }"
                  :to="(!item.submenu || !item.submenu.length) && item.route ? item.route : undefined"
                  @click="handleDesktopMenuClick(item, $event); spinMenuIcon($event)"
                  @mouseenter="onItemHoverEnter(item, $event); spinMenuIcon($event)"
                  @mouseleave="onItemHoverLeave()"
                >
                  <template v-slot:prepend>
                    <!-- The open-task count rides on the group icon so the workload is
                         readable with the sidebar collapsed, on any page. -->
                    <v-badge
                      :content="menuTaskCount(item)"
                      :model-value="menuTaskCount(item) > 0"
                      color="accent"
                      offset-x="4"
                      offset-y="4"
                    >
                      <img
                        v-if="menuIcon3d(item.title) && !item._iconOverride"
                        :src="menuIcon3d(item.title)"
                        :alt="item.title"
                        class="menu-icon-3d"
                        :class="{ 'menu-icon-3d--glow': sidebarOnDark && activeSubmenu !== item.title && !isItemActiveByRoute(item) && !brandingStore.isNewDesignIcons }"
                      />
                      <CallCenterIcon v-else-if="item.customIcon === 'call-center'" :size="24" class="custom-icon" />
                      <v-icon v-else class="menu-icon-spin" :color="sidebarOnDark ? 'white' : 'primary'" :size="menuIconSizePx">{{ item.icon }}</v-icon>
                    </v-badge>
                  </template>
                  <!-- SIDEBAR-MODE MODIFIED: title is shown only when sidebar is expanded -->
                  <v-list-item-title v-if="isSidebarExpanded" class="sidebar-item-label">
                    {{ item.displayTitle || item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-tooltip>
          </v-list>

          <!-- Scroll indicator when bottom items are hidden -->
          <div v-if="sidebarCanScrollDown" class="sidebar-scroll-hint" @click="scrollSidebarDown">
            <v-icon size="14" :color="sidebarOnDark ? 'white' : 'primary'" class="opacity-70">mdi-chevron-double-down</v-icon>
          </div>
          
          <!-- Bottom Settings -->
          <v-list class="sidebar-bottom" density="compact" nav color="transparent">
            <!-- SIDEBAR-MODE MODIFIED: tooltip disabled in expanded mode -->
            <!-- MENU-AUDIT FIX (2026-06-12): same tooltip-over-flyout fix as the main list -->
            <v-tooltip
              v-for="item in displayBottomMenuItems"
              :key="item.title"
              location="right"
              :text="item.displayTitle || item.title"
              :disabled="isSidebarExpanded || !!activeSubmenu"
            >
              <template v-slot:activator="{ props }">
                <!-- SIDEBAR-PUSH-LAYOUT MODIFIED: bottom items have no route — only submenu-active applies -->
                <v-list-item
                  v-bind="props"
                  class="sidebar-item"
                  :data-submenu-key="item.title"
                  :class="{
                    'sidebar-item-active': activeSubmenu === item.title
                  }"
                  @click="handleDesktopMenuClick(item, $event); spinMenuIcon($event)"
                  @mouseenter="onItemHoverEnter(item, $event); spinMenuIcon($event)"
                  @mouseleave="onItemHoverLeave()"
                >
                  <template v-slot:prepend>
                    <img
                      v-if="menuIcon3d(item.title) && !item._iconOverride"
                      :src="menuIcon3d(item.title)"
                      :alt="item.title"
                      class="menu-icon-3d"
                      :class="{ 'menu-icon-3d--glow': sidebarOnDark && activeSubmenu !== item.title && !isItemActiveByRoute(item) && !brandingStore.isNewDesignIcons }"
                    />
                    <CallCenterIcon v-else-if="item.customIcon === 'call-center'" :size="24" class="custom-icon" />
                    <v-icon v-else class="menu-icon-spin" :color="sidebarOnDark ? 'white' : 'primary'" :size="menuIconSizePx">{{ item.icon }}</v-icon>
                  </template>
                  <!-- SIDEBAR-MODE MODIFIED: title only in expanded mode -->
                  <v-list-item-title v-if="isSidebarExpanded" class="sidebar-item-label">
                    {{ item.displayTitle || item.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </v-tooltip>
          </v-list>
          </template>
          <!-- MENU-PERSONALIZATION: end of favorites-mode conditional -->
        </div>

        <!-- MENU-PERSONALIZATION D3 (2026-06-12): footer control bar (Slack/GitLab
             pattern) — Menu Settings gear + Collapse/Expand pinned to the bottom,
             behind a divider. Expanded mode shows a labeled "Collapse" button. -->
        <div class="sidebar-footer" :class="{ 'sidebar-footer--expanded': isSidebarExpanded || favoritesOnly }">
          <v-divider :class="sidebarOnDark ? 'sidebar-footer-divider--dark' : ''" />
          <div class="sidebar-footer-row">
            <!-- Menu Settings gear + popover -->
            <v-menu v-model="menuSettingsOpen" location="right" offset="8" :close-on-content-click="false">
              <template v-slot:activator="{ props: menuProps }">
                <v-tooltip location="right" :text="$t('menu.settingsTooltip')">
                  <template v-slot:activator="{ props: tipProps }">
                    <v-btn
                      v-bind="{ ...menuProps, ...tipProps }"
                      icon
                      size="x-small"
                      variant="text"
                      :color="sidebarOnDark ? 'white' : 'primary'"
                      class="sidebar-toggle-btn"
                      :class="{ 'sidebar-toggle-btn--active': menuSettingsOpen }"
                    >
                      <v-icon size="18">mdi-cog-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </template>
              <v-card min-width="280" rounded="lg">
                <v-card-title class="d-flex align-center pa-3 pb-2">
                  <v-icon size="18" color="primary" class="mr-2">mdi-cog-outline</v-icon>
                  <span class="text-subtitle-2">{{ $t('menu.settings') }}</span>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-3">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div>
                      <div class="text-body-2">{{ $t('menu.animation') }}</div>
                      <div class="text-caption text-grey">{{ $t('menu.animationHint') }}</div>
                    </div>
                    <v-switch
                      :model-value="menuAnimationEnabled"
                      color="accent"
                      hide-details
                      density="compact"
                      @update:model-value="setMenuAnimation"
                    />
                  </div>
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div>
                      <div class="text-body-2">{{ $t('menu.robotAnimation') }}</div>
                      <div class="text-caption text-grey">{{ $t('menu.robotAnimationHint') }}</div>
                    </div>
                    <v-switch
                      :model-value="robotAnimationEnabled"
                      color="accent"
                      hide-details
                      density="compact"
                      @update:model-value="setRobotAnimation"
                    />
                  </div>
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div>
                      <div class="text-body-2">{{ $t('menu.favoritesOnly') }}</div>
                      <div class="text-caption text-grey">{{ $t('menu.favoritesOnlyHint') }}</div>
                    </div>
                    <v-switch
                      :model-value="favoritesOnly"
                      color="accent"
                      hide-details
                      density="compact"
                      @update:model-value="setFavoritesOnly"
                    />
                  </div>
                  <div class="d-flex align-center justify-space-between mb-2">
                    <div class="text-body-2">{{ $t('menu.theme') }}</div>
                    <v-btn-toggle
                      :model-value="menuTheme"
                      density="compact"
                      mandatory
                      color="accent"
                      @update:model-value="setMenuTheme"
                    >
                      <v-btn size="x-small" value="light">{{ $t('menu.themeLight') }}</v-btn>
                      <v-btn size="x-small" value="dark">{{ $t('menu.themeDark') }}</v-btn>
                    </v-btn-toggle>
                  </div>
                  <div class="d-flex align-center justify-space-between">
                    <div class="text-body-2">{{ $t('menu.width') }}</div>
                    <v-btn-toggle
                      :model-value="sidebarMode"
                      density="compact"
                      mandatory
                      color="accent"
                      @update:model-value="v => v !== sidebarMode && toggleSidebarMode()"
                    >
                      <v-btn size="x-small" value="compact">{{ $t('menu.widthIcons') }}</v-btn>
                      <v-btn size="x-small" value="expanded">{{ $t('menu.widthLabels') }}</v-btn>
                    </v-btn-toggle>
                  </div>
                </v-card-text>
              </v-card>
            </v-menu>
            <!-- Collapse / Expand (hidden in favorites-only mode — width is forced) -->
            <v-tooltip
              v-if="!favoritesOnly"
              location="right"
              :text="isSidebarExpanded ? $t('menu.collapseMenu') : $t('menu.expandMenu')"
            >
              <template v-slot:activator="{ props }">
                <button
                  v-bind="props"
                  type="button"
                  class="sidebar-collapse-btn"
                  :class="{
                    'sidebar-collapse-btn--labeled': isSidebarExpanded,
                    'sidebar-collapse-btn--dark': sidebarOnDark
                  }"
                  @click="toggleSidebarMode"
                >
                  <v-icon size="15" :color="sidebarOnDark ? 'white' : 'primary'">
                    {{ isSidebarExpanded ? 'mdi-chevron-double-left' : 'mdi-chevron-double-right' }}
                  </v-icon>
                  <span v-if="isSidebarExpanded" class="sidebar-collapse-label">{{ $t('menu.collapse') }}</span>
                </button>
              </template>
            </v-tooltip>
          </div>
        </div>
      </v-navigation-drawer>

      <!-- MOBILE Hamburger Menu (surface-colored, text-based, expandable) -->
      <v-navigation-drawer
        v-if="mobile"
        v-model="sidebarDrawer"
        temporary
        app
        :width="280"
        color="surface"
      >
        <!-- Mobile Menu Header -->
        <div class="mobile-menu-header">
          <div class="d-flex align-center">
            <v-icon size="32" color="primary">mdi-star-four-points</v-icon>
            <span class="text-h6 ml-3 font-weight-bold" style="color: rgb(var(--v-theme-primary));">FiveStarJob</span>
          </div>
          <v-btn icon size="small" @click="sidebarDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider></v-divider>

        <!-- MENU-PERSONALIZATION (2026-06-12, M1 "Sheet"): Quick Access chips strip
             (pinned + frequent, max 6) + All / Favorites segmented control. -->
        <div v-if="mobileQuickItems.length" class="mobile-qa-strip">
          <div class="mobile-qa-label">
            <v-icon size="12" color="accent">mdi-lightning-bolt</v-icon>
            <span>{{ $t('menu.quickAccess') }}</span>
          </div>
          <div class="mobile-qa-chips">
            <v-chip
              v-for="item in mobileQuickItems"
              :key="item.route"
              size="small"
              color="accent"
              variant="tonal"
              class="mobile-qa-chip"
              @click="handleMobileSubmenuClick(item)"
            >
              <v-icon start size="14">{{ item.icon || item.moduleIcon }}</v-icon>
              {{ item.title }}
            </v-chip>
          </div>
        </div>
        <div class="mobile-menu-tabs">
          <v-btn-toggle v-model="mobileMenuTab" density="compact" mandatory color="accent" class="mobile-menu-tabs-toggle">
            <v-btn value="all" size="small" class="flex-grow-1">{{ $t('menu.all') }}</v-btn>
            <v-btn value="fav" size="small" class="flex-grow-1">
              <v-icon start size="14" color="warning">mdi-star</v-icon>{{ $t('menu.favorites') }}
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- MENU-PERSONALIZATION: Favorites tab — flat list sorted by usage -->
        <v-list v-if="mobileMenuTab === 'fav'" nav>
          <v-list-item
            v-for="fav in favoriteMenuItems"
            :key="fav.route"
            :to="fav.route"
            @click="onMobileSubnavClick(fav)"
          >
            <template v-slot:prepend>
              <v-icon size="20">{{ fav.icon || fav.moduleIcon }}</v-icon>
            </template>
            <v-list-item-title class="d-flex align-center">
              {{ fav.title }}
              <span class="text-caption text-grey ml-2">{{ fav.moduleTitle }}</span>
            </v-list-item-title>
            <template v-slot:append>
              <v-btn
                icon
                size="x-small"
                variant="text"
                :title="$t('menu.removeFavorite')"
                @click.stop.prevent="toggleFavorite(fav.route)"
              >
                <v-icon size="16" color="warning">mdi-star</v-icon>
              </v-btn>
            </template>
          </v-list-item>
          <div v-if="!favoriteMenuItems.length" class="favorites-empty favorites-empty--mobile">
            {{ $t('menu.favoritesEmpty') }}
          </div>
        </v-list>

        <!-- Mobile Menu Items (text with expandable submenus) - key forces re-render -->
        <v-list v-if="mobileMenuTab === 'all'" nav :key="'mobile-menu-' + (authStore.permissions?.length || 0)">
          <template v-for="item in displayMenuItems" :key="item.title">
            <!-- Item with submenu (expandable) -->
            <v-list-group v-if="item.submenu && item.submenu.length > 0" :value="item.title">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <CallCenterIcon v-if="item.customIcon === 'call-center'" :size="24" class="mr-4" />
                    <v-icon v-else>{{ item.icon }}</v-icon>
                  </template>
                  <v-list-item-title class="d-flex align-center">
                    {{ item.displayTitle || item.title }}
                    <PermissionIndicator 
                      v-if="item.permission" 
                      :permission-code="item.permission"
                    />
                    <!-- Open tasks live one level down, so the group carries the count
                         while it is collapsed. -->
                    <v-chip
                      v-if="menuTaskCount(item) > 0"
                      color="accent"
                      size="x-small"
                      variant="flat"
                      class="ml-2"
                    >{{ menuTaskCount(item) }}</v-chip>
                  </v-list-item-title>
                </v-list-item>
              </template>

              <!-- Submenu items -->
              <template v-for="(subitem, index) in item.submenu" :key="index">
                <v-divider v-if="subitem.divider" class="my-1 ml-12" />
                <!-- MENU-AUDIT (2026-07-08): group headers now render on mobile too
                     (they used to be silently skipped, leaving groups unseparated) -->
                <v-list-subheader
                  v-else-if="subitem.header"
                  class="mobile-submenu-header pl-8"
                >
                  {{ subitem.title }}
                </v-list-subheader>
                <!-- MENU-AUDIT FIX (2026-06-12): had BOTH :to and a router.push click handler
                     (double navigation). :to handles routing + active state; click only closes. -->
                <!-- MENU-PERSONALIZATION (2026-06-12): click tracks usage + closes drawer
                     (:to still does the navigation); star toggles favorite. -->
                <v-list-item
                  v-else
                  class="pl-8 mobile-submenu-item"
                  :to="subitem.route || subitem"
                  @click="onMobileSubnavClick(subitem)"
                >
                  <!-- MENU-ICONS (2026-07-08): same per-item icons as the desktop flyout -->
                  <template v-slot:prepend>
                    <v-icon size="20">{{ subitem.icon || 'mdi-circle-small' }}</v-icon>
                  </template>
                  <v-list-item-title class="d-flex align-center">
                    {{ subitem.title || subitem }}
                    <PermissionIndicator 
                      v-if="subitem.permission" 
                      :permission-code="subitem.permission"
                    />
                  </v-list-item-title>
                  <template v-if="subitem.route" v-slot:append>
                    <!-- Open tasks assigned to me: the workload behind this row. -->
                    <v-chip
                      v-if="menuTaskCount(subitem) > 0"
                      color="accent"
                      size="x-small"
                      variant="flat"
                      class="mr-1"
                    >{{ menuTaskCount(subitem) }}</v-chip>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      :title="isFavorite(subitem.route) ? $t('menu.removeFavorite') : $t('menu.addFavorite')"
                      @click.stop.prevent="toggleFavorite(subitem.route)"
                    >
                      <v-icon size="16" :color="isFavorite(subitem.route) ? 'warning' : 'grey-lighten-1'">
                        {{ isFavorite(subitem.route) ? 'mdi-star' : 'mdi-star-outline' }}
                      </v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </template>
            </v-list-group>

            <!-- Item without submenu (direct link) -->
            <v-list-item
              v-else
              @click="handleMobileMenuClick(item)"
            >
              <template v-slot:prepend>
                <CallCenterIcon v-if="item.customIcon === 'call-center'" :size="24" class="mr-4" />
                <v-icon v-else>{{ item.icon }}</v-icon>
              </template>
              <v-list-item-title class="d-flex align-center">
                {{ item.title }}
                <PermissionIndicator 
                  v-if="item.permission" 
                  :permission-code="item.permission"
                />
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list>

        <v-divider></v-divider>

        <!-- Mobile Bottom Menu (with submenus) -->
        <v-list nav>
          <template v-for="item in filteredBottomMenuItems" :key="item.title">
            <!-- Item with submenu (expandable) -->
            <v-list-group v-if="item.submenu && item.submenu.length > 0" :value="item.title">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <CallCenterIcon v-if="item.customIcon === 'call-center'" :size="24" class="mr-4" />
                    <v-icon v-else>{{ item.icon }}</v-icon>
                  </template>
                  <v-list-item-title class="d-flex align-center">
                    {{ item.title }}
                    <PermissionIndicator 
                      v-if="item.permission" 
                      :permission-code="item.permission"
                    />
                  </v-list-item-title>
                </v-list-item>
              </template>

              <!-- Submenu items -->
              <template v-for="(subitem, index) in item.submenu" :key="index">
                <v-divider v-if="subitem.divider" class="my-1 ml-12" />
                <!-- MENU-AUDIT FIX (2026-06-12): had BOTH :to and a router.push click handler
                     (double navigation). :to handles routing + active state; click only closes. -->
                <!-- MENU-PERSONALIZATION (2026-06-12): click tracks usage + closes drawer
                     (:to still does the navigation); star toggles favorite. -->
                <v-list-subheader
                  v-else-if="subitem.header"
                  class="mobile-submenu-header pl-8"
                >
                  {{ subitem.title }}
                </v-list-subheader>
                <v-list-item
                  v-else
                  class="pl-8 mobile-submenu-item"
                  :to="subitem.route || subitem"
                  @click="onMobileSubnavClick(subitem)"
                >
                  <!-- MENU-ICONS (2026-07-08): same per-item icons as the desktop flyout -->
                  <template v-slot:prepend>
                    <v-icon size="20">{{ subitem.icon || 'mdi-circle-small' }}</v-icon>
                  </template>
                  <v-list-item-title class="d-flex align-center">
                    {{ subitem.title || subitem }}
                    <PermissionIndicator 
                      v-if="subitem.permission" 
                      :permission-code="subitem.permission"
                    />
                  </v-list-item-title>
                  <template v-if="subitem.route" v-slot:append>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      :title="isFavorite(subitem.route) ? $t('menu.removeFavorite') : $t('menu.addFavorite')"
                      @click.stop.prevent="toggleFavorite(subitem.route)"
                    >
                      <v-icon size="16" :color="isFavorite(subitem.route) ? 'warning' : 'grey-lighten-1'">
                        {{ isFavorite(subitem.route) ? 'mdi-star' : 'mdi-star-outline' }}
                      </v-icon>
                    </v-btn>
                  </template>
                </v-list-item>
              </template>
            </v-list-group>

            <!-- Item without submenu (direct link) -->
            <!-- MENU-AUDIT FIX (2026-06-12): `sidebarDrawer.value = false` in a template
                 throws (ref is auto-unwrapped here) — drawer never closed after navigation.
                 handleMobileMenuClick() does push + close correctly. -->
            <v-list-item
              v-else
              :prepend-icon="item.icon"
              @click="handleMobileMenuClick(item)"
            >
              <v-list-item-title class="d-flex align-center">
                {{ item.title }}
                <PermissionIndicator 
                  v-if="item.permission" 
                  :permission-code="item.permission"
                />
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-list>

        <v-divider />

        <!-- Mobile User Info + Sign Out -->
        <div class="pa-3">
          <div class="d-flex align-center mb-2">
            <v-avatar color="accent" size="32" class="mr-2">
              <v-img v-if="authStore.user?.avatar_url" :src="authStore.user.avatar_url" cover />
              <span v-else class="text-white text-body-2">{{ authStore.user?.first_name ? authStore.user.first_name[0] : 'U' }}</span>
            </v-avatar>
            <div>
              <div class="text-body-2 font-weight-medium">{{ getUserFullName() }}</div>
              <div class="text-caption text-grey">{{ authStore.domain?.name || '' }}</div>
            </div>
          </div>
          <v-btn
            block
            variant="outlined"
            color="error"
            prepend-icon="mdi-logout"
            size="small"
            @click="logout"
          >
            Sign Out
          </v-btn>
        </div>
      </v-navigation-drawer>

      <!-- Desktop Submenu Flyout (overlay — floats OVER the content, anchored next to the item) -->
      <!-- SIDEBAR-FLYOUT BEGIN (2026-06-09): HubSpot-style overlay. No `app`, so v-main is NOT
           pushed aside; the panel is position:fixed, anchored to the hovered/clicked item
           (top = item top, left = item right) and sized to its own content (max 80vh, scrolls). -->
      <div
        v-if="!mobile && submenuDrawerOpen"
        ref="submenuFlyoutEl"
        class="submenu-flyout"
        :style="submenuFlyoutStyle"
        @mouseenter="onSubmenuPanelEnter()"
        @mouseleave="onSubmenuPanelLeave()"
      >
        <div class="submenu-header">
          <div class="d-flex align-center" style="min-width: 0;">
            <v-icon
              v-if="activeSubmenuItem?.icon && activeSubmenuItem?.customIcon !== 'call-center'"
              size="20"
              color="primary"
              class="mr-2"
            >
              {{ activeSubmenuItem.icon }}
            </v-icon>
            <CallCenterIcon
              v-else-if="activeSubmenuItem?.customIcon === 'call-center'"
              :size="20"
              class="mr-2"
            />
            <!-- MENU-PERSONALIZATION (2026-06-12): human title (Quick Access uses an internal key) -->
            <h3 class="text-subtitle-1 font-weight-bold text-truncate">
              {{ activeSubmenuTitle }}
            </h3>
            <PermissionIndicator
              v-if="activeSubmenuItem?.permission"
              :permission-code="activeSubmenuItem.permission"
              class="ml-2"
            />
            <v-icon
              v-if="submenuPinned"
              size="14"
              color="grey"
              class="ml-2"
              title="Pinned (click icon to close, or click outside / press ESC)"
            >
              mdi-pin
            </v-icon>
          </div>
          <v-btn icon size="x-small" variant="text" @click="closeSubmenu">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <div class="submenu-flyout-scroll">
          <v-list class="submenu-list" density="compact" nav>
            <template v-for="(subitem, index) in activeSubmenuItems" :key="submenuItemKey(subitem, index)">
              <v-divider v-if="subitem.divider" class="my-2" />
              <v-list-subheader
                v-else-if="subitem.header"
                class="submenu-group-header d-flex align-center"
              >
                {{ subitem.title }}
                <PermissionIndicator
                  v-if="subitem.permission"
                  :permission-code="subitem.permission"
                />
              </v-list-subheader>
              <!-- MENU-PERSONALIZATION (2026-06-12): Quick Access empty state -->
              <div v-else-if="subitem._qaEmpty" class="qa-empty">
                {{ $t('menu.quickAccessEmpty') }}
              </div>
              <!-- MENU-PERSONALIZATION (2026-06-12): rows got star (favorite) + pin
                   (Quick Access) hover actions; pinned QA rows are drag-reorderable. -->
              <v-list-item
                v-else
                class="submenu-item"
                :class="subitem._qaPinned ? [pinnedDropClass(subitem.route), { 'qa-dragging': dragPinnedRoute === subitem.route }] : null"
                :to="subitem.route || undefined"
                :active="!!subitem.route && subitem.route === activeSubmenuRoute"
                :draggable="subitem._qaPinned && dragArmedRoute === subitem.route"
                @dragstart="subitem._qaPinned && onPinnedDragStart(subitem.route)"
                @dragover.prevent="subitem._qaPinned && onPinnedDragOver($event, subitem.route)"
                @dragend="subitem._qaPinned && onPinnedDragEnd()"
                @drop.prevent="subitem._qaPinned && onPinnedDrop(subitem.route)"
                @click="handleSubmenuClick(subitem, $event)"
              >
                <!-- MENU-ICONS (2026-07-08): every row gets a prepend icon (fallback
                     mdi-circle-small) so labels align in one column in ALL flyouts. -->
                <template v-slot:prepend>
                  <!-- TICK-00064: the handle is the only drag source. Pressing it
                       arms the row; releasing anywhere disarms it, so a normal
                       click on the label still opens the page. -->
                  <v-icon
                    v-if="subitem._qaPinned"
                    size="16"
                    class="qa-drag-handle"
                    :title="$t('menu.dragToReorder')"
                    @pointerdown="onPinnedHandleDown(subitem.route)"
                    @pointerup="onPinnedDragEnd()"
                    @click.stop
                  >mdi-drag-vertical</v-icon>
                  <v-icon v-else size="20" class="submenu-item-icon">{{ subitem.icon || 'mdi-circle-small' }}</v-icon>
                </template>
                <!-- MENU-ICONS (2026-07-08): text in its own span so a long title
                     truncates, while the permission shield stays visible. -->
                <v-list-item-title class="submenu-item-label d-flex align-center">
                  <span class="submenu-item-text">{{ subitem.title || subitem }}</span>
                  <PermissionIndicator
                    v-if="subitem.permission"
                    :permission-code="subitem.permission"
                  />
                </v-list-item-title>
                <template v-if="subitem.route" v-slot:append>
                  <!-- Open tasks assigned to me: the workload behind this row. -->
                  <v-chip
                    v-if="menuTaskCount(subitem) > 0"
                    color="accent"
                    size="x-small"
                    variant="flat"
                    class="mr-1"
                  >{{ menuTaskCount(subitem) }}</v-chip>
                  <span v-if="subitem._opens" class="qa-opens">{{ subitem._opens }}</span>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    class="row-action-btn"
                    :class="{ 'row-action-btn--on': isPinned(subitem.route) }"
                    :title="isPinned(subitem.route) ? $t('menu.unpinItem') : $t('menu.pinItem')"
                    @click.stop="togglePinned(subitem.route)"
                  >
                    <v-icon size="14" :color="isPinned(subitem.route) ? 'accent' : 'grey'">
                      {{ isPinned(subitem.route) ? 'mdi-pin' : 'mdi-pin-outline' }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    class="row-action-btn"
                    :class="{ 'row-action-btn--on': isFavorite(subitem.route) }"
                    :title="isFavorite(subitem.route) ? $t('menu.removeFavorite') : $t('menu.addFavorite')"
                    @click.stop="toggleFavorite(subitem.route)"
                  >
                    <v-icon size="14" :color="isFavorite(subitem.route) ? 'warning' : 'grey'">
                      {{ isFavorite(subitem.route) ? 'mdi-star' : 'mdi-star-outline' }}
                    </v-icon>
                  </v-btn>
                </template>
              </v-list-item>
            </template>
          </v-list>
        </div>
        <!-- MENU-PERSONALIZATION (2026-06-12): how-to hint at the bottom of Quick Access -->
        <div v-if="activeSubmenu === QUICK_ACCESS_TITLE" class="qa-hint">
          {{ $t('menu.quickAccessHint') }}
        </div>
      </div>
      <!-- SIDEBAR-FLYOUT END -->
      
      <!-- Top App Bar — `surface` follows the active theme (white in light,
           slate in dark); a subtle bottom border separates it from content. -->
      <v-app-bar app color="surface" elevation="0" :height="64" class="app-top-bar" :class="{ 'app-top-bar--editing': hb.editing && hb.active }">
        <template v-if="mobile">
          <v-app-bar-nav-icon @click="sidebarDrawer = !sidebarDrawer"></v-app-bar-nav-icon>
        </template>

        <!-- COMPANY-BRAND BEGIN (2026-06-09) — current company logo + name (leftmost).
             Logo size is hard-capped via CSS (object-fit: contain) so a huge upload
             can never break the header. Name hidden on mobile to save space. -->
        <div v-if="companyLogo || companyName" class="app-company-brand d-flex align-center ga-2">
          <img v-if="companyLogo" :src="companyLogo" :alt="companyName" class="app-company-logo" />
          <span v-if="!mobile && companyName" class="app-company-name">{{ companyName }}</span>
          <!-- In-app company switcher (member companies; all companies for global admins) -->
          <CompanySwitcher />
        </div>
        <!-- COMPANY-BRAND END -->

        <!-- Module title intentionally removed from header (2026-06-14) — the open
             module is already obvious from the page itself. This space is reserved
             for future header tools/widgets. -->

        <v-spacer></v-spacer>

        <!-- Workday Presence Button (moved left of search, desktop only) -->
        <v-menu v-if="!isCompactHeader && hb.inHeader('presence')" v-model="showPresenceMenu" location="bottom end" offset="8" :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-tooltip text="Work Status" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn v-bind="{ ...props, ...tooltipProps }" variant="tonal" :color="presenceStatusColor" class="presence-pill mr-4" rounded="pill" height="36" :style="{ order: hb.orderOf('presence') + 1 }" data-hkey="presence">
                  <v-icon start size="13" :color="presenceStatusColor">mdi-circle</v-icon>
                  <span v-if="!mobile" class="text-caption" style="font-variant-numeric: tabular-nums;">
                    {{ presenceStatusLabel }} <span v-if="presenceWorkdayStarted" class="font-weight-bold">{{ currentStatusTimer }}</span>
                  </span>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-card min-width="280" rounded="lg">
            <v-card-text class="pa-3">
              <div class="text-subtitle-2 mb-2">Work Status</div>

              <!-- Not working yet -->
              <v-btn
                v-if="!presenceWorkdayStarted"
                block color="accent" variant="flat" size="small" class="mb-2"
                @click="toggleWorkday(); showPresenceMenu = false"
              >
                <v-icon start size="16">mdi-play-circle</v-icon> Start Workday
              </v-btn>

              <!-- Working: show timer + status controls -->
              <template v-else>
                <!-- Current status timer (like technician app) -->
                <div class="text-center mb-3 pa-2 rounded" style="background: var(--ui-hover, rgba(0,0,0,0.04));">
                  <div class="text-caption text-grey mb-1">In "{{ presenceStatusLabel }}" for</div>
                  <div class="text-h5 font-weight-bold" :class="`text-${presenceStatusColor}`" style="font-variant-numeric: tabular-nums;">
                    {{ currentStatusTimer }}
                  </div>
                  <div class="text-caption text-grey mt-1">Total today: {{ workdayTimer }}</div>
                </div>

                <!-- Status breakdown (like tech Drive/Work/Event/Idle) -->
                <div class="d-flex text-center mb-3 ga-1">
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-bold text-success" style="font-variant-numeric: tabular-nums;">{{ formatDurationShort(todaySummary.available_seconds) }}</div>
                    <div class="text-caption text-grey">Available</div>
                  </div>
                  <v-divider vertical />
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-bold text-warning" style="font-variant-numeric: tabular-nums;">{{ formatDurationShort(todaySummary.on_hold_seconds) }}</div>
                    <div class="text-caption text-grey">On Hold</div>
                  </div>
                  <v-divider vertical />
                  <div class="flex-grow-1">
                    <div class="text-body-2 font-weight-bold text-error" style="font-variant-numeric: tabular-nums;">{{ formatDurationShort(todaySummary.busy_seconds) }}</div>
                    <div class="text-caption text-grey">Busy</div>
                  </div>
                </div>

                <!-- Status toggle -->
                <div class="d-flex ga-1 mb-2">
                  <v-btn size="small" :variant="presenceStatus === 'available' ? 'flat' : 'outlined'" color="success" class="flex-grow-1" @click="changePresenceStatus('available')">Available</v-btn>
                  <v-btn size="small" :variant="presenceStatus === 'on_hold' ? 'flat' : 'outlined'" color="warning" class="flex-grow-1" @click="changePresenceStatus('on_hold')">On Hold</v-btn>
                  <v-btn size="small" :variant="presenceStatus === 'busy' ? 'flat' : 'outlined'" color="error" class="flex-grow-1" @click="changePresenceStatus('busy')">Busy</v-btn>
                </div>

                <!-- End Workday + History -->
                <div class="d-flex ga-1">
                  <v-btn variant="outlined" color="grey" size="small" class="flex-grow-1" @click="toggleWorkday(); showPresenceMenu = false">
                    <v-icon start size="14">mdi-stop-circle</v-icon> End Workday
                  </v-btn>
                  <v-btn variant="text" size="small" @click="openPresenceHistory(); showPresenceMenu = false">
                    <v-icon size="16">mdi-history</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-menu>

        <!-- Global Search -->
        <span v-if="authStore.hasPermission('header.search') && hb.inHeader('search')" class="hdr-anchor" :style="{ order: hb.orderOf('search') + 1 }" data-hkey="search">
          <GlobalSearchDropdown :mobile="mobile" />
        </span>

        <!-- Voice Assistant — single hidden instance shared by desktop + compact
             headers and the Support tab (breakpoint swap must not unregister). -->
        <VoiceAssistantButton
          v-if="authStore.isAuthenticated && !isPortalRoute"
          ref="voiceAssistantRef"
          :hide-trigger="true"
          :hide-panel="true"
          class="d-none"
        />

        <!-- COMPACT-HEADER BEGIN (2026-06-03) — viewport <1280px shows ONLY: Search, Phone, Voice, ⋯, Avatar.
             Desktop (>=1280px) keeps the original full icon row UNCHANGED. -->
        <template v-if="!isCompactHeader">
        
        <!-- Presence History Dialog -->
        <v-dialog v-model="showPresenceHistory" max-width="500" scrollable>
          <v-card rounded="lg">
            <v-card-title class="d-flex align-center pa-3">
              <v-icon class="mr-2" color="primary" size="20">mdi-history</v-icon>
              <span>Today's Work Log</span>
              <v-spacer />
              <v-btn icon size="small" variant="text" @click="showPresenceHistory = false">
                <v-icon size="18">mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-3" style="max-height: 400px;">
              <!-- Summary at top -->
              <div class="d-flex text-center mb-3 pa-2 rounded" style="background: var(--ui-hover, rgba(0,0,0,0.04));">
                <div class="flex-grow-1">
                  <div class="text-body-2 font-weight-bold text-success">{{ formatDurationShort(todaySummary.available_seconds) }}</div>
                  <div class="text-caption text-grey">Available</div>
                </div>
                <v-divider vertical />
                <div class="flex-grow-1">
                  <div class="text-body-2 font-weight-bold text-warning">{{ formatDurationShort(todaySummary.on_hold_seconds) }}</div>
                  <div class="text-caption text-grey">On Hold</div>
                </div>
                <v-divider vertical />
                <div class="flex-grow-1">
                  <div class="text-body-2 font-weight-bold text-error">{{ formatDurationShort(todaySummary.busy_seconds) }}</div>
                  <div class="text-caption text-grey">Busy</div>
                </div>
                <v-divider vertical />
                <div class="flex-grow-1">
                  <div class="text-body-2 font-weight-bold text-primary">{{ workdayTimer }}</div>
                  <div class="text-caption text-grey">Total</div>
                </div>
              </div>

              <!-- Timeline -->
              <v-timeline side="end" density="compact" class="ml-n2">
                <v-timeline-item
                  v-for="entry in presenceHistoryData"
                  :key="entry.id"
                  :dot-color="presenceHistoryStatusColors[entry.status] || 'grey'"
                  size="x-small"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <v-chip :color="presenceHistoryStatusColors[entry.status]" size="x-small" label class="mr-1">
                        {{ entry.status }}
                      </v-chip>
                      <span class="text-caption text-grey">{{ entry.trigger_detail || entry.trigger_type }}</span>
                    </div>
                    <div class="text-right">
                      <div class="text-caption" style="font-variant-numeric: tabular-nums;">{{ formatPresenceTime(entry.started_at) }}</div>
                      <div v-if="entry.duration_seconds" class="text-caption text-grey">{{ formatPresenceDuration(entry.duration_seconds) }}</div>
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>

              <div v-if="presenceHistoryData.length === 0" class="text-center pa-4 text-grey">
                <v-icon size="32" color="grey-lighten-1">mdi-clock-outline</v-icon>
                <p class="text-caption mt-2">No status changes today</p>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Parts In-App Notifications Bell removed from the header (2026-06-14):
             Parts has its own dedicated section/menu, so there's no need to
             surface it here. Logic (polling/counters) is left intact but unused. -->

        <!-- Task Notifications Bell with Dropdown -->
        <v-menu v-if="hb.inHeader('task_notifications')" v-model="showTaskNotifMenu" :close-on-content-click="false" location="bottom end" max-width="400" min-width="360">
          <template #activator="{ props: menuProps }">
            <v-tooltip text="Task Notifications" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn v-bind="{ ...menuProps, ...tooltipProps }" icon class="ml-1" color="secondary" size="small" :style="{ order: hb.orderOf('task_notifications') + 1 }" data-hkey="task_notifications">
                  <v-badge :content="taskNotifCount" :model-value="taskNotifCount > 0" color="error" overlap>
                    <img v-if="headerIcon3d('checklist')" :src="headerIcon3d('checklist')" class="header-icon-3d" alt="" />
                    <v-icon v-else size="20">mdi-clipboard-check-outline</v-icon>
                  </v-badge>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-card elevation="8" rounded="lg">
            <v-card-title class="d-flex align-center justify-space-between pa-3 pb-1">
              <span class="text-subtitle-1 font-weight-medium">Task Notifications</span>
              <v-btn v-if="taskNotifCount > 0" variant="text" color="accent" size="small" density="compact" @click="markAllTaskNotifsRead">
                Mark all read
              </v-btn>
            </v-card-title>
            <v-divider />
            <v-list v-if="taskNotifList.length > 0" density="compact" class="py-0" style="max-height: 400px; overflow-y: auto">
              <v-list-item
                v-for="notif in taskNotifList"
                :key="notif.id"
                :class="{ 'notif-unread': !notif.is_read }"
                class="py-2 px-3"
                @click="openTaskFromNotif(notif)"
              >
                <template #prepend>
                  <div class="notif-dot mr-3" :class="notifDotColor(notif.notification_type)" />
                </template>
                <v-list-item-title class="text-body-2" style="white-space: normal; line-height: 1.3">
                  {{ notif.title }}
                </v-list-item-title>
                <v-list-item-subtitle class="text-caption mt-1">
                  {{ formatNotifTime(notif.created_at) }}
                </v-list-item-subtitle>
                <template #append>
                  <v-icon size="16" color="grey">mdi-chevron-right</v-icon>
                </template>
              </v-list-item>
            </v-list>
            <div v-else class="text-center pa-6 text-grey">
              <v-icon size="32" color="grey-lighten-1">mdi-bell-check-outline</v-icon>
              <div class="text-body-2 mt-2">No notifications</div>
            </div>
            <v-divider />
            <div class="text-center pa-2">
              <v-btn variant="text" color="accent" size="small" @click="showTaskNotifMenu = false; $router.push('/tasks')">
                View all tasks
              </v-btn>
            </div>
          </v-card>
        </v-menu>

        <!-- Quick Task Button -->
        <v-tooltip text="Quick Task" location="bottom">
          <template #activator="{ props: tp }">
            <v-btn v-if="!mobile && hb.inHeader('quick_create')" v-bind="tp" icon class="ml-2" color="accent" :style="{ order: hb.orderOf('quick_create') + 1 }" data-hkey="quick_create" @click="showGlobalQuickTask = true">
              <img v-if="headerIcon3d('plus')" :src="headerIcon3d('plus')" class="header-icon-3d" alt="" />
              <v-icon v-else>mdi-plus-circle-outline</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        
        <!-- Mute toggle for notification sounds (desktop + mobile) -->
        <v-tooltip :text="notificationsMuted ? 'Sound alerts are OFF — click to turn on' : 'Turn off sound alerts (mute call & notification sounds)'" location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="hb.inHeader('mute')"
              icon
              class="ml-2"
              :color="notificationsMuted ? 'error' : 'secondary'"
              v-bind="props"
              :style="{ order: hb.orderOf('mute') + 1 }" data-hkey="mute"
              @click="toggleMute"
            >
              <img v-if="headerIcon3d(notificationsMuted ? 'volume-off' : 'volume')" :src="headerIcon3d(notificationsMuted ? 'volume-off' : 'volume')" class="header-icon-3d" alt="" />
              <v-icon v-else>{{ notificationsMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <!-- Notification Bell - Mobile -->
        <v-menu v-if="mobile" location="bottom end" offset="8">
          <template v-slot:activator="{ props }">
            <v-tooltip text="Notifications" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn icon v-bind="{ ...props, ...tooltipProps }" class="ml-2" color="secondary">
                  <v-badge
                    :content="pendingRegistrationsCount"
                    :model-value="pendingRegistrationsCount > 0"
                    color="error"
                    overlap
                  >
                    <img v-if="headerIcon3d('notifications')" :src="headerIcon3d('notifications')" class="header-icon-3d" alt="" />
                    <v-icon v-else>mdi-bell-outline</v-icon>
                  </v-badge>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-card min-width="350" max-height="500" rounded="lg">
            <v-card-title class="d-flex align-center pa-4">
              <v-icon class="mr-2" color="primary">mdi-bell</v-icon>
              <span class="text-h6">Notifications</span>
              <v-spacer></v-spacer>
              <v-btn icon size="small" @click="showRegistrationRequests = true">
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <div v-if="pendingRegistrationsCount === 0" class="text-center py-8">
                <v-icon size="48" color="secondary" class="mb-2">mdi-check-circle-outline</v-icon>
                <p class="text-body-2 text-secondary">No pending notifications</p>
              </div>
              <v-list v-else>
                <v-list-item
                  v-for="request in pendingRegistrations"
                  :key="request.id"
                  class="px-4"
                  @click="openRequestDetails(request)"
                  style="cursor: pointer;"
                >
                  <template v-slot:prepend>
                    <v-avatar color="accent" size="40">
                      <span class="text-white">{{ request.first_name ? request.first_name[0] : 'U' }}</span>
                    </v-avatar>
                  </template>
                  <v-list-item-title class="font-weight-medium">
                    New Registration Request
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ request.full_name || request.email || request.phone_number }}
                  </v-list-item-subtitle>
                  <template v-slot:append>
                    <v-icon size="small" color="secondary">mdi-chevron-right</v-icon>
                  </template>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-2">
              <v-btn block variant="text" @click="showRegistrationRequests = true">
                View All Requests
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        
        <!-- Notification Bell - Desktop -->
        <v-menu v-if="!mobile && hb.inHeader('notifications')" location="bottom end" offset="8">
          <template v-slot:activator="{ props }">
            <v-tooltip text="Notifications" location="bottom">
              <template #activator="{ props: tooltipProps }">
                <v-btn icon class="ml-2" color="secondary" v-bind="{ ...props, ...tooltipProps }" :style="{ order: hb.orderOf('notifications') + 1 }" data-hkey="notifications">
                  <v-badge
                    :content="totalNotificationsCount"
                    :model-value="totalNotificationsCount > 0"
                    color="error"
                    overlap
                  >
                    <img v-if="headerIcon3d('notifications')" :src="headerIcon3d('notifications')" class="header-icon-3d" alt="" />
                    <v-icon v-else>mdi-bell-outline</v-icon>
                  </v-badge>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-card width="420" min-width="420" max-width="420" max-height="600" rounded="lg" class="sysnotif-card">
            <v-card-title class="d-flex align-center pa-4">
              <v-icon class="mr-2" color="primary">mdi-bell</v-icon>
              <span class="text-h6">Notifications</span>
              <v-spacer></v-spacer>
              <v-btn icon size="small" @click="showRegistrationRequests = true">
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0" style="max-height: 450px; overflow-y: auto;">
              <!-- System Notifications Section (grouped by severity, crm2 style) -->
              <template v-if="unreadSystemGroupCount > 0">
                <div class="d-flex align-center px-4 pt-3 pb-1">
                  <v-icon size="16" class="mr-1" color="error">mdi-alert-circle</v-icon>
                  <span class="font-weight-bold text-body-2">System Alerts ({{ unreadSystemGroupCount }})</span>
                  <v-spacer />
                  <v-btn
                    size="x-small"
                    variant="text"
                    color="medium-emphasis"
                    :loading="markingAllSystemRead"
                    @click.stop="markAllSystemRead"
                  >
                    <v-icon size="14" class="mr-1">mdi-check-all</v-icon>
                    Mark all read
                  </v-btn>
                  <v-tooltip text="Notification settings" location="bottom">
                    <template #activator="{ props: tipProps }">
                      <v-btn
                        v-bind="tipProps"
                        icon
                        size="x-small"
                        variant="text"
                        color="medium-emphasis"
                        to="/settings/notifications?tab=my"
                      >
                        <v-icon size="16">mdi-cog-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-tooltip>
                </div>

                <!-- Severity filter pills -->
                <div class="d-flex flex-wrap ga-2 px-4 pb-2">
                  <v-chip size="small" :variant="systemFilter === 'all' ? 'flat' : 'outlined'"
                          :color="systemFilter === 'all' ? 'primary' : undefined"
                          @click.stop="systemFilter = 'all'">
                    All <span class="font-weight-bold ml-1">{{ systemSeverityCounts.all }}</span>
                  </v-chip>
                  <v-chip size="small" color="error" :variant="systemFilter === 'critical' ? 'flat' : 'outlined'"
                          :disabled="!systemSeverityCounts.critical"
                          @click.stop="systemFilter = 'critical'">
                    Critical <span class="font-weight-bold ml-1">{{ systemSeverityCounts.critical }}</span>
                  </v-chip>
                  <v-chip size="small" color="warning" :variant="systemFilter === 'warning' ? 'flat' : 'outlined'"
                          :disabled="!systemSeverityCounts.warning"
                          @click.stop="systemFilter = 'warning'">
                    Warning <span class="font-weight-bold ml-1">{{ systemSeverityCounts.warning }}</span>
                  </v-chip>
                  <v-chip size="small" color="success" :variant="systemFilter === 'info' ? 'flat' : 'outlined'"
                          :disabled="!systemSeverityCounts.info"
                          @click.stop="systemFilter = 'info'">
                    Info <span class="font-weight-bold ml-1">{{ systemSeverityCounts.info }}</span>
                  </v-chip>
                </div>

                <!-- Grouped rows: critical -> warning -> info -->
                <template v-for="sev in ['critical', 'warning', 'info']" :key="'sec-' + sev">
                  <template v-if="systemGroupsForSeverity(sev).length">
                    <div class="sysnotif-sec px-4 pt-2 pb-1" :class="'sev-' + sev">
                      <span class="sysnotif-sec-label">{{ sev }}</span>
                      <span class="sysnotif-sec-badge">{{ systemGroupsForSeverity(sev).length }}</span>
                    </div>
                    <div
                      v-for="group in systemGroupsForSeverity(sev)"
                      :key="'grp-' + group.key"
                      class="sysnotif-row"
                      :class="'sev-' + group.severity"
                    >
                      <span class="sysnotif-stripe"></span>
                      <v-avatar :color="getSeverityColor(group.severity)" variant="tonal" size="36" class="sysnotif-ico">
                        <v-icon :color="getSeverityColor(group.severity)" size="20">{{ getSeverityIcon(group.severity) }}</v-icon>
                      </v-avatar>
                      <div class="sysnotif-body">
                        <div class="d-flex align-start">
                          <div class="sysnotif-title flex-grow-1">{{ group.rep.title }}</div>
                          <v-btn icon size="x-small" variant="text" class="ml-1"
                                 @click.stop="markGroupRead(group)"
                                 :title="group.count > 1 ? `Got it — dismiss all ${group.count}` : 'Got it — dismiss'">
                            <v-icon size="16">mdi-check</v-icon>
                          </v-btn>
                        </div>
                        <div class="sysnotif-msg">{{ group.rep.message }}</div>
                        <div class="d-flex align-center flex-wrap ga-2 mt-2">
                          <v-chip v-if="group.count > 1" size="x-small" variant="tonal"
                                  :color="getSeverityColor(group.severity)">
                            {{ group.count }} alerts
                          </v-chip>
                          <span class="sysnotif-time">{{ timeAgo(group.rep.created_at) }}</span>
                          <v-btn
                            v-if="group.rep.link_url || group.rep.details?.link_url"
                            size="x-small"
                            variant="tonal"
                            color="primary"
                            @click.stop="handleSystemNotificationClick(group.rep)"
                          >
                            <v-icon size="14" class="mr-1">mdi-open-in-new</v-icon>
                            {{ group.rep.link_label || group.rep.details?.link_label || 'View Logs' }}
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </template>
                </template>
                <v-divider class="my-2"></v-divider>
              </template>
              
              <!-- Registration Requests Section -->
              <template v-if="pendingRegistrationsCount > 0">
                <v-list-subheader class="text-primary font-weight-bold">
                  <v-icon size="16" class="mr-1" color="primary">mdi-account-plus</v-icon>
                  Registration Requests ({{ pendingRegistrationsCount }})
                </v-list-subheader>
                <v-list density="compact">
                  <v-list-item
                    v-for="request in pendingRegistrations"
                    :key="'reg-' + request.id"
                    class="px-4"
                    @click="openRequestDetails(request)"
                    style="cursor: pointer;"
                  >
                    <template v-slot:prepend>
                      <v-avatar color="accent" size="36">
                        <span class="text-white">{{ request.first_name ? request.first_name[0] : 'U' }}</span>
                      </v-avatar>
                    </template>
                    <v-list-item-title class="font-weight-medium text-body-2">
                      New Registration Request
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption">
                      {{ request.full_name || request.email || request.phone_number }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </template>
              
              <!-- No Notifications -->
              <div v-if="totalNotificationsCount === 0" class="text-center py-8">
                <v-icon size="48" color="secondary" class="mb-2">mdi-check-circle-outline</v-icon>
                <p class="text-body-2 text-secondary">No pending notifications</p>
              </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-2">
              <v-btn variant="text" size="small" @click="showRegistrationRequests = true">
                View Requests
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn variant="text" size="small" color="primary" to="/settings/cron-jobs">
                View System Logs
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        
        <!-- Chat Button -->
        <span v-if="hb.inHeader('chat')" class="hdr-anchor ml-2" :style="{ order: hb.orderOf('chat') + 1 }" data-hkey="chat">
          <ChatButton />
        </span>
        
        </template>
        <!-- COMPACT-HEADER: desktop block is split here so the dialer below
             stays OUTSIDE both the desktop/compact branches. -->

        <!-- Phone Dialer Button V3 (Twilio Direct) — only version supported.
             ONE persistent instance for desktop + compact headers: it owns the
             in-page dialpad, the Twilio device and the dial-phone listener, so
             hiding Phone in Header Builder or resizing across 1280px only hides
             the icon (hide-trigger) and never drops a live call.
             HeaderCommDropdown (Send SMS / Send Email chevron) intentionally
             sits IMMEDIATELY after the phone icon — they read as one combined
             control. Do NOT insert anything between them. -->
        <div
          class="phone-comm-group d-inline-flex align-center"
          :class="{ 'ml-2': !isCompactHeader && hb.inHeader('dialer'), 'ml-1': isCompactHeader && showDialerV3 }"
          :style="isCompactHeader ? undefined : { order: hb.orderOf('dialer') + 1 }"
          :data-hkey="!isCompactHeader && hb.inHeader('dialer') ? 'dialer' : undefined"
        >
          <DialerButtonV3 v-if="showDialerV3" ref="dialerHostRef" :hide-trigger="!showDialerTrigger" />
          <HeaderCommDropdown v-if="!isCompactHeader && hb.inHeader('dialer')" />
        </div>

        <template v-if="!isCompactHeader">

        <!-- 2026-05-06 — Sales Call Orchestrator: bell button in header.
             Placed AFTER the phone+chevron pair so it does not split the
             SMS/Email dropdown away from the phone icon. The actual incoming-
             calls banner lives INSIDE <v-main>, not here, so it never blocks
             the search field or other header controls. -->
        <!-- Orchestrator bell is scoped to the CRM Panel (SERM 2.0) — it must NOT
             appear app-wide on unrelated pages (see isCrmPanelRoute). -->
        <span v-if="authStore.isAuthenticated && !isPortalRoute && isCrmPanelRoute && hb.inHeader('call_alerts')" class="hdr-anchor" :style="{ order: hb.orderOf('call_alerts') + 1 }" data-hkey="call_alerts">
          <CallAlertsBellButton />
        </span>

        <!-- Atomic Billing — token balance chip -->
        <span
          v-if="authStore.isAuthenticated && !isPortalRoute && canSeeTokenBalance && hb.inHeader('balance')"
          class="hdr-anchor ml-2"
          :style="{ order: hb.orderOf('balance') + 1 }" data-hkey="balance"
        >
          <TokenBalanceChip />
        </span>
        
        <!-- AP-7-00340: per-company custom Oliver badge (uploaded in Company
             Settings). When set it replaces the 3D robot with the company's own
             icon; still clicks through to the voice assistant. -->
        <button
          v-if="oliverBadgeIconUrl && hb.inHeader('ai')"
          type="button"
          class="oliver-badge-btn ml-2"
          :class="`is-${voiceStatus}`"
          aria-label="Open Oliver AI Assistant"
          :style="{ order: hb.orderOf('ai') + 1 }" data-hkey="ai"
          @click="voiceAssistantRef?.toggleConversation()"
        >
          <v-avatar :size="40">
            <v-img :src="oliverBadgeIconUrl" cover alt="Oliver">
              <template #placeholder>
                <v-icon color="white" size="22">mdi-robot-outline</v-icon>
              </template>
            </v-img>
          </v-avatar>
        </button>
        <!-- Mini 3D robot avatar (head->hands, idle life + hover wave/sparks).
             Click drives the voice assistant (replaces the microphone).
             :status drives the visual state ring (connecting/listening/speaking).
             DEFAULT for every company (no custom icon uploaded). -->
        <RobotMiniAvatar
          v-else-if="hb.inHeader('ai')"
          class="ml-2"
          :width="40"
          :height="40"
          :status="voiceStatus"
          :idle-tricks="robotAnimationEnabled"
          :style="{ order: hb.orderOf('ai') + 1 }" data-hkey="ai"
          @activate="voiceAssistantRef?.toggleConversation()"
        />

        <!-- Divider objects (Header Builder). Part of the header row via CSS
             order; in edit mode the overlay frames them for drag/remove. -->
        <span
          v-for="sp in hb.spacerItems"
          :key="'hdr-sp-' + sp.id"
          class="hdr-spacer"
          :style="{ order: sp.order + 1, width: (sp.width || 16) + 'px' }"
          :data-hkey="'spacer:' + sp.id"
        ></span>

        </template>
        <!-- COMPACT-HEADER END (desktop block) -->

        <!-- COMPACT-HEADER BEGIN (compact block: viewport <1280px) -->
        <template v-else>
          <!-- Phone (always visible, primary action) — rendered by the single
               persistent DialerButtonV3 above (showDialerTrigger). -->

          <!-- AP-7-00340: per-company custom Oliver badge (compact header) -->
          <button
            v-if="oliverBadgeIconUrl"
            type="button"
            class="oliver-badge-btn ml-1"
            :class="`is-${voiceStatus}`"
            aria-label="Open Oliver AI Assistant"
            @click="voiceAssistantRef?.toggleConversation()"
          >
            <v-avatar :size="40">
              <v-img :src="oliverBadgeIconUrl" cover alt="Oliver">
                <template #placeholder>
                  <v-icon color="white" size="22">mdi-robot-outline</v-icon>
                </template>
              </v-img>
            </v-avatar>
          </button>
          <!-- Mini 3D robot avatar (compact header) — drives the voice assistant -->
          <RobotMiniAvatar
            v-else
            class="ml-1"
            :width="40"
            :height="40"
            :status="voiceStatus"
            :idle-tricks="robotAnimationEnabled"
            @activate="voiceAssistantRef?.toggleConversation()"
          />

          <!-- "⋯" More menu — everything else is accessible from here -->
          <v-menu
            v-model="showCompactMoreMenu"
            location="bottom end"
            offset="8"
            :close-on-content-click="false"
            min-width="280"
            max-width="320"
          >
            <template #activator="{ props: moreProps }">
              <v-btn
                v-bind="moreProps"
                icon
                size="small"
                color="secondary"
                class="ml-1"
                aria-label="More actions"
              >
                <v-badge
                  :model-value="compactUnreadTotal > 0"
                  dot
                  color="error"
                  offset-x="2"
                  offset-y="2"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-badge>
              </v-btn>
            </template>

            <v-card rounded="lg" elevation="8" class="compact-menu-card">
              <v-list density="compact" nav class="py-1">
                <!-- Notifications section -->
                <v-list-item
                  class="compact-item"
                  :class="{ 'compact-item--unread': taskNotifCount > 0 }"
                  @click="showCompactMoreMenu = false; $router.push('/tasks')"
                >
                  <template #prepend>
                    <v-icon size="22" color="primary">mdi-clipboard-check-outline</v-icon>
                  </template>
                  <v-list-item-title class="compact-label">Tasks</v-list-item-title>
                  <template #append>
                    <v-chip
                      v-if="taskNotifCount > 0"
                      size="x-small"
                      color="error"
                      variant="flat"
                    >
                      {{ taskNotifCount > 99 ? '99+' : taskNotifCount }}
                    </v-chip>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="canSeePartsNotifications"
                  class="compact-item"
                  :class="{ 'compact-item--unread': partsNotifCount > 0 }"
                  @click="showCompactMoreMenu = false; $router.push('/operations/parts-manager')"
                >
                  <template #prepend>
                    <v-icon size="22" color="warning">mdi-wrench-outline</v-icon>
                  </template>
                  <v-list-item-title class="compact-label">Parts Requests</v-list-item-title>
                  <template #append>
                    <v-chip
                      v-if="partsNotifCount > 0"
                      size="x-small"
                      color="warning"
                      variant="flat"
                    >
                      {{ partsNotifCount > 99 ? '99+' : partsNotifCount }}
                    </v-chip>
                  </template>
                </v-list-item>

                <v-list-item
                  class="compact-item"
                  :class="{ 'compact-item--unread': pendingRegistrationsCount > 0 }"
                  @click="showCompactMoreMenu = false; showRegistrationRequests = true"
                >
                  <template #prepend>
                    <v-icon size="22" color="info">mdi-bell-outline</v-icon>
                  </template>
                  <v-list-item-title class="compact-label">Registration Requests</v-list-item-title>
                  <template #append>
                    <v-chip
                      v-if="pendingRegistrationsCount > 0"
                      size="x-small"
                      color="error"
                      variant="flat"
                    >
                      {{ pendingRegistrationsCount > 99 ? '99+' : pendingRegistrationsCount }}
                    </v-chip>
                  </template>
                </v-list-item>

                <v-divider class="my-1" />

                <!-- Actions section -->
                <v-list-item
                  class="compact-item"
                  @click="showCompactMoreMenu = false; $router.push('/chat')"
                >
                  <template #prepend>
                    <v-icon size="22" color="primary">mdi-chat-outline</v-icon>
                  </template>
                  <v-list-item-title class="compact-label">Chat</v-list-item-title>
                </v-list-item>

                <v-list-item
                  v-if="authStore.hasPermission('settings.administration')"
                  class="compact-item"
                  @click="showCompactMoreMenu = false; $router.push('/ideas')"
                >
                  <template #prepend>
                    <v-icon size="22" color="warning">mdi-lightbulb-on-outline</v-icon>
                  </template>
                  <v-list-item-title class="compact-label">Ideas</v-list-item-title>
                </v-list-item>

                <!-- Presence inline status (current workday status + start/end toggle) -->
                <v-list-item class="compact-item" @click.stop>
                  <template #prepend>
                    <v-icon size="22" :color="presenceWorkdayStarted ? presenceStatusColor : 'grey'">
                      mdi-circle
                    </v-icon>
                  </template>
                  <v-list-item-title class="compact-label">
                    Presence
                  </v-list-item-title>
                  <template #append>
                    <div class="d-flex align-center ga-2">
                      <span class="text-caption text-grey">
                        {{ presenceWorkdayStarted ? `${presenceStatusLabel} · ${currentStatusTimer}` : 'Not started' }}
                      </span>
                      <v-btn
                        size="x-small"
                        :color="presenceWorkdayStarted ? 'error' : 'accent'"
                        variant="tonal"
                        @click="toggleWorkday(); showCompactMoreMenu = false"
                      >
                        {{ presenceWorkdayStarted ? 'End' : 'Start' }}
                      </v-btn>
                    </div>
                  </template>
                </v-list-item>

                <v-divider class="my-1" />

                <!-- Utility section -->
                <v-list-item
                  class="compact-item"
                  @click.stop="toggleMute"
                >
                  <template #prepend>
                    <v-icon size="22" :color="notificationsMuted ? 'error' : 'primary'">
                      {{ notificationsMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}
                    </v-icon>
                  </template>
                  <v-list-item-title class="compact-label">
                    {{ notificationsMuted ? 'Turn on sound alerts' : 'Turn off sound alerts' }}
                  </v-list-item-title>
                </v-list-item>

              </v-list>
            </v-card>
          </v-menu>
        </template>
        <!-- COMPACT-HEADER END (compact block) -->

        <!-- HEADER-BUILDER "⋯" overflow (desktop, New Design preset only).
             Holds the simple actions an admin/user chose to tuck away. Rich
             components (dialer/notifications/etc.) are header-or-hidden only. -->
        <v-menu v-if="!isCompactHeader && hb.hasMoreItems()" v-model="showHeaderMoreMenu" location="bottom end" offset="8">
          <template v-slot:activator="{ props }">
            <v-tooltip text="More" location="bottom">
              <template #activator="{ props: tp }">
                <v-btn icon class="ml-2" color="secondary" v-bind="{ ...props, ...tp }" :style="{ order: 900 }">
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </template>
          <v-list class="py-1" rounded="lg" elevation="2" min-width="220">
            <v-list-item v-if="hb.inMore('quick_create')" @click="showGlobalQuickTask = true; showHeaderMoreMenu = false">
              <template #prepend><v-icon size="20" color="accent">mdi-plus-circle-outline</v-icon></template>
              <v-list-item-title>{{ t('headerBuilder.items.quick_create') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="hb.inMore('mute')" @click="toggleMute(); showHeaderMoreMenu = false">
              <template #prepend><v-icon size="20" :color="notificationsMuted ? 'error' : 'secondary'">{{ notificationsMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon></template>
              <v-list-item-title>{{ notificationsMuted ? 'Turn on sound alerts' : t('headerBuilder.items.mute') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="hb.inMore('chat')" @click="$router.push('/chat'); showHeaderMoreMenu = false">
              <template #prepend><v-icon size="20" color="secondary">mdi-chat-outline</v-icon></template>
              <v-list-item-title>{{ t('headerBuilder.items.chat') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="hb.inMore('ai')" @click="voiceAssistantRef?.toggleConversation(); showHeaderMoreMenu = false">
              <template #prepend><v-icon size="20" color="secondary">mdi-robot-outline</v-icon></template>
              <v-list-item-title>{{ t('headerBuilder.items.ai') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- User Menu -->
        <v-menu :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props" class="ml-4 avatar-btn" :style="{ order: 999 }">
              <v-avatar color="accent" size="40">
                <v-img
                  v-if="authStore.user?.avatar_url"
                  :src="authStore.user.avatar_url"
                  :alt="authStore.user?.first_name || 'User'"
                  cover
                />
                <span v-else class="text-white text-h6">{{ authStore.user?.first_name ? authStore.user.first_name[0] : 'U' }}</span>
              </v-avatar>
            </v-btn>
          </template>
          <v-list class="py-0" rounded="lg" elevation="2">
            <!-- COMPANY-BRAND BEGIN — which company the user is working in -->
            <div class="company-band d-flex align-center ga-2 pa-3">
              <img v-if="companyLogo" :src="companyLogo" :alt="companyName" class="company-band-logo" />
              <v-icon v-else color="primary" size="24">mdi-domain</v-icon>
              <span class="company-band-name">{{ companyName || 'N/A' }}</span>
            </div>
            <v-divider></v-divider>
            <!-- COMPANY-BRAND END -->
            <v-list-item class="text-body-2 py-3">
              <template v-slot:prepend>
                <!-- Editable profile photo: click to open the avatar editor -->
                <div class="profile-avatar-edit mr-3" @click.stop="openAvatarEditor">
                  <v-avatar color="accent" size="52">
                    <v-img
                      v-if="authStore.user?.avatar_url"
                      :src="authStore.user.avatar_url"
                      :alt="authStore.user?.first_name || 'User'"
                      cover
                    />
                    <span v-else class="text-white text-h6">{{ authStore.user?.first_name ? authStore.user.first_name[0] : 'U' }}</span>
                  </v-avatar>
                  <div class="profile-avatar-edit__overlay">
                    <v-icon size="16" color="white">mdi-camera</v-icon>
                  </div>
                </div>
              </template>
              <v-list-item-title class="font-weight-medium">{{ getUserFullName() }}</v-list-item-title>
              <v-list-item-subtitle>{{ authStore.user?.email || authStore.user?.phone_number || 'N/A' }}</v-list-item-subtitle>
            </v-list-item>
            
            <v-divider class="my-2"></v-divider>
            
            <v-list-item v-if="userTeamName" prepend-icon="mdi-account-group" class="text-body-2">
              <v-list-item-title>Team: {{ userTeamName }}</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-domain" class="text-body-2">
              <v-list-item-title>Domain: {{ authStore.domain?.name || 'N/A' }}</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-clock-outline" class="text-body-2">
              <v-list-item-title>Timezone: {{ getTimezoneLabel(authStore.user?.timezone) }}</v-list-item-title>
            </v-list-item>
            
            <v-divider class="my-2"></v-divider>

            <!-- APP-DARK-THEME: app theme toggle (light / dark, persists via /account/my-theme API).
                 Replaced the old sidebar-only "Menu theme" toggle (2026-06-12). -->
            <v-list-item v-if="allowDarkToggle" class="text-body-2">
              <template v-slot:prepend>
                <v-icon size="20">mdi-theme-light-dark</v-icon>
              </template>
              <v-list-item-title>Theme</v-list-item-title>
              <template v-slot:append>
                <v-btn-toggle
                  :model-value="appIsDark ? 'dark' : 'light'"
                  mandatory
                  density="comfortable"
                  variant="outlined"
                  divided
                  color="accent"
                  :disabled="themeToggleBusy"
                  @update:model-value="setAppTheme"
                >
                  <v-btn value="light" size="small">
                    <v-icon>mdi-white-balance-sunny</v-icon>
                    <v-tooltip activator="parent" location="bottom">Light</v-tooltip>
                  </v-btn>
                  <v-btn value="dark" size="small">
                    <v-icon>mdi-weather-night</v-icon>
                    <v-tooltip activator="parent" location="bottom">Dark</v-tooltip>
                  </v-btn>
                </v-btn-toggle>
              </template>
            </v-list-item>

            <v-divider v-if="allowDarkToggle" class="my-2"></v-divider>

            <!-- TICK-73: inbound SMS/call popups — per-user toggle (dispatchers only) -->
            <template v-if="canSeeInboundPopups">
              <v-list-item class="text-body-2">
                <template v-slot:prepend>
                  <v-icon size="20">mdi-bell-ring-outline</v-icon>
                </template>
                <v-list-item-title>Inbound popups</v-list-item-title>
                <template v-slot:append>
                  <v-switch
                    :model-value="inboundPopupsEnabled"
                    color="accent"
                    density="compact"
                    hide-details
                    inset
                    @update:model-value="toggleInboundPopups"
                    @click.stop
                  />
                </template>
              </v-list-item>
              <v-list-item v-if="inboundPopupsEnabled" class="text-body-2">
                <template v-slot:prepend>
                  <v-icon size="20">{{ inboundPopupsSound ? 'mdi-volume-high' : 'mdi-volume-off' }}</v-icon>
                </template>
                <v-list-item-title>Popup sound</v-list-item-title>
                <template v-slot:append>
                  <v-switch
                    :model-value="inboundPopupsSound"
                    color="accent"
                    density="compact"
                    hide-details
                    inset
                    @update:model-value="toggleInboundPopupsSound"
                    @click.stop
                  />
                </template>
              </v-list-item>
              <v-divider class="my-2"></v-divider>
            </template>

            <!-- Per-user visibility of the two bottom-right floating widgets.
                 Company-enabled for everyone with the permission; each user may
                 hide them for themselves. Default ON. -->
            <template v-if="canToggleSupportWidget || canToggleAssistantWidget">
              <v-list-item v-if="canToggleSupportWidget" class="text-body-2">
                <template v-slot:prepend>
                  <v-icon size="20">mdi-lightbulb-on-outline</v-icon>
                </template>
                <v-list-item-title>Support widget</v-list-item-title>
                <template v-slot:append>
                  <v-switch
                    :model-value="showSupportWidget"
                    color="accent"
                    density="compact"
                    hide-details
                    inset
                    @update:model-value="setShowSupportWidget"
                    @click.stop
                  />
                </template>
              </v-list-item>
              <v-list-item v-if="canToggleAssistantWidget" class="text-body-2">
                <template v-slot:prepend>
                  <v-icon size="20">mdi-robot-outline</v-icon>
                </template>
                <v-list-item-title>Assistant robot widget</v-list-item-title>
                <template v-slot:append>
                  <v-switch
                    :model-value="showAssistantWidget"
                    color="accent"
                    density="compact"
                    hide-details
                    inset
                    @update:model-value="setShowAssistantWidget"
                    @click.stop
                  />
                </template>
              </v-list-item>
              <v-divider class="my-2"></v-divider>
            </template>

            <!-- Platform God Mode — rendered ONLY for the platform superadmin
                 (users.platform_access). Invisible to everyone else. -->
            <template v-if="authStore.user?.platform_access">
              <v-list-item
                prepend-icon="mdi-shield-crown"
                class="text-error"
                @click="router.push('/platform/god')"
              >
                <v-list-item-title>{{ $t('platform.god.menu_item') }}</v-list-item-title>
              </v-list-item>
              <v-divider class="my-2"></v-divider>
            </template>
            
            <v-list-item @click="logout" prepend-icon="mdi-logout" class="text-error">
              <v-list-item-title>Logout</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>
    </template>

    <!-- On-header live edit surface (Header Builder). Fixed strip under the
         app-bar; only visible while editing under the New Design preset. -->
    <HeaderEditOverlay />

    <!-- Main Content Area -->
    <v-main>
      <VersionUpdateBanner />
      <!-- 2026-05-06 — Incoming Calls Banner (RETIRED as an app-wide surface).
           The orchestrator incoming call now surfaces ONLY inside the CRM
           Panel (SERM 2.0) as its own in-page card — never as a banner that
           pops on unrelated pages where the operator cannot tell what is
           ringing or where the sound is from. We keep the component mounted
           only within the CRM panel context (isCrmPanelRoute), where it
           self-hides anyway, so there is no app-wide strip. -->
      <IncomingCallsBanner v-if="showLayout && isCrmPanelRoute" />
      <!-- TICK-73: global inbound SMS/call popups (all dispatchers) -->
      <InboundCommsPopups v-if="showLayout && canSeeInboundPopups" />
      <router-view />
    </v-main>

    <!-- Company Onboarding nudge — blinking corner badge for admins while the
         business is not fully set up. Click / hover opens the checklist; the
         Settings > Company Onboarding menu item stays regardless.
         Hidden in the technician mobile app: onboarding is an office/admin task,
         it must never overlay the technicians' mobile workflow. -->
    <OnboardingNudge v-if="authStore.isAuthenticated && !isPortalRoute && !isDialerWindow && !isTechnicianRoute && !isPublicSignRoute" />
    
    <!-- Profile photo editor (avatar crop/zoom) — opened from the header menu -->
    <AvatarEditorDialog
      v-model="showAvatarEditor"
      :current-avatar="authStore.user?.avatar_url"
    />

    <!-- Registration Requests Dialog -->
    <!-- Not persistent: this is a read-only viewer, so outside-click / Esc must
         simply close it instead of playing the "shake" animation (TICK-00135). -->
    <v-dialog v-model="showRegistrationRequests" max-width="1200" scrollable>
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center pa-4">
          <v-icon class="mr-2" color="primary">mdi-account-plus</v-icon>
          <span class="text-h6">Registration Requests</span>
          <v-spacer></v-spacer>
          <v-btn icon size="small" @click="showRegistrationRequests = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <RegistrationRequestsView />
        </v-card-text>
      </v-card>
    </v-dialog>
    
    <!-- Request Details Dialog (from notification) -->
    <v-dialog v-model="showRequestDetailsDialog" max-width="600" persistent>
      <v-card rounded="lg" v-if="selectedRequestForDetails">
        <v-card-title class="d-flex align-center pa-4">
          <v-icon class="mr-2" color="primary">mdi-account-details</v-icon>
          <span class="text-h6">Registration Request Details</span>
          <v-spacer></v-spacer>
          <v-btn icon size="small" @click="showRequestDetailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <v-row>
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">Full Name</div>
                <div class="text-body-1 font-weight-medium">
                  {{ selectedRequestForDetails.full_name || 'Not provided' }}
                </div>
              </div>
              
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">First Name</div>
                <div class="text-body-1">
                  {{ selectedRequestForDetails.first_name || 'Not provided' }}
                </div>
              </div>
              
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">Last Name</div>
                <div class="text-body-1">
                  {{ selectedRequestForDetails.last_name || 'Not provided' }}
                </div>
              </div>
            </v-col>
            
            <v-col cols="12" md="6">
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">Phone Number</div>
                <div class="text-body-1">
                  {{ selectedRequestForDetails.phone_number }}
                </div>
              </div>
              
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">Email</div>
                <div class="text-body-1">
                  {{ selectedRequestForDetails.email }}
                </div>
              </div>
              
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">Domain</div>
                <div class="text-body-1">
                  {{ selectedRequestForDetails.domain_name || 'N/A' }}
                </div>
              </div>
              
              <div class="mb-3">
                <div class="text-caption text-secondary mb-1">Requested At</div>
                <div class="text-body-1">
                  {{ formatDate(selectedRequestForDetails.registration_requested_at) }}
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showRequestDetailsDialog = false; showRegistrationRequests = true">
            View All Requests
          </v-btn>
          <v-btn color="accent" variant="flat" @click="showRequestDetailsDialog = false; showRegistrationRequests = true">
            Manage Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Global Quick Task Dialog (not on portal routes, not on the standalone
         public e-sign page) -->
    <template v-if="!isPortalRoute && !isPublicSignRoute">
      <QuickTaskDialog
        v-model="showGlobalQuickTask"
        @created="onGlobalQuickTaskCreated"
      />
      <GlobalNotifications v-if="!isTechnicianRoute || route.name === 'Login'" />
      <CustomerCardDialog />
      <!-- v3.5 - global Part Card dialog opened from header search -->
      <PartDetailDialog
        v-model="partCardDialog.isOpen.value"
        :part-id="partCardDialog.partId.value"
      />
      <!-- v2: Real-time Sales alerts toaster (PDF §А.1/А.6) -->
      <SalesAlertToaster v-if="authStore.isAuthenticated && authStore.hasPermission('sales.view')" />
      <!-- Atomic Billing — global insufficient balance dialog -->
      <InsufficientBalanceDialog v-if="authStore.isAuthenticated" />
      <!-- Atomic Billing — global self-service top-up dialog (Flow A) -->
      <TopUpDialog v-if="authStore.isAuthenticated" />

      <!-- Phase 4.5 — Switch context dialog for incoming calls with unsaved data -->
      <SwitchContextDialog v-if="authStore.isAuthenticated && !isPortalRoute" />

      <!-- In-app visual annotation: click any element -> create a support ticket.
           Desktop-only for now: hidden on mobile screens and in the technician
           app so it never blocks the technicians' mobile workflow. -->
      <AnnotationOverlay
        v-if="authStore.isAuthenticated && !isPortalRoute && !isTechnicianRoute && !mobile && authStore.hasPermission('support.annotate') && showSupportWidget"
        :can-annotate="authStore.hasPermission('support.annotate')"
      />

      <!-- TICK-00022: floating Oliver chat widget (bottom-right, under the
           lightbulb). Lives here (outside router-view), so the conversation
           survives page navigation. Toggled per company in Company Settings. -->
      <OliverChatWidget
        v-if="authStore.isAuthenticated && !isPortalRoute && !isTechnicianRoute && !isDialerWindow && !isMessagingRoute && showAssistantWidget"
        :enabled="oliverWidgetEnabled"
        :icon-url="oliverBadgeIconUrl"
        @voice="voiceAssistantRef?.toggleConversation()"
      />
    </template>

    <!-- Cookie consent — shown on all routes incl. customer portal & technician app -->
    <CookieConsent />
  </v-app>
</template>

<style>
/* UNIFIED FIGMA HEADER (FSJ-6-00059 #23): the approved Figma header uses the
   Inter Tight typeface. Imported here (@import must stay at the very top of the
   stylesheet) so the global top bar can adopt it without waiting for the CRM 2
   panel to mount. */
@import url('https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700;800&display=swap');

.notif-unread {
  background: #f0f7ff !important;
}

/* AP-7-00340: per-company custom Oliver badge button in the office top bar.
   Circular clickable image that replaces the 3D robot avatar when a company
   uploads its own Oliver icon. Status ring mirrors the 3D avatar's meaning:
   idle/connecting = brand ring, listening = red, speaking = accent. */
.oliver-badge-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  vertical-align: middle;
  transition: transform 0.15s ease, box-shadow 0.25s ease;
}
.oliver-badge-btn:hover {
  transform: scale(1.06);
}
.oliver-badge-btn.is-idle,
.oliver-badge-btn.is-connecting {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.45);
}
:root[data-theme="dark"] .oliver-badge-btn.is-idle,
:root[data-theme="dark"] .oliver-badge-btn.is-connecting {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-on-surface), 0.35);
}
.oliver-badge-btn.is-listening {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-error), 0.85), 0 0 8px 2px rgba(var(--v-theme-error), 0.55);
}
.oliver-badge-btn.is-speaking {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-accent), 1), 0 0 10px 3px rgba(var(--v-theme-accent), 0.8);
}

/* APP-DARK-THEME BEGIN (2026-06-12) — dark-mode overrides for the global
   layout. Scoped to :root[data-theme="dark"] (set by stores/branding.js), so
   the light theme is byte-for-byte unchanged. */
.app-top-bar {
  border-bottom: 1px solid var(--ui-border, rgba(0, 0, 0, 0.08)) !important;
}

/* ============================================================================
   UNIFIED FIGMA HEADER (FSJ-6-00059 #23)
   Restyle ONLY the visual language of the global top bar to the approved Figma
   header (Inter Tight, white surface, soft border, rounded "pill" search, neat
   neutral Availability pill, clean rounded icon buttons, round avatar).
   NO structure is removed: Global Search, Availability/Presence, Notifications,
   Quick Actions, Dialer/Comm, AI, User Menu and the Header Builder slots
   (order / data-hkey / spacers / edit overlay) all keep working unchanged.
   Mirrors the --crm2-* design tokens (see CrmPanel2View.vue). ======== */
.app-top-bar,
.app-top-bar .v-toolbar__content {
  font-family: 'Inter Tight', 'Inter', -apple-system, BlinkMacSystemFont,
    'Segoe UI', Roboto, sans-serif;
}

/* Left brand: keep the company logo/switcher (a required control) but present
   the logo as the rounded tile from the Figma header and the name in the
   Figma weight/typeface. */
.app-top-bar .app-company-logo {
  border-radius: 10px;
}
.app-top-bar .app-company-name {
  font-family: 'Inter Tight', 'Inter', sans-serif;
  letter-spacing: -0.01em;
}

/* Availability / Presence pill → the neutral, rounded "• Available" chip from
   Figma. The status COLOUR stays on the leading dot + text label so meaning is
   never conveyed by shape alone. */
.app-top-bar .presence-pill.v-btn {
  background: var(--crm2-chip-bg, #f9f9fa) !important;
  border: 1px solid var(--crm2-border, #e3e5ea);
  color: rgb(var(--v-theme-on-surface, 31, 41, 55)) !important;
  box-shadow: none;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 500;
}
.app-top-bar .presence-pill.v-btn:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}

/* Icon action buttons → clean, rounded, monochrome-friendly hit targets that
   match the Figma icon row. Colours/semantics of the glyphs themselves are
   left to the existing per-button logic (status badges, mute = error, etc.). */
.app-top-bar .v-btn.v-btn--icon {
  border-radius: 12px;
}
.app-top-bar .v-btn.v-btn--icon:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* Round user avatar with a subtle ring, as in the Figma header. */
.app-top-bar .avatar-btn .v-avatar {
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.04);
}

/* HEADER-BUILDER edit mode: outline the live header so it is obvious the bar is
   being edited (the framed drag controls sit in the strip right below it). */
.app-top-bar--editing {
  outline: 2px dashed rgba(77, 212, 172, 0.9);
  outline-offset: -3px;
  background: rgba(77, 212, 172, 0.06) !important;
}
/* HEADER-BUILDER: measurable wrapper for component-based header controls
   (search / chat / call-alerts / balance) whose root is a fragment or menu and
   therefore cannot carry data-hkey directly. display:inline-flex keeps them a
   single flex child that the edit overlay can frame and drag. */
.hdr-anchor { display: inline-flex; align-items: center; }
/* HEADER-BUILDER: divider/spacer object inserted between header controls. The
   padding creates the gap; the line is the visible divider. */
/* Width is driven inline by the divider's configured px (Header Builder);
   the 1px line stays centered inside that width. */
.hdr-spacer { display: inline-flex; align-items: center; justify-content: center; padding: 0; }
.hdr-spacer::before {
  content: '';
  display: block;
  width: 1px;
  height: 24px;
  background: var(--ui-border, rgba(0, 0, 0, 0.14));
}
/* PREMIUM-SKIN HEADER ICONS (2026-06-14): floating 3D glyphs inside top-bar buttons */
.header-icon-3d {
  width: 26px;
  height: 26px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.18));
  transition: transform 0.15s ease;
}
.app-top-bar .v-btn:hover .header-icon-3d {
  transform: translateY(-1px) scale(1.08);
}
/* NEW DESIGN header↔menu parity (2026-09-22): in the New Design appearance the
   LEFT MENU renders clean two-tone icons in a single dark-ink color on the
   shared `surface`, but the top-bar action buttons kept their per-button tints
   (accent = the theme's violet, secondary, primary …) so the header looked like
   a different, colourful design. The New Design icon SET has no header glyphs
   (only the 12 menu-module icons), so we cannot reuse the raster set up here —
   instead we unify the header action icons to the SAME ink the menu already
   uses (`--v-theme-on-surface`), introducing NO new colour. Scoped to
   `data-icon-style="newdesign"` (set on <html> by stores/branding.js) so every
   other appearance is byte-for-byte unchanged. Icon-only buttons only
   (`.v-btn--icon`), so the presence pill and count badges keep their status
   colours. On a dark surface `on-surface` is light, matching the dark menu. */
html[data-icon-style="newdesign"] .app-top-bar .v-btn--icon .v-icon {
  color: rgb(var(--v-theme-on-surface)) !important;
}
/* NEW DESIGN: the header user avatar used `accent` as its background, which is
   violet in New Design — a lone violet blob in an otherwise graphite header.
   Neutralise it to the menu ink (`primary`, graphite in New Design) so the
   header reads as one design. Scoped to the header + newdesign only; the white
   initial stays legible. Avatars inside dropdown overlays are left untouched. */
html[data-icon-style="newdesign"] .app-top-bar .v-avatar.bg-accent {
  background-color: rgb(var(--v-theme-primary)) !important;
}
:root[data-theme="dark"] .notif-unread {
  background: var(--status-info-bg, rgba(96, 165, 250, 0.16)) !important;
}
/* Active sidebar items: white pill → accent-tinted pill in dark mode */
:root[data-theme="dark"] .sidebar-item-active,
:root[data-theme="dark"] .sidebar-item-route-active {
  background-color: rgba(var(--v-theme-accent, 77, 212, 172), 0.18) !important;
}
:root[data-theme="dark"] .sidebar-item-active .v-icon,
:root[data-theme="dark"] .sidebar-item-route-active .v-icon,
:root[data-theme="dark"] .sidebar-item-route-active .custom-icon {
  color: rgb(var(--v-theme-accent, 77, 212, 172)) !important;
}
:root[data-theme="dark"] .sidebar-item-active .sidebar-item-label,
:root[data-theme="dark"] .sidebar-item-route-active .sidebar-item-label {
  color: rgb(var(--v-theme-on-surface, 241, 245, 249)) !important;
}
:root[data-theme="dark"] .sidebar-item:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
/* APP-DARK-THEME END */
.notif-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-teal { background: #4dd4ac; }
.dot-orange { background: #ff9800; }
.dot-green { background: #4caf50; }
.dot-blue { background: #2196f3; }
.dot-red { background: #ef4444; }

/* System notification messages must show in full — Vuetify list subtitles
   clamp to a single line with an ellipsis by default, which truncated the
   alert text. Allow the message to wrap over multiple lines instead. */
.notif-message-wrap {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: clip !important;
  line-height: 1.3;
  -webkit-line-clamp: unset !important;
  -webkit-box-orient: unset !important;
}

/* System alerts — grouped by severity, crm2 design language.
   Exact colours taken 1:1 from the crm2 Figma priority chips:
   critical #ff4646 (High), warning #f3a21f (Medium); info uses the brand
   success green #4dd4ac (Figma has no green priority token) with the same
   chip recipe (full-colour text/icon + ~10% tint + soft border). */
.sysnotif-sec {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sysnotif-sec-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.sysnotif-sec-badge {
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
}
.sysnotif-sec.sev-critical .sysnotif-sec-label { color: #ff4646; }
.sysnotif-sec.sev-critical .sysnotif-sec-badge { color: #ff4646; background: rgba(255, 70, 70, 0.10); }
.sysnotif-sec.sev-warning  .sysnotif-sec-label { color: #d98407; }
.sysnotif-sec.sev-warning  .sysnotif-sec-badge { color: #d98407; background: rgba(243, 162, 31, 0.10); }
.sysnotif-sec.sev-info     .sysnotif-sec-label { color: #1fa87f; }
.sysnotif-sec.sev-info     .sysnotif-sec-badge { color: #1fa87f; background: rgba(77, 212, 172, 0.14); }

.sysnotif-row {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 10px 16px 10px 20px;
}
.sysnotif-row + .sysnotif-row { border-top: 1px solid #f1f2f4; }
.sysnotif-stripe {
  position: absolute;
  left: 8px;
  top: 12px;
  bottom: 12px;
  width: 3px;
  border-radius: 3px;
}
.sysnotif-row.sev-critical .sysnotif-stripe { background: #ff4646; }
.sysnotif-row.sev-warning  .sysnotif-stripe { background: #f3a21f; }
.sysnotif-row.sev-info     .sysnotif-stripe { background: #4dd4ac; }
.sysnotif-ico { flex: none; }
.sysnotif-body { flex: 1; min-width: 0; }
.sysnotif-title {
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.3;
  color: rgb(var(--v-theme-on-surface));
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.sysnotif-msg {
  font-size: 12.5px;
  line-height: 1.35;
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  margin-top: 2px;
}
/* Keep the system-alerts dropdown compact regardless of message length.
   A v-menu overlay is absolutely positioned, so without a width cap it
   shrink-to-fits to the viewport when a line's max-content is huge. */
.sysnotif-card { width: 420px; }
.sysnotif-time {
  font-size: 11px;
  color: #b4b4b4;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  width: 100%;
  overscroll-behavior: none;
}

html {
  touch-action: manipulation;
  -ms-touch-action: manipulation;
  -webkit-text-size-adjust: 100%;
}

.v-main {
  background: rgb(var(--v-theme-background, 245, 247, 250));
}

/* Desktop Sidebar Styles */
/* Override Vuetify's navigation drawer content wrapper to prevent double-scroll */
.v-navigation-drawer--permanent :deep(.v-navigation-drawer__content) {
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  /* Small 5px breathing room above the brand mark, keep bottom room */
  padding: 5px 0 16px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
}
.sidebar-content::-webkit-scrollbar {
  display: none;
}

.sidebar-logo {
  display: flex;
  justify-content: center;
  /* Pin the brand mark to the top border of the drawer (P23) */
  align-items: flex-start;
  height: 48px;
  /* +10px breathing room below the brand mark (was 8px) */
  margin-bottom: 18px;
  flex-shrink: 0;
  cursor: pointer;
  transition: transform 0.15s ease, filter 0.15s ease;
}
.sidebar-logo:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
}
.sidebar-logo:active {
  transform: translateY(0) scale(0.96);
}
.sidebar-logo:focus-visible {
  outline: 2px solid rgba(var(--v-theme-accent), 0.6);
  outline-offset: 4px;
  border-radius: 10px;
}

.sidebar-logo-img {
  width: 38px;
  height: 38px;
  object-fit: contain;
  display: block;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
}

.sidebar-menu {
  flex: 1 0 auto;
  /* Nudge the whole menu (Quick Access lightning + items) down a few px */
  padding: 7px 8px 0;
}

.sidebar-bottom {
  padding: 0 8px;
  flex-shrink: 0;
}

.sidebar-scroll-hint {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.3));
  position: sticky;
  bottom: 0;
  z-index: 1;
  animation: pulse-hint 1.5s ease-in-out infinite;
}
.sidebar-scroll-hint:hover .v-icon {
  opacity: 1 !important;
}
@keyframes pulse-hint {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.sidebar-item {
  margin-bottom: 4px;
  border-radius: 8px !important;
  min-height: 40px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sidebar-item:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.sidebar-item-active {
  background-color: white !important;
}

.sidebar-item-active .v-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

/* SIDEBAR-PUSH-LAYOUT BEGIN (2026-06-03) — current-route module highlight.
   The module that the currently open page belongs to is highlighted with a
   white background (same look as the opened-submenu module), so it is always
   clear which module you are in. Shown only when the submenu is NOT open on
   this module (the open one already uses .sidebar-item-active). */
.sidebar-item-route-active {
  background-color: white !important;
}
.sidebar-item-route-active .v-icon,
.sidebar-item-route-active .custom-icon,
.sidebar-item-route-active .sidebar-item-label {
  color: rgb(var(--v-theme-primary)) !important;
}
/* SIDEBAR-PUSH-LAYOUT END */

/* COMPANY-BRAND BEGIN (2026-06-09) — header + avatar-menu company logo/name.
   The logo box is hard-capped (fixed height + max-width + object-fit: contain),
   so any uploaded image — even a huge one — is letter-boxed inside the box and
   can never stretch or break the header layout. */
.app-company-brand { min-width: 0; padding-left: 8px; }
.app-company-logo {
  height: 32px;
  width: auto;
  max-width: 150px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}
.app-company-name {
  font-weight: 700;
  font-size: 1rem;
  /* Tight leading so a wrapped 2-line name has minimal gap between lines;
     the header keeps comfortable space above/below via align-center. */
  line-height: 1.05;
  color: rgb(var(--v-theme-on-background, 31, 41, 55));
  /* Wrap long names onto up to 2 lines instead of truncating with an ellipsis. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  overflow-wrap: anywhere;
  max-width: 220px;
}
.company-band { background: rgba(var(--v-theme-on-surface, 31, 41, 55), 0.04); }
.company-band-logo {
  height: 28px;
  width: auto;
  max-width: 170px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}
.company-band-name {
  font-weight: 700;
  font-size: 0.95rem;
  color: rgb(var(--v-theme-on-surface, 31, 41, 55));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
/* COMPANY-BRAND END */

/* PROFILE-AVATAR-EDIT — clickable avatar with camera overlay in the user menu */
.profile-avatar-edit {
  position: relative;
  cursor: pointer;
  border-radius: 50%;
  transition: transform 0.15s ease;
}
.profile-avatar-edit:hover {
  transform: scale(1.03);
}
.profile-avatar-edit__overlay {
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgb(var(--v-theme-primary, 30, 58, 95));
  border: 2px solid rgb(var(--v-theme-surface, 255, 255, 255));
  display: flex;
  align-items: center;
  justify-content: center;
}
/* PROFILE-AVATAR-EDIT END */

/* MENU-THEME + 3D ICONS BEGIN (2026-06-09) */
.menu-icon-3d {
  /* MENU-ICON-SIZE (2026-06-13, rev3): real width/height so each icon takes its
     own space — rows grow to fit (no overlap) and the sidebar widens with the
     icon (see sidebarWidth). The icon is centered in the rail so it never drifts
     to the right edge. Driven by --menu-icon-size (30px default / 38px premium
     base × the user's scale). */
  width: var(--menu-icon-size, 30px);
  height: var(--menu-icon-size, 30px);
  object-fit: contain;
  display: block;
  flex-shrink: 0;
  transform-style: preserve-3d;
  will-change: transform;
}
/* Soft edge glow so dark-navy icons stay visible on the dark navy sidebar */
.menu-icon-3d--glow {
  filter:
    drop-shadow(0 0 2px rgba(77, 212, 172, 0.65))
    drop-shadow(0 0 5px rgba(120, 220, 255, 0.30));
}
/* 360° vertical-axis spin + small hop on hover / click (one-shot) */
.menu-icon-3d.spin { animation: menu-icon-spin-hop 0.75s ease-in-out; }
/* Same hover spin for mdi fallback icons when a PNG variant is missing */
.sidebar-item .menu-icon-spin {
  transform-style: preserve-3d;
  will-change: transform;
  flex-shrink: 0;
}
.sidebar-item .menu-icon-spin.spin { animation: menu-icon-spin-hop 0.75s ease-in-out; }
@keyframes menu-icon-spin-hop {
  0%   { transform: perspective(320px) rotateY(0deg)   translateY(0); }
  45%  { transform: perspective(320px) rotateY(180deg) translateY(-6px); }
  100% { transform: perspective(320px) rotateY(360deg) translateY(0); }
}

/* Light sidebar theme (white surface, dark labels) */
.sidebar--light .sidebar-item-label { color: #1f2937 !important; }
.sidebar--light .sidebar-item:hover { background-color: rgba(30, 58, 95, 0.06) !important; }
.sidebar--light .sidebar-item-active,
.sidebar--light .sidebar-item-route-active {
  background-color: rgba(77, 212, 172, 0.16) !important;
}
.sidebar--light .sidebar-item-active .sidebar-item-label,
.sidebar--light .sidebar-item-route-active .sidebar-item-label {
  color: #1e3a5f !important;
  font-weight: 600;
}
/* MENU-THEME + 3D ICONS END */

/* SIDEBAR-MODE BEGIN (2026-05-22) — two-mode sidebar styling.
   MENU-PERSONALIZATION D3 (2026-06-12): .sidebar-toggle-row removed — controls
   moved to .sidebar-footer at the bottom of the rail. */
.sidebar-toggle-btn {
  opacity: 0.85;
}
.sidebar-toggle-btn:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.15) !important;
}
.sidebar-item-label {
  color: white !important;
  font-size: 14px !important;
  font-weight: 500;
  margin-left: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-item-active .sidebar-item-label {
  color: rgb(var(--v-theme-primary)) !important;
}
.sidebar-drawer--expanded .v-navigation-drawer__content {
  transition: width 0.18s ease;
}
/* Make custom SVG icons (Call Center) align identically to v-icon in prepend slot */
.sidebar-item .custom-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
/* Tighten gap between icon and label in expanded mode (half of Vuetify default ~32px → 8px) */
.sidebar-drawer--expanded .sidebar-item {
  --v-list-item-prepend-padding: 8px;
}
.sidebar-drawer--expanded .sidebar-item .v-list-item__prepend {
  padding-inline-end: 8px !important;
  margin-inline-end: 0 !important;
}
.sidebar-drawer--expanded .sidebar-item .v-list-item__spacer {
  width: 8px !important;
  flex: 0 0 8px !important;
}
/* SIDEBAR-MODE END */

/* MENU-ICON-SIZE (rev3): in the COMPACT rail (icons only, no labels) center the
   icon inside the item so an enlarged icon grows symmetrically and never anchors
   to the left / overflows to the right edge. Excludes favorites (they show labels). */
.v-navigation-drawer:not(.sidebar-drawer--expanded) .sidebar-item:not(.favorites-item) {
  padding-inline: 2px !important;
}
.v-navigation-drawer:not(.sidebar-drawer--expanded) .sidebar-item:not(.favorites-item) .v-list-item__prepend {
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  justify-content: center;
}
.v-navigation-drawer:not(.sidebar-drawer--expanded) .sidebar-item:not(.favorites-item) .v-list-item__spacer {
  display: none !important;
}

/* Custom SVG icons in sidebar */
.sidebar-item .custom-icon {
  color: white;
}

.sidebar-item-active .custom-icon {
  color: rgb(var(--v-theme-primary)) !important;
}

/* Mobile Menu Styles */
.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

/* COMPACT-HEADER BEGIN (2026-06-03) — "⋯" dropdown menu for mobile + tablet header */
.compact-menu-card {
  overflow: hidden;
}
.compact-item {
  min-height: 44px !important;
  border-radius: 8px;
  margin: 2px 6px;
  position: relative;
}
.compact-item--unread {
  border-left: 3px solid rgb(var(--v-theme-accent));
  padding-left: 13px !important;
}
.compact-label {
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
}
/* COMPACT-HEADER END */

/* SIDEBAR-PUSH-LAYOUT BEGIN (2026-06-03) — second navigation drawer for submenu (push layout) */
/* SIDEBAR-FLYOUT (2026-06-09): floating overlay submenu (HubSpot-style).
   Sits OVER the content (no layout push); anchored next to the item via inline
   top/left/width/maxHeight; height fits its content, then the list scrolls. */
.submenu-flyout {
  position: fixed;
  /* MENU-AUDIT FIX (2026-06-12): was 1010 — too close to Vuetify layout layers
     (drawers/app-bar get 1000 + 2*n) and below mid-range floating widgets.
     1900 = safely above layout & most widgets, still below Vuetify overlays
     (menus/dialogs/tooltips start at 2000) so dialogs always cover the flyout. */
  z-index: 1900;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  border: 1px solid var(--ui-border, rgba(0, 0, 0, 0.08));
  border-radius: 10px;
  box-shadow: 0 8px 28px var(--ui-shadow, rgba(0, 0, 0, 0.16));
  overflow: hidden;
}

/* TICK-00249: while the annotation pick overlay is active (z-index near max),
   lift the rail flyout above it so hover-opened submenus stay visible and
   clickable for element pick. */
html[data-annotation-active="1"] .submenu-flyout {
  z-index: 2147483647;
}

.submenu-flyout-scroll {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.submenu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  min-height: 56px;
  flex-shrink: 0;
}

.submenu-list {
  padding: 8px;
}

.submenu-item {
  border-radius: 8px;
  margin-bottom: 2px;
  min-height: 40px;
  cursor: pointer;
}

/* Submenu item label — match the size/weight of the expanded sidebar item labels (14px / 500).
   NOTE: <style> is NOT scoped, so we put the class directly on v-list-item-title (no :deep needed). */
.submenu-item-label {
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
  color: rgb(var(--v-theme-on-surface, 31, 41, 55));
}

.submenu-item-icon {
  color: rgb(var(--v-theme-on-surface, 31, 41, 55));
}

/* MENU-ICONS (2026-07-08): Vuetify's default icon-to-label spacer is 32px —
   halve it (16px) in the flyout so the label sits close to its icon. */
.submenu-list .v-list-item__spacer {
  width: 16px !important;
}

/* MENU-ICONS (2026-07-08): long titles truncate with an ellipsis, but the
   permission shield / pin / star icons never get cut off. */
.submenu-item-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 0 1 auto;
}
.submenu-item-label .permission-indicator-wrapper {
  flex-shrink: 0;
}

/* MENU-ICONS (2026-07-08): mobile submenu rows — same halved icon-to-label gap */
.mobile-submenu-item .v-list-item__spacer {
  width: 16px !important;
}

.submenu-item:hover {
  background-color: rgb(var(--v-theme-background, 245, 247, 250));
}

/* Active submenu item — subtle, tasteful highlight: soft accent tint + a thin
   accent bar + accent-colored bold text. No loud solid-green fill. */
.submenu-item.v-list-item--active {
  background-color: rgba(var(--v-theme-accent), 0.12) !important;
  position: relative;
}
.submenu-item.v-list-item--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  border-radius: 0 2px 2px 0;
  background: rgb(var(--v-theme-accent));
  pointer-events: none;
}
.submenu-item.v-list-item--active .submenu-item-label,
.submenu-item.v-list-item--active .submenu-item-icon {
  color: rgb(var(--v-theme-accent)) !important;
  font-weight: 600 !important;
}
.submenu-item.v-list-item--active:hover {
  background-color: rgba(var(--v-theme-accent), 0.18) !important;
}

/* Submenu subheader (group label like "PHONE", "REPORTS") — small uppercase */
.submenu-list .v-list-subheader {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  min-height: 28px !important;
  opacity: 0.7;
}
/* TICK-00129: group headers used class `text-primary` (navy). On the dark
   flyout surface that is dark-on-dark and the headers (Help Desk, E-Signature,
   PHONE, REPORTS…) become invisible. Drive the colour from the theme instead:
   brand navy in light, high-emphasis on-surface in dark. */
.submenu-list .submenu-group-header {
  color: rgb(var(--v-theme-primary)) !important;
  opacity: 1;
}
.v-theme--dark .submenu-list .submenu-group-header {
  color: rgba(var(--v-theme-on-surface), 0.85) !important;
}
/* MENU-AUDIT (2026-07-08): headers are the group separators now (no dividers) —
   give every non-first group a little breathing room above its header. */
.submenu-list .v-list-subheader:not(:first-child) {
  margin-top: 6px;
}
/* Mobile drawer group headers — same compact uppercase style, indented to align
   with the submenu rows. */
.mobile-submenu-header {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  min-height: 26px !important;
  opacity: 0.7;
}
/* SIDEBAR-PUSH-LAYOUT END */

/* MENU-PERSONALIZATION BEGIN (2026-06-12) — V1 "Compass" + M1 "Sheet" styles */
.sidebar-toggle-btn--active {
  opacity: 1;
  background-color: rgba(77, 212, 172, 0.18) !important;
}
/* D3 footer control bar: gear + Collapse pinned to the bottom of the rail.
   Sits OUTSIDE .sidebar-content (sibling), so it never scrolls away — the
   drawer content wrapper is a flex column and .sidebar-content has flex: 1. */
.sidebar-footer {
  flex-shrink: 0;
}
.sidebar-footer-divider--dark {
  border-color: rgba(255, 255, 255, 0.25) !important;
}
.sidebar-footer-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
}
/* Expanded (or favorites) mode: single row — gear left, labeled Collapse right */
.sidebar-footer--expanded .sidebar-footer-row {
  flex-direction: row;
  justify-content: space-between;
}
.sidebar-collapse-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 26px;
  min-width: 26px;
  padding: 0;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  background: rgba(30, 58, 95, 0.07);
  transition: background 0.15s ease;
}
.sidebar-collapse-btn:hover {
  background: rgba(30, 58, 95, 0.14);
}
.sidebar-collapse-btn--dark {
  background: rgba(255, 255, 255, 0.12);
}
.sidebar-collapse-btn--dark:hover {
  background: rgba(255, 255, 255, 0.22);
}
.sidebar-collapse-btn--labeled {
  padding: 0 10px 0 6px;
}
.sidebar-collapse-label {
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: rgb(var(--v-theme-primary));
}
.sidebar-collapse-btn--dark .sidebar-collapse-label {
  color: white;
}
/* Divider under the Quick Access pseudo-item */
/* Divider between Quick Access and the menu — hidden (no visible line),
   small spacer kept so the lightning isn't glued to the first item. */
.sidebar-qa-divider {
  border: none !important;
  margin: 0 0 4px;
  height: 0;
  opacity: 0;
}
.sidebar-qa-divider--dark {
  border: none !important;
}
/* Favorites-only flat panel */
.favorites-panel-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px 4px;
  height: 28px;
  flex-shrink: 0;
}
.favorites-panel-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: white;
}
.sidebar--light .favorites-panel-title {
  color: rgb(var(--v-theme-primary));
}
.favorites-item {
  min-height: 36px;
}
.favorites-item-label {
  font-size: 13px !important;
}
.favorites-usage {
  font-size: 10px;
  opacity: 0.55;
  margin-right: 2px;
  font-variant-numeric: tabular-nums;
}
.favorites-empty {
  padding: 12px 14px;
  font-size: 12px;
  opacity: 0.65;
}
.favorites-empty--mobile {
  color: rgb(var(--v-theme-on-surface, 31, 41, 55));
}
.favorites-hint {
  padding: 4px 14px 8px;
  font-size: 10.5px;
  opacity: 0.55;
  flex-shrink: 0;
}
/* Quick Access flyout bits */
.qa-empty {
  padding: 10px 12px;
  font-size: 12.5px;
  opacity: 0.7;
}
.qa-hint {
  padding: 6px 16px 10px;
  font-size: 11px;
  opacity: 0.6;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}
.qa-opens {
  font-size: 10.5px;
  opacity: 0.55;
  margin-right: 2px;
  font-variant-numeric: tabular-nums;
}
.qa-drag-handle {
  cursor: grab;
  opacity: 0.5;
}
.qa-drag-handle:hover {
  opacity: 1;
}
/* TICK-00064: reorder feedback. The dragged row fades, and a single accent line
   marks the exact gap it will drop into, so it is obvious that ONE row moves. */
.submenu-item.qa-dragging {
  opacity: 0.45;
}
.submenu-item.qa-drop-before,
.submenu-item.qa-drop-after {
  position: relative;
}
.submenu-item.qa-drop-before::before,
.submenu-item.qa-drop-after::after {
  content: '';
  position: absolute;
  left: 8px;
  right: 8px;
  height: 2px;
  border-radius: 2px;
  background: rgb(var(--v-theme-accent));
  pointer-events: none;
}
.submenu-item.qa-drop-before::before { top: -1px; }
.submenu-item.qa-drop-after::after { bottom: -1px; }
/* Star/pin row actions: hidden until row hover (or when already ON).
   MENU-AUDIT (2026-07-08): was opacity:0 — invisible buttons still reserved
   ~56px per row and forced long titles to truncate. display:none frees that
   space; buttons appear (and take space) only while the row is hovered. */
.submenu-item .row-action-btn {
  display: none;
}
.submenu-item:hover .row-action-btn,
.submenu-item .row-action-btn--on {
  display: inline-flex;
}
/* Mobile M1: Quick Access chips strip + All/Favorites tabs */
.mobile-qa-strip {
  padding: 8px 12px 4px;
}
.mobile-qa-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-accent));
  margin-bottom: 6px;
}
.mobile-qa-chips {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
}
.mobile-qa-chips::-webkit-scrollbar {
  display: none;
}
.mobile-qa-chip {
  flex-shrink: 0;
}
.mobile-menu-tabs {
  padding: 4px 12px 8px;
}
.mobile-menu-tabs-toggle {
  width: 100%;
}
/* MENU-PERSONALIZATION END */

/* .search-bar removed — replaced by GlobalSearchDropdown */

.god-mode-bar {
  color: white !important;
  z-index: 9999 !important;
  animation: god-mode-pulse 2s ease-in-out infinite;
}
@keyframes god-mode-pulse {
  0%, 100% { background-color: #dc2626; }
  50% { background-color: #b91c1c; }
}
</style>
